import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import styled, { createGlobalStyle, css } from "styled-components";
import {
  Input,
  message,
  Modal,
  Drawer,
  Card,
  Statistic,
  Tooltip,
  Tag,
  Spin,
  Progress,
  Rate,
  Select,
  Alert,
  Button,
  Image,
} from "antd";
import {
  SendOutlined,
  PlusOutlined,
  MenuOutlined,
  DollarOutlined,
  TeamOutlined,
  CommentOutlined,
  LinkOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  MinusCircleOutlined,
  PictureOutlined,
  CloseOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  StarOutlined,
  IdcardOutlined,
  PhoneOutlined,
  HomeOutlined,
  LockOutlined,
  DollarCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
  SortAscendingOutlined,
  DownOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuth } from "firebase/auth";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../helpers/firebase";
import {
  useSearchParams,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { device, useBreakpoints } from "../lib/breakpoints";
import {
  slugify,
  generateChatSlug,
  isSlugCanonical,
  parseChatlocation,
  buildCheckoutReturnChatPath,
} from "../lib/slugUtils";
import SharkStatusLog from "../components/SharkStatusLog";
import PaymentSecurityGroup from "../components/PaymentSecurityGroup";
import RegionalMarketPulse from "../components/RegionalMarketPulse";
import { extractZipFromAddress } from "../lib/regionFromZip";
import ContactVendor from "../components/ContactVendor";
import { classifyJobTier, CLUMSE_SEARCH_FEE_USD } from "../utils/tierClassification";

// Environment detection for sandbox mode indicator
const isLive = process.env.REACT_APP_DEPLOYMENT === "prod";
import { sendChatMessage } from "../lib/api/chatAPI";
import { fetchLandlordConversation } from "../lib/api/visualVisioningAPI";
import {
  uploadImage,
  isBase64Image,
  isImageUrl,
} from "../lib/api/imageUploadAPI";
import { getApiUrl } from "../lib/api/config";
import { getRawSubscriptionPlanKey, TierKey } from "../lib/pricing";
import { AuthContext } from "../context/AuthProvider";
import {
  CLUMSE_AI_DISPLAY_NAME,
  isActiveDemoQueryParam,
  isMarketingDebugControlsEnabled,
  isMarketingSandboxActive,
  isMarketingSandboxPath,
} from "../helpers/constants";
import { APP_TERMS_ROUTE } from "../helpers/routeNames";
import EditableScopeCard from "../components/chat/EditableScopeCard";
import MarketingSandboxControls from "../components/debug/MarketingSandboxControls";
import {
  buildMarketingProcurement,
  buildMarketingSandboxBids,
  getMarketingSandboxThreads,
  MARKETING_SANDBOX_JOB_ID,
  normalizeMarketingPhase,
} from "../lib/marketingSandboxFixtures";
import {
  buildClientScopeFallback,
  findLatestStructuredScopeInMessages,
  mergeSupplementalIntoScope,
  normalizeStructuredScope,
} from "../lib/scopeOfWorkUtils";
import {
  CLUMSE_CHAT_WELCOME_MARKDOWN,
  formatClumseAiBubbleText,
} from "../lib/clumseChatWelcome";
import {
  BidsPanelInlineButton,
  formatChatBoldSegments,
  formatChatMessageSegments,
} from "../lib/formatChatBoldSegments";
import ChatMessageImageThumbs from "../components/chat/ChatMessageImageThumbs";

/** Pending-attach row: small square before send, same preview behavior. */
const PENDING_CHAT_IMAGE_STYLES = {
  root: { width: "100%", height: "100%", display: "block" },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    cursor: "pointer",
  },
};

const STORAGE_KEY_PREFIX = "clumse_chat_threads_v1";

function getStorageKey(userId) {
  return userId ? `${STORAGE_KEY_PREFIX}_${userId}` : STORAGE_KEY_PREFIX;
}
const MAX_CHAT_IMAGES = 10;
const MAX_IMAGE_INPUT_BYTES = 12 * 1024 * 1024;

/**
 * Mobile: fallback for rail + dimmer `top` until `MainTopBar` height is measured.
 * Actual offset uses `--chat-mobile-header-stack` from ResizeObserver (must match title bar).
 */
const MOBILE_CHAT_HEADER_STACK_PX = 64;

/** Shared Ant Design Modal `styles` - aligns rename + delete with marketing shell (see docs/BRANDING_GUIDE.md). */
const CHAT_MODAL_SHELL_STYLES = {
  content: {
    padding: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow:
      "0 4px 24px rgba(60, 64, 67, 0.18), 0 8px 32px rgba(60, 64, 67, 0.08)",
  },
  header: {
    margin: 0,
    padding: "18px 22px 14px",
    borderBottom: "1px solid #e8eaed",
    background: "#fafbfc",
  },
  body: { padding: "18px 22px 4px" },
  footer: { margin: 0, padding: 0, borderTop: "none" },
  mask: { backdropFilter: "blur(2px)" },
};

/** Market variance: cap height so header/footer stay put and the body scrolls. */
const VARIANCE_MODAL_STYLES = {
  ...CHAT_MODAL_SHELL_STYLES,
  content: {
    ...CHAT_MODAL_SHELL_STYLES.content,
    maxHeight: "min(92dvh, calc(100vh - 24px))",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    ...CHAT_MODAL_SHELL_STYLES.header,
    flexShrink: 0,
    background: "linear-gradient(135deg, #ff5722 0%, #f4511e 100%)",
    color: "#ffffff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
  },
  body: {
    ...CHAT_MODAL_SHELL_STYLES.body,
    padding: "24px 24px 16px",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
  footer: {
    ...CHAT_MODAL_SHELL_STYLES.footer,
    flexShrink: 0,
  },
};

/** Close hit-area + hover (glyph color comes from Modal `closeIcon`). */
const VarianceMarketModalChrome = createGlobalStyle`
 .clumse-variance-market-modal .ant-modal-title {
 width: 100%;
 }
 .clumse-variance-market-modal .ant-modal-close {
 top: 12px !important;
 width: 40px !important;
 height: 40px !important;
 border-radius: 999px !important;
 color: #ffffff !important;
 }
 .clumse-variance-market-modal .ant-modal-close:hover {
 color: #ffffff !important;
 background-color: rgba(255, 255, 255, 0.16) !important;
 }
 .clumse-variance-market-modal .ant-modal-close .ant-modal-close-x {
 color: #ffffff !important;
 }
 .clumse-variance-market-modal .ant-modal-close .anticon {
 color: #ffffff !important;
 }
 .clumse-variance-market-modal .ant-modal-close .anticon svg {
 color: #ffffff !important;
 fill: #ffffff !important;
 }
 .clumse-variance-market-modal .ant-modal-close .anticon svg path {
 fill: #ffffff !important;
 stroke: #ffffff !important;
 }
`;

/** Delete (and other light chat shell) modals: plain X, no circular chrome. */
const ChatShellModalChrome = createGlobalStyle`
 .clumse-chat-delete-modal .ant-modal-close {
 background: transparent !important;
 border: none !important;
 border-radius: 0 !important;
 box-shadow: none !important;
 backdrop-filter: none !important;
 overflow: visible !important;
 }
 .clumse-chat-delete-modal .ant-modal-close::before {
 display: none !important;
 }
 .clumse-chat-delete-modal .ant-modal-close:hover {
 background: transparent !important;
 border: none !important;
 box-shadow: none !important;
 transform: none !important;
 color: #0f172a !important;
 }
 .clumse-chat-delete-modal .ant-modal-close:hover::before {
 display: none !important;
 opacity: 0 !important;
 }
 .clumse-chat-delete-modal .ant-modal-close:active {
 background: transparent !important;
 transform: none !important;
 }
 .clumse-chat-delete-modal .ant-modal-close:hover .anticon,
 .clumse-chat-delete-modal .ant-modal-close:hover .anticon svg,
 .clumse-chat-delete-modal .ant-modal-close:hover svg {
 transform: none !important;
 }
`;

/** Mobile bids drawer: true center title + subtitle; close on the right without shifting center */
const BidsDrawerHeaderChrome = createGlobalStyle`
 .clumse-bids-drawer-header.ant-drawer-header {
 position: relative;
 display: flex;
 align-items: center;
 justify-content: center;
 }
 .clumse-bids-drawer-header .ant-drawer-close {
 position: absolute;
 top: 50%;
 inset-inline-end: 10px;
 transform: translateY(-50%);
 margin: 0 !important;
 flex: none;
 z-index: 2;
 }
 .clumse-bids-drawer-header .ant-drawer-header-title {
 flex: 1 1 100%;
 width: 100%;
 max-width: 100%;
 display: flex;
 justify-content: center;
 align-items: center;
 min-width: 0;
 margin: 0 !important;
 overflow: hidden;
 box-sizing: border-box;
 /* Equal gutters so centered copy stays at true panel center (not nudged by the close control) */
 padding-inline: 44px;
 }
 .clumse-bids-drawer .ant-drawer-body {
 padding-right: 6px !important;
 }
`;

const WELCOME_TEXT = CLUMSE_CHAT_WELCOME_MARKDOWN;

function makeWelcomeMessage() {
  return {
    id: `w-${Date.now()}`,
    role: "assistant",
    content: WELCOME_TEXT,
    timestamp: new Date().toISOString(),
    isInitial: true,
  };
}

// Typing animation component for chat titles
const TypingTitle = ({ finalText, isActive, children, ...props }) => {
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!isActive || !finalText || finalText === displayText) return;

    // Start typing animation
    setIsTyping(true);
    setDisplayText("");

    let currentIndex = 0;
    const typeNextChar = () => {
      if (currentIndex < finalText.length) {
        setDisplayText(finalText.slice(0, currentIndex + 1));
        currentIndex++;
        setTimeout(typeNextChar, 50); // 50ms per character
      } else {
        setIsTyping(false);
      }
    };

    // Small delay before starting to type
    const timeout = setTimeout(typeNextChar, 200);

    return () => {
      clearTimeout(timeout);
      setIsTyping(false);
    };
  }, [finalText, isActive]);

  // If not typing, show the final text or fallback to children
  if (!isActive || !finalText) {
    return children;
  }

  return (
    <span {...props}>
      {displayText}
      {isTyping && <span style={{ opacity: 0.5 }}>|</span>}
    </span>
  );
};

function createThread() {
  const id = `t_${Date.now()}`;
  return {
    id,
    title: "Chat",
    slug: "new-chat",
    updatedAt: Date.now(),
    linkedTicketId: null,
    messages: [makeWelcomeMessage()],
  };
}

/** Fixture job id for `?demo=true` - not a real Firestore document. */
const DEMO_JOB_ID = "demo_clumse_chat_job";
const DEMO_JOB_ID_2 = "demo_hvac_touchup";
/** 1×1 transparent GIF - keeps demo threads small if persisted. */
const DEMO_PLACEHOLDER_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

function getDemoProcurement() {
  return {
    procurementPlanReady: true,
    scopeDiagnosisReady: true,
    procurementPlanMissing: [],
    dispatchOrchestrationFeeUsd: CLUMSE_SEARCH_FEE_USD,
    dispatchOrchestrationIncluded: false,
    clumseDispatchStatus: "DISPATCHED",
    lockedBenchmarkUsd: 2890,
  };
}

function getDemoBids() {
  const iso = (minsAgo) =>
    new Date(Date.now() - minsAgo * 60_000).toISOString();
  return [
    {
      id: "demo_bid_1",
      ticketId: DEMO_JOB_ID,
      ticketTitle: "Master bath sink - trap & supply (1800 Meridian Row)",
      vendorName: "Front Range Plumbing Co.",
      googlePlaceRating: 4.6,
      googleReviewCount: 214,
      bidAmount: 1825,
      status: "pending",
      createdAt: iso(12),
      distanceMiles: 6.4,
      vendorAvailability: "Tuesday or Thursday mornings, first slot 8am.",
      timeline: "Roughly half day including leak test.",
      vendorChatSummary:
        'Confirmed scope is **P-trap and supply lines** under the vanity. They will bring common 1/2" compression fittings; if shutoffs are seized, a **small materials add** may apply after photos.\n\nAsked for **street parking on Meridian** and a **two-hour arrival window** once the job is confirmed.',
    },
    {
      id: "demo_bid_2",
      ticketId: DEMO_JOB_ID,
      ticketTitle: "Master bath sink - trap & supply (1800 Meridian Row)",
      vendorName: "Peak Drain & Rooter",
      googlePlaceRating: 4.4,
      googleReviewCount: 89,
      bidAmount: 1640,
      status: "pending",
      createdAt: iso(36),
      distanceMiles: 11.2,
      vendorAvailability:
        "Next business day after dispatch, or Saturday after 9am.",
      timeline:
        "About **2-3 hours** on site; same-day finish if parts are in the truck.",
      vendorChatSummary:
        "Walked through **slow drain vs leak** with Clumse: they believe it is supply-side weeping at the angle stop, not a main line clog. Plan is to replace stops and braided supplies, re-seat the trap, and **pressure-check** before leaving.\n\nHome access: OK after **5pm weekdays** per your note.",
    },
    {
      id: "demo_bid_3",
      ticketId: DEMO_JOB_ID,
      ticketTitle: "Master bath sink - trap & supply (1800 Meridian Row)",
      vendorName: "Mile High Elite Handyman Collective (insured)",
      googlePlaceRating: 4.8,
      googleReviewCount: 156,
      bidAmount: 2100,
      status: "accepted",
      createdAt: iso(90),
      distanceMiles: 4.1,
      vendorEmail: "dispatch@milehigh-elite-handyman.example",
      propertyExactAddress: "1800 Meridian Row, Denver, CO 80202",
      vendorAvailability:
        "Within 48 hours of hire; crew lead texts 30 minutes out.",
      timeline: "Half day; includes **haul-away** of old trap if needed.",
      vendorChatSummary:
        "Offered a **single trip** bundle: minor drywall touch-up behind escutcheons if opened, plus silicone re-bead at backsplash seam. Noted they carry **GL insurance** and can email COI to the building if required.",
    },
    {
      id: "demo_bid_4",
      ticketId: DEMO_JOB_ID,
      ticketTitle: "Master bath sink - trap & supply (1800 Meridian Row)",
      vendorName: "QuickFix Plumbing Co.",
      googlePlaceRating: 4.2,
      googleReviewCount: 42,
      bidAmount: 2450,
      status: "pending",
      createdAt: iso(120),
      distanceMiles: 17.8,
      vendorAvailability: "Soonest is next week mid-mornings.",
      timeline:
        "3-4 hours; may need return if specialty valve order is required.",
      vendorChatSummary:
        "Quoted **premium cartridge valves** as optional upgrade. Flagged possible **corrosion** on galvanized stub-outs; contingency discussed with Clumse if swap to copper is requested mid-job.",
    },
    {
      id: "demo_bid_5",
      ticketId: DEMO_JOB_ID_2,
      ticketTitle: "HVAC tune-up - 2-head mini-split (metro corridor)",
      vendorName: "Altitude Heating & Cooling LLC",
      googlePlaceRating: 4.9,
      googleReviewCount: 512,
      bidAmount: 320,
      status: "pending",
      createdAt: iso(200),
      distanceMiles: 3.2,
      vendorAvailability:
        "Seasonal route: **Wednesdays** in your ZIP when on the metro corridor.",
      timeline: "90 minutes per head, filter swap included if filters on hand.",
      vendorChatSummary:
        "Confirmed **two indoor heads** on one condenser. Will verify line set lengths and **flush drains** on condensate pumps. Asked for ladder access to outdoor unit on rear pad.",
    },
    {
      id: "demo_bid_6",
      ticketId: DEMO_JOB_ID_2,
      ticketTitle: "HVAC tune-up - 2-head mini-split (metro corridor)",
      vendorName: "B-Series Mechanical Services",
      googlePlaceRating: 4.5,
      googleReviewCount: 67,
      bidAmount: 285,
      status: "pending",
      createdAt: iso(240),
      distanceMiles: 8.9,
      vendorAvailability: "Friday afternoons or **Saturday 8am-noon**.",
      timeline:
        "About 2 hours total; photos of model plates already shared with Clumse.",
      vendorChatSummary:
        "Focused on **filter sizing** for slim ducts and offered to stock common MERV 11 replacements. Noted outdoor coil rinse only if water hookup is within 50 feet.",
    },
  ];
}

function getDemoThreads() {
  const tMain = "t_demo_main";
  const tOther = "t_demo_secondary";
  const assistantBlocks = `Thanks for the photos and context. That helps a lot.

**Before you start vendor search**, here is a quick checklist:

**Photos worth adding**
- Overall bathroom facing the vanity wall 
- Close-up of the P-trap and shutoff valves 
- Any water staining on the cabinet floor 

**Next steps**
1. Confirm your home ZIP for regional pricing. 
2. Note whether anyone will be home during work (access window). 
3. When you are ready, tap **Pay $49 · Start search** so Clumse can pull live vendor bids.

You will see **fair local pricing** to compare once bids arrive.`;
  return [
    {
      id: tMain,
      title: "Bathroom sink - downtown loft (demo)",
      updatedAt: Date.now(),
      linkedTicketId: DEMO_JOB_ID,
      __demo: true,
      messages: [
        {
          id: "dm_u1",
          role: "user",
          content: "Here are photos of the sink and the slow drain.",
          images: [DEMO_PLACEHOLDER_IMAGE],
          timestamp: new Date(Date.now() - 86400000).toISOString(),
        },
        {
          id: "dm_a1",
          role: "assistant",
          content: assistantBlocks,
          timestamp: new Date(Date.now() - 86350000).toISOString(),
        },
        {
          id: "dm_u2",
          role: "user",
          content:
            "Address is 1800 Meridian Row, 80202. I'm home weekday evenings after 5.",
          timestamp: new Date(Date.now() - 82000000).toISOString(),
        },
        {
          id: "dm_a2",
          role: "assistant",
          content:
            "Captured. I will emphasize **evening access after 5pm** on your project scope.\n\nWhen you start vendor search, our standard is **3 comparable bids** within the first 24h for a job like this in your ZIP (or a search-fee refund if we cannot deliver).",
          timestamp: new Date(Date.now() - 81900000).toISOString(),
        },
      ],
    },
    {
      id: tOther,
      title: "HVAC tune-up (quick question)",
      updatedAt: Date.now() - 3600000,
      linkedTicketId: null,
      __demo: true,
      messages: [
        makeWelcomeMessage(),
        {
          id: "dm2_u1",
          role: "user",
          content:
            "Do you bundle HVAC tune-ups with filter replacement quotes?",
          timestamp: new Date(Date.now() - 4000000).toISOString(),
        },
        {
          id: "dm2_a1",
          role: "assistant",
          content:
            "Yes - vendors usually pair a **tune-up + filter swap** as a single line item.\n\nIf you share the approximate tonnage or photo of the air handler label, I can tighten the script before you post it.",
          timestamp: new Date(Date.now() - 3900000).toISOString(),
        },
      ],
    },
  ];
}

function chatDemoParamOn() {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    return isActiveDemoQueryParam(sp.has("demo"), sp.get("demo"));
  } catch {
    return false;
  }
}

function resolveInitialChatState() {
  if (typeof window !== "undefined") {
    const path = window.location.pathname || "";
    if (isMarketingSandboxPath(path)) {
      const phase = normalizeMarketingPhase(
        new URLSearchParams(window.location.search).get("phase")
      );
      const threads = getMarketingSandboxThreads(phase);
      return { threads, activeId: threads[0].id };
    }
  }
  if (chatDemoParamOn()) {
    const threads = getDemoThreads();
    return { threads, activeId: threads[0].id };
  }
  return getInitialThreadsState();
}

function normalizeThread(t) {
  if (!t || typeof t !== "object") return null;
  return {
    ...t,
    serverConversationId: t.serverConversationId ?? null,
    linkedTicketId: t.linkedTicketId ?? null,
    slug: t.slug || generateChatSlug(t.title, t.messages) || "new-chat",
    messages:
      Array.isArray(t.messages) && t.messages.length
        ? t.messages
        : [makeWelcomeMessage()],
  };
}

function isClientUiThreadId(id) {
  return typeof id === "string" && /^t_\d+$/.test(id);
}

function userMessageCount(thread) {
  return (thread?.messages || []).filter((m) => m.role === "user").length;
}

/**
 * Multiple welcome-only `t_*` drafts (same title, no user turns) show up as phantom
 * “New chat” rows after hydrate + localStorage recovery. Keep one (URL match or newest).
 */
function dedupeEmptyClientDraftThreads(threads, preferredClientId) {
  if (!Array.isArray(threads) || threads.length <= 1) return threads;
  const emptyClients = threads.filter(
    (t) =>
      t &&
      isClientUiThreadId(t.id) &&
      !t.serverConversationId &&
      userMessageCount(t) === 0
  );
  if (emptyClients.length <= 1) return threads;
  const keepId =
    preferredClientId && emptyClients.some((c) => c.id === preferredClientId)
      ? preferredClientId
      : emptyClients.reduce((best, t) =>
          (t.updatedAt || 0) >= (best.updatedAt || 0) ? t : best
        ).id;
  return threads.filter(
    (t) =>
      !(
        t &&
        isClientUiThreadId(t.id) &&
        !t.serverConversationId &&
        userMessageCount(t) === 0 &&
        t.id !== keepId
      )
  );
}

/** Map Firestore conversation messages → ClumseChat transcript shape. */
function serverChatMessagesToUiMessages(convMessages) {
  if (!Array.isArray(convMessages) || !convMessages.length) {
    return [makeWelcomeMessage()];
  }
  const out = [];
  for (const msg of convMessages) {
    const isAi = msg.fromUserType === "ai" || msg.fromUserId === "ai_assistant";
    const rawText = String(msg.content?.text ?? "").trim();
    const images = Array.isArray(msg.content?.images)
      ? msg.content.images.filter((u) => typeof u === "string")
      : [];
    let text = rawText;
    if (text === "(Image attachment)" && images.length > 0) {
      text = "";
    }
    if (!text && !images.length) continue;
    const formatted = isAi ? formatClumseAiBubbleText(text) : text;
    const prev = out[out.length - 1];
    if (
      isAi &&
      prev?.role === "assistant" &&
      msg.metadata?.dispatchSearchStarted &&
      prev.content?.includes("Congrats on starting your vendor search") &&
      formatted.includes("Congrats on starting your vendor search")
    ) {
      continue;
    }
    const metadata = mapServerMessageMetadata(msg);
    out.push({
      id:
        msg.id ||
        `srv_${msg.timestamp || Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 9)}`,
      role: isAi ? "assistant" : "user",
      content: formatted,
      ...(images.length ? { images } : {}),
      ...(metadata ? { metadata } : {}),
      timestamp: msg.timestamp || new Date().toISOString(),
    });
  }
  if (!out.length) {
    return [makeWelcomeMessage()];
  }
  // Legacy threads: first stored turn was the user message (client-only welcome was never persisted).
  if (out[0].role === "user") {
    const t0 = out[0].timestamp
      ? new Date(out[0].timestamp).getTime()
      : Date.now();
    out.unshift({
      id: "w-clumse-intro",
      role: "assistant",
      content: WELCOME_TEXT,
      timestamp: new Date(t0 - 1).toISOString(),
      isInitial: true,
    });
  }
  return out;
}

function threadFromServerConversation(conv) {
  if (!conv?.id) return null;
  const msgs = serverChatMessagesToUiMessages(conv.messages);
  const related =
    conv.context?.relatedTicketId ||
    conv.context?.relatedJobRequestId ||
    conv.context?.jobRequestId;
  let title = String(conv.context?.chatName || "").trim();
  if (!title) {
    const firstUser = msgs.find((m) => m.role === "user");
    title = firstUser
      ? threadTitleFromMessage(firstUser.content, firstUser.images)
      : "New chat";
  }
  const lastAt =
    conv.metadata?.lastMessageAt ||
    conv.metadata?.updatedAt ||
    new Date().toISOString();
  const updatedAt = new Date(lastAt).getTime() || Date.now();
  const existingSlug = conv.metadata?.slug || conv.slug;
  const generatedSlug = existingSlug || generateChatSlug(title, msgs);

  return normalizeThread({
    id: conv.id,
    serverConversationId: conv.id,
    title,
    slug: generatedSlug,
    updatedAt,
    linkedTicketId: related ? String(related) : null,
    messages: msgs,
  });
}

/** Merge landlord `ai_chat` threads from API with unsynced local drafts. */
function mergeServerAiThreadsIntoLocal(serverConversations, prevThreads) {
  const serverThreads = (serverConversations || [])
    .map(threadFromServerConversation)
    .filter(Boolean);
  const byId = new Map(serverThreads.map((th) => [th.id, th]));
  for (const t of prevThreads || []) {
    if (!t || t.__demo) continue;
    if (!t.serverConversationId && isClientUiThreadId(t.id)) {
      // Keep every unsynced client draft, including welcome-only New chat rows, so
      // opening another thread (re-hydrate) does not drop the draft you just created.
      if (!byId.has(t.id)) {
        byId.set(t.id, normalizeThread(t));
      }
    }
  }
  return [...byId.values()].sort(
    (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)
  );
}

/** Job id returned with sendChat (maintenance created, data modification, etc.) - no manual linking. */
function jobIdFromSendChatPayload(data) {
  if (!data || typeof data !== "object") return null;
  if (data.jobRequestId) return String(data.jobRequestId);
  const m = data.message;
  if (!m || typeof m !== "object") return null;
  if (m.repairTicketId) return String(m.repairTicketId);
  if (m.jobRequestId) return String(m.jobRequestId);
  const dm = m.dataModificationResult;
  if (dm && typeof dm === "object") {
    if (dm.repairId) return String(dm.repairId);
    if (dm.repair && typeof dm.repair === "object" && dm.repair.id) {
      return String(dm.repair.id);
    }
  }
  return null;
}

/**
 * Enough chat context to start paid vendor search (aligned with backend surgical intake).
 */
const VENDOR_SEARCH_READY_PERCENT = 90;

/** Matches backend `buildCheckoutHandoffMessage` (Pay $X · Start search). */
/** Matches backend pre-checkout "anything else?" prompt. */
const FINAL_DETAILS_BEFORE_CHECKOUT_RE = /anything else about the project|factor in before we line up vendor|what else should I know for accurate local quotes|anything else.*factored in/i;

/**
 * Detects the "Value Shock" diagnosis message (Project Blueprint Ready) in the transcript.
 * Must match the DIAGNOSIS_BREAKDOWN_MARKER exported from surgical-intake.service.ts.
 */
const DIAGNOSIS_READY_RE = /\*\*Project Blueprint Ready\*\*/;

/**
 * Mirrors the EXISTING_CREW_INTENT_RE from surgical-intake.service.ts.
 * When a user message matches, the UI shifts from "Find Pros" to "Generate Blueprint Pack".
 */
const EXISTING_CREW_INTENT_RE =
  /\b(already\s+have\s+(a\s+)?(contractor|crew|handyman|plumber|electrician|guy|someone)|have\s+(my\s+own|a)\s+(contractor|crew|guy|handyman|team)|using\s+(my|our)\s+(own\s+)?(contractor|crew|guy|guys|team)|just\s+curi(?:ous|osity)|checking\s+(prices?|quotes?|costs?)|comparing\s+(prices?|quotes?|bids?|costs?)|doing\s+(research|my\s+homework|due\s+diligence)|want\s+a\s+second\s+opinion|my\s+contractor\s+(asked|wants|said|told|needs)|have\s+someone\s+in\s+mind|not\s+looking\s+for\s+a\s+contractor)\b/i;

/**
 * Returns true when any user message in the transcript signals existing-crew / just-curious intent.
 */
function detectExistingCrewIntentInMessages(messages) {
  if (!Array.isArray(messages)) return false;
  return messages.some((m) => {
    const role = String(m?.role || m?.from || "").toLowerCase();
    if (role !== "user" && role !== "landlord") return false;
    return EXISTING_CREW_INTENT_RE.test(String(m?.content || m?.text || ""));
  });
}

function mapServerMessageMetadata(msg) {
  const meta = msg?.metadata;
  if (!meta || typeof meta !== "object") return undefined;
  const out = {};
  if (meta.intakeReadyForSearch) out.intakeReadyForSearch = true;
  if (meta.summaryReviewLocked) out.summaryReviewLocked = true;
  if (meta.intakePhase) out.intakePhase = meta.intakePhase;
  if (meta.structuredScope) out.structuredScope = meta.structuredScope;
  if (meta.diagnosisComplete) out.diagnosisComplete = true;
  const fee = Number(meta.checkoutFeeUsd);
  if (Number.isFinite(fee) && fee > 0) out.checkoutFeeUsd = fee;
  return Object.keys(out).length ? out : undefined;
}

/**
 * Returns true when the transcript contains a delivered diagnosis breakdown,
 * either via message metadata or by the DIAGNOSIS_READY_RE marker in text.
 * Used to gate the "Diagnostic Unveiled" UI state.
 */
function isTranscriptDiagnosisReady(messages) {
  if (!Array.isArray(messages)) return false;
  return messages.some(
    (m) =>
      m?.role === "assistant" &&
      (m.metadata?.diagnosisComplete ||
        DIAGNOSIS_READY_RE.test(String(m.content || "")))
  );
}

/** User replied after the pre-checkout "anything else?" prompt (mirrors backend gate). */
function isFinalDetailsGateSatisfiedInTranscript(messages) {
  if (!Array.isArray(messages) || !messages.length) return false;

  let lastFinalDetailsAskIdx = -1;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (
      m?.role === "assistant" &&
      FINAL_DETAILS_BEFORE_CHECKOUT_RE.test(String(m.content || ""))
    ) {
      lastFinalDetailsAskIdx = i;
    }
  }
  if (lastFinalDetailsAskIdx < 0) return false;

  return messages
    .slice(lastFinalDetailsAskIdx + 1)
    .some((m) => m?.role === "user" && String(m.content || "").trim());
}

function assistantAskedFinalDetailsBeforeCheckoutInTranscript(messages) {
  if (!Array.isArray(messages)) return false;
  return messages.some(
    (m) =>
      m?.role === "assistant" &&
      FINAL_DETAILS_BEFORE_CHECKOUT_RE.test(String(m.content || ""))
  );
}

function assistantDeliveredCheckoutHandoffInTranscript(messages) {
  if (!Array.isArray(messages)) return false;
  return messages.some(
    (m) =>
      m?.role === "assistant" &&
      parseQuotedSearchFeeUsd(String(m.content || "")) !== null
  );
}

/** Mirrors backend userSignalsReadyToSearch for checkout unlock. */
function userSignalsReadyToSearchInTranscript(messages) {
  if (!Array.isArray(messages) || !messages.length) return false;

  const userLines = messages
    .filter((m) => m?.role === "user" || m?.from === "user")
    .map((m) => String(m.content || m.text || "").trim())
    .filter(Boolean)
    .slice(-6);

  const USER_DONE_RE = /^\s*(nope|no|nothing|that'?s\s+it|that\s+is\s+it|all\s+set|we'?re\s+good)\s*[.!]?\s*$/i;

  for (let i = userLines.length - 1; i >= 0; i--) {
    const text = userLines[i];
    const lower = text.toLowerCase();
    if (USER_DONE_RE.test(text)) return true;
    if (
      /\b(i'?m\s+ready|ready\s+to\s+(go|proceed|start)|move\s+forward|let'?s\s+(go|move)|line\s+up|get\s+quotes|that'?s\s+all|nothing\s+else|no\s+other\s+details|proceed|start\s+search|i\s+am\s+ready|that\s+is\s+it)\b/i.test(
        lower
      )
    ) {
      return true;
    }
    if (/^\s*(yes|yeah|yep|sure)\s*[.!]?\s*$/i.test(text)) return true;
  }
  return false;
}

/** True when checkout is allowed (mirrors backend resolveCheckoutReady). */
function isTranscriptReadyForVendorSearchCheckout(messages) {
  if (!Array.isArray(messages) || !messages.length) return false;

  if (
    messages.some(
      (m) =>
        m?.metadata?.intakeReadyForSearch || m?.metadata?.summaryReviewLocked
    )
  ) {
    return true;
  }

  if (assistantDeliveredCheckoutHandoffInTranscript(messages)) {
    return true;
  }

  if (isFinalDetailsGateSatisfiedInTranscript(messages)) {
    return true;
  }

  if (
    assistantAskedFinalDetailsBeforeCheckoutInTranscript(messages) &&
    userSignalsReadyToSearchInTranscript(messages)
  ) {
    return true;
  }

  return false;
}

function buildBidsPanelAckSnapshot({
  scopedBids,
  scopeSummary,
  checkoutReady,
  diagnosisReady,
  intakeReady,
}) {
  const bidIds = (scopedBids || [])
    .map((b) => String(b.id || b.bidId || ""))
    .filter(Boolean)
    .sort()
    .join(",");
  return {
    bidIds,
    scopeFp: String(scopeSummary || "").trim(),
    checkoutReady: Boolean(checkoutReady),
    diagnosisReady: Boolean(diagnosisReady),
    intakeReady: Boolean(intakeReady),
  };
}

/** True when bids panel content changed since the user last opened it. */
function bidsPanelHasUnreadUpdates(ack, current) {
  if (!ack || !current) return false;

  const ackBidIds = ack.bidIds ? ack.bidIds.split(",").filter(Boolean) : [];
  const curBidIds = current.bidIds
    ? current.bidIds.split(",").filter(Boolean)
    : [];
  if (curBidIds.length > ackBidIds.length) return true;
  if (curBidIds.length > 0 && current.bidIds !== ack.bidIds) return true;

  if (current.scopeFp !== ack.scopeFp && (current.scopeFp || ack.scopeFp)) {
    return true;
  }

  if (current.checkoutReady && !ack.checkoutReady) return true;
  if (current.intakeReady && !ack.intakeReady) return true;
  if (current.diagnosisReady && !ack.diagnosisReady) return true;

  return false;
}

/** Search-fee dollar patterns, from most to least specific. */
const SEARCH_FEE_PATTERNS = [
  // Exact button copy: "Pay $49 · Start search"
  /Pay\s+\$(\d+)\s*[·\-–]\s*Start search/i,
  // Old handoff prose: "The $95 search fee" / "$25 search fee"
  /\$(\d+)\s+search fee/i,
  // Fee redirect fallback: "one-time $95"
  /one[- ]time\s+\$(\d+)/i,
];

function parseQuotedSearchFeeUsd(text) {
  if (!text) return null;
  const s = String(text);
  for (const pattern of SEARCH_FEE_PATTERNS) {
    const m = s.match(pattern);
    if (m) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

/** Walk backward for the latest assistant handoff fee (chat text or metadata). */
function findLatestQuotedSearchFeeUsd(messageList) {
  if (!Array.isArray(messageList)) return null;
  for (let i = messageList.length - 1; i >= 0; i--) {
    const msg = messageList[i];
    if (msg?.role !== "assistant" && msg?.from !== "assistant") continue;
    const body = msg.content || msg.text || "";
    const fromText = parseQuotedSearchFeeUsd(body);
    if (fromText != null) return fromText;
    const metaFee = Number(msg.metadata?.checkoutFeeUsd);
    if (Number.isFinite(metaFee) && metaFee > 0) return metaFee;
  }
  return null;
}

function tierKeyFromSearchFeeUsd(usd) {
  const n = Number(usd);
  if (n >= 250) return "CAPEX";
  if (n >= 60) return "MAJOR";
  return "MINOR";
}

/** ensure-job requires jobDescription.length >= 28; scope summary alone can be shorter. */
const ENSURE_JOB_MIN_DESC_CHARS = 28;

function extractBlueprintScopeFromMessages(messages) {
  if (!Array.isArray(messages)) return "";
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role !== "assistant" && m?.from !== "assistant") continue;
    const text = String(m.content || m.text || "");
    const scopeMatch = text.match(
      /\*\*Scope of Work:\*\*\s*([\s\S]*?)(?:\n\*\*|$)/i
    );
    if (scopeMatch?.[1]?.trim()) return scopeMatch[1].trim();
  }
  return "";
}

function extractZipFromMessages(messages) {
  if (!Array.isArray(messages)) return "";
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    const text = String(m?.content || m?.text || "");
    const fromSpace = text.match(/\*\*Space:\*\*[^]*?\bZIP\s+(\d{5})\b/i);
    if (fromSpace?.[1]) return fromSpace[1];
    const z = extractZipFromText(text);
    if (z) return z;
  }
  return "";
}

function buildEnsureJobDescription(scope, conversationText, messages = []) {
  const summary = String(scope?.projectSummary || "").trim();
  const convo = String(conversationText || "").trim();
  const blueprintScope = extractBlueprintScopeFromMessages(messages);
  const candidates = [
    convo,
    summary,
    blueprintScope,
    [summary, blueprintScope, convo].filter(Boolean).join(". "),
  ]
    .map((s) => s.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  const best =
    candidates.find((s) => s.length >= ENSURE_JOB_MIN_DESC_CHARS) ||
    candidates[0] ||
    "";
  return best.slice(0, 8000);
}

function extractZipFromText(text) {
  const m = String(text || "").match(/\b(\d{5})(?:-\d{4})?\b/);
  return m ? m[1] : "";
}

/** Scope payload sent to ensure-job with a long enough projectSummary + ZIP when known. */
function buildEnsureJobScopePayload(scope, conversationText, messages = []) {
  if (!scope) return undefined;
  const zip =
    String(scope.zipCode || "").trim() ||
    extractZipFromText(conversationText) ||
    extractZipFromMessages(messages);
  const projectSummary = buildEnsureJobDescription(
    scope,
    conversationText,
    messages
  );
  return {
    ...scope,
    ...(zip ? { zipCode: zip } : {}),
    projectSummary,
  };
}

/** What happens after Pay · Start search (shown in Bids panel). */
function vendorSearchCheckoutSteps() {
  return [
    {
      n: 1,
      text:
        "Click **Pay $49 · Start search** to complete your secure one-time payment and line up vetted pros",
    },
    {
      n: 2,
      text:
        "You return directly to this chat after checkout and receive a confirmation email with your full project summary",
    },
    {
      n: 3,
      text:
        "Competitive bids from matched pros usually arrive within 1-3 business days",
    },
  ];
}

function VendorSearchCheckoutExplainer({ userInfo }) {
  const steps = vendorSearchCheckoutSteps();
  return (
    <BidsSearchExplainer>
      <BidsSearchExplainerTitle>What happens next</BidsSearchExplainerTitle>
      <BidsSearchExplainerSteps>
        {steps.map((step) => (
          <BidsSearchExplainerStep key={step.n}>
            <BidsSearchExplainerStepNum aria-hidden>
              {step.n}
            </BidsSearchExplainerStepNum>
            <BidsSearchExplainerStepText>
              {step.text}
            </BidsSearchExplainerStepText>
          </BidsSearchExplainerStep>
        ))}
      </BidsSearchExplainerSteps>
    </BidsSearchExplainer>
  );
}

/**
 * Heuristic completeness for “enough to open a job” before `linkedTicketId` exists.
 * Per-chat; not authoritative vs. backend rules.
 *
 * Bar: “Would a contractor have enough to produce a defensible rough estimate?”
 * Not substring games on user text (e.g. “how” inside “whole” / “shower”).
 */
function calculateJobInformationCompleteness(messages) {
  if (!Array.isArray(messages)) {
    return { percentage: 0, missing: [], completed: [], score: 0 };
  }

  const userMessages = messages.filter((m) => m.role === "user");
  if (userMessages.length === 0) {
    return { percentage: 0, missing: [], completed: [], score: 0 };
  }

  const conversationText = userMessages
    .map(
      (m) =>
        `${(m.content || "").toLowerCase()} ${(m.images || [])
          .map(() => "photo")
          .join(" ")}`
    )
    .join(" ");

  // Calculate content length (aligns with backend MIN_SCOPE_CHARS = 28)
  const MIN_DESCRIPTION_LENGTH = 28;
  const hasMinimumDescription =
    conversationText.length >= MIN_DESCRIPTION_LENGTH;

  /** Remodel / finish work: vendors need size, finishes direction, and usually photos.
   *  Adjacent-room mentions (e.g. "pantry off the kitchen") must NOT trigger kitchen remodel intake. */
  const primaryScopeIsSmallOrganizational = /\b(pantry|shelv|shelf|shelves|closet\s+organiz|wire\s+shelf|hooks?|mudroom|one[- ]day|small\s+job)\b/i.test(
    conversationText
  );
  const isRemodelLike =
    !primaryScopeIsSmallOrganizational &&
    (/\b(remodel|renovation|renovate|gut\b|makeover|cosmetic\s+refresh|finish\s+work|redoing|full\s+renovation)\b/i.test(
      conversationText
    ) ||
      (/\b(bathroom|kitchen|basement|garage)\b/i.test(conversationText) &&
        /\b(redo|whole\s+thing|entire|outdated|upgrade|refresh|replace|remodel|renovate|new\s+look|make\s+it\s+better|cosmetic|make\s+them\s+better)\b/i.test(
          conversationText
        ) &&
        !/\b(off\s+the|next\s+to|adjacent|near)\s+(kitchen|bathroom)\b/i.test(
          conversationText
        )));

  const hasUploadedPhotos = userMessages.some(
    (m) => (m.images || []).length > 0
  );
  const userDeclinedPhotos = /\b(can'?t|cannot|can not)\s+(send|share|upload|get).*(pic|photo|image)|no\s+pic(ture)?|not right now|without\s+a?\s+photo\b/i.test(
    conversationText
  );
  const userReadyForSearch = /\b(yes\b|ready|move\s+forward|let'?s\s+go|line\s+up|get\s+quotes|that'?s\s+all|nothing\s+else|no\s+other\s+details|proceed|start\s+search|i\s+am\s+ready)\b/i.test(
    conversationText
  );

  // Scope: substantive description; never use bare /how|what|where/ (matches inside "whole", "shower", etc.)
  const hasScopeDescription = Boolean(
    hasMinimumDescription &&
      (conversationText.length >= 140 ||
        userMessages.length >= 4 ||
        /\b(?:sq\.?\s*ft|sqft|\bsf\b|square\s+feet|'\s*x\s*'|\d{1,2}\s*x\s*\d{1,2}\s*(?:ft|feet|')?)\b/i.test(
          conversationText
        ) ||
        /\b(?:tear[-\s]?out|demolition|layout|footprint|drywall|subfloor|waterproof|rough[-\s]?in|gut|full\s+strip|symptom|extent|damage\s+pattern)\b/i.test(
          conversationText
        ) ||
        (/\b(?:tile|vanity|tub|shower|floor(?:ing)?|countertop|cabinet|fixtures?|grout|paint)\b/i.test(
          conversationText
        ) &&
          (userMessages.length >= 3 || conversationText.length >= 90)))
  );

  // Timing: explicit schedule/urgency only (property type is tracked separately).
  // "Next 30-40" and similar "next N[-M]" phrases are treated as a planned timeline.
  const hasExplicitTimeline = /\b(?:urgent|asap|as\s+soon|emergency|today|tomorrow|this\s+week|next\s+week|next\s+\d+|week\s+or\s+two|couple\s+of\s+weeks?|within\s+30|still\s+planning|planning|research|can\s+wait|not\s+urgent|no\s+rush|broken\s+for\s+a\s+while|not\s+an?\s+emergency|flexible|deadline|schedule)\b/i.test(
    conversationText
  );

  const hasPropertyContext = /\b(?:condo|townhome|townhouse|single[- ]family|multi[- ]family|duplex|apartment|detached|house)\b/i.test(
    conversationText
  );

  const hasOccupancyContext = /\b(?:primary(\s+home|\s+house)?|living\s+here|live\s+here|vacant|rental|occupied|during\s+the\s+work|not\s+home|away|tenant)\b/i.test(
    conversationText
  );

  // Legacy timing/access bucket - kept for progress meter weighting
  const hasTimingAccess = hasExplicitTimeline || hasOccupancyContext;

  const hasApproximateSize =
    /\b\d{1,4}\s*(?:sq\.?\s*ft|sqft|\bsf\b|square\s+feet)\b/i.test(
      conversationText
    ) ||
    /\b(?:~|about|approximately|roughly|around)\s*\d{1,4}\s*(?:sq|sqft|sf)\b/i.test(
      conversationText
    ) ||
    /\b\d{1,2}\s*['\u2019]?\s*(?:x|by|×)\s*\d{1,2}\s*['\u2019]?(?:\s*(?:feet|ft|'))?\b/i.test(
      conversationText
    ) ||
    /\b(?:tiny|very\s+small|small|compact|average|medium|large|oversized|step[- ]in)\s+(?:full\s+)?(?:bathroom|bath|half\s+bath|kitchen|pantry|closet|walk[- ]in)\b/i.test(
      conversationText
    ) ||
    (/\b(small|medium|large|step[- ]in|compact|tiny)\b/i.test(
      conversationText
    ) &&
      /\b(bathroom|bath|kitchen|remodel|makeover|powder\s+room|pantry|closet)\b/i.test(
        conversationText
      ));

  const hasMaterialsOrFinishesDirection =
    /\b(?:tile|porcelain|ceramic|marble|quartz|granite|laminate|lvp|hardwood|vanity|countertop|cabinets?|fixtures?|flooring|paint(?:ed)?|grout|finish\s*level|budget|premium|mid[-\s]?range|stock|custom|builder\s*grade|painted\s+wood|sturdy|drawers?|hooks?|shelv)\b/i.test(
      conversationText
    ) ||
    /\b(?:cosmetic|refresh|outdated|new\s+look|resurface|reface)\b/i.test(
      conversationText
    ) ||
    /\b(?:keep(ing)?\s+(everything|it|things)\s+in\s+the\s+same|same\s+spots|same\s+layout|not\s+moving\s+plumbing|same\s+footprint)\b/i.test(
      conversationText
    );

  const hasMaterialsResponsibilityPlan =
    /\b(?:buy(?:ing)?\s+(?:the\s+)?materials\s+(?:myself|ourselves)|i(?:'ll| will)\s+(?:buy|get|source|pick\s+up|purchase|order)\s+(?:the\s+)?(?:materials|tile|fixtures|supplies|cabinets)|owner[\s-]?(?:furnished|supplied)|homeowner\s+suppl(?:y|ies)|(?:i|we)\s+(?:will\s+)?(?:supply|provide)\s+(?:the\s+)?materials)\b/i.test(
      conversationText
    ) ||
    /\b(?:contractor\s+(?:to\s+)?(?:buy|get|supply|source|handle|provide|order)|want\s+(?:the\s+)?contractor\s+to\s+(?:handle|buy|get|supply)|turnkey|materials?\s+included\s+in\s+(?:bid|quote)|let\s+(?:them|the\s+pro|the\s+contractor)\s+(?:handle|get|buy|supply))\b/i.test(
      conversationText
    ) ||
    /\b(?:split|mix\s+of\s+both|some\s+(?:materials?\s+)?myself)\b/i.test(
      conversationText
    ) ||
    /\b(?:already\s+(?:bought|purchased|ordered)|materials?\s+already|have\s+materials?\s+on\s+hand)\b/i.test(
      conversationText
    );

  // Mirrors the backend's assistant-echo fallback: if the user's first message was too
  // brief (e.g. "A") but the AI acknowledged with "Got it - [project description]...",
  // the echo counts as a valid problem description so the frontend gate stays in sync.
  const PROJECT_KW_RE = /\b(?:leak|broken|not working|issue|problem|repair|fix|replace|maintenance|clog|drain|toilet|sink|hvac|heat|cool|door|window|light|electric|plumb|damage|install|service|check|inspect|clean|stuck|loose|noise|smell|water|pipe|wire|outlet|switch|faucet|shower|tub|appliance|furnace|ac|air condition|heating|cooling|electrical|carpentry|flooring|roof|gutter|paint|remodel|renovation|refresh|upgrade|redo|conversion|walk-in|walk\s+in|fence|deck|cabinet|bedroom|bathroom|kitchen)\b/i;
  const assistantEchoHasProjectKw = messages.some((m) => {
    if (m?.role !== "assistant") return false;
    const text = String(m.content || "").trim();
    const match = text.match(
      /^(?:got it|perfect|great|sure|ok|understood)[!.\s,-]+(.{20,})/i
    );
    if (!match) return false;
    const echo = match[1].split("\n")[0];
    return PROJECT_KW_RE.test(echo);
  });

  const infoChecks = {
    // Backend requirement: Category identification (25% weight)
    hasProblemCategory: {
      weight: 30,
      present:
        PROJECT_KW_RE.test(conversationText) || assistantEchoHasProjectKw,
    },
    // Backend requirement: Service location with ZIP code (25% weight)
    hasServiceLocation: {
      weight: 25,
      present:
        // ZIP code pattern (5 digits, optionally followed by dash and 4 more digits)
        /\b\d{5}(?:-\d{4})?\b/.test(conversationText) ||
        // Address-related keywords
        /(?:zip|zipcode|address|unit|apt|apartment|property|building|location|house|home|residence|\d{3,5}\s+\w+|st\.|ave\.|blvd\.|rd\.|drive|street|avenue|lane|way|court|circle|place)/i.test(
          conversationText
        ),
    },
    // Sufficient scope for a vendor rough estimate (not substring “question words”)
    hasScopeDescription: {
      weight: 25,
      present: hasScopeDescription,
    },
    // Visual documentation: higher bar for remodel / finish work
    hasPhotos: {
      weight: isRemodelLike ? 22 : 15,
      present:
        hasUploadedPhotos ||
        userDeclinedPhotos ||
        (!isRemodelLike && hasScopeDescription && userMessages.length >= 3),
    },
    // Timing and access
    hasTimingAccess: {
      weight: 5,
      present: hasTimingAccess,
    },
    hasPropertyType: {
      weight: 8,
      present: hasPropertyContext,
    },
    hasExplicitTimeline: {
      weight: 7,
      present: hasExplicitTimeline,
    },
  };

  if (isRemodelLike) {
    infoChecks.hasApproximateSize = {
      weight: 18,
      present: hasApproximateSize,
    };
    infoChecks.hasMaterialsOrFinishesDirection = {
      weight: 18,
      present: hasMaterialsOrFinishesDirection,
    };
    infoChecks.hasMaterialsResponsibilityPlan = {
      weight: 14,
      present: hasMaterialsResponsibilityPlan,
    };
  }

  let totalScore = 0;
  let maxScore = 0;
  const missing = [];
  const completed = [];

  const labels = {
    hasProblemCategory: "Problem description",
    hasServiceLocation: "Property location",
    hasScopeDescription: "Scope of work (what you want done)",
    hasPhotos: "Photos / visual evidence",
    hasTimingAccess: "Timing / access details",
    hasApproximateSize:
      "Approx. size (sq ft, dimensions, or small/medium/large)",
    hasMaterialsOrFinishesDirection: "Materials / finishes direction",
    hasMaterialsResponsibilityPlan:
      "Materials plan (you vs contractor vs split)",
    hasPropertyType: "Property type (house, condo, townhome, etc.)",
    hasExplicitTimeline: "Timeline (urgent, within 30 days, or still planning)",
  };

  Object.entries(infoChecks).forEach(([key, check]) => {
    maxScore += check.weight;
    if (check.present) {
      totalScore += check.weight;
      completed.push(labels[key] || key);
    } else {
      missing.push(labels[key] || key);
    }
  });

  // Debug logging for development (only in non-production environments)
  if (process.env.NODE_ENV === "development" && userMessages.length > 0) {
    const breakdown = Object.entries(infoChecks).map(([key, check]) => ({
      category: labels[key] || key,
      weight: check.weight,
      present: check.present,
      points: check.present ? check.weight : 0,
    }));
    console.log("🎯 Job Information Progress Breakdown:", {
      totalScore,
      maxScore,
      percentage: Math.round((totalScore / maxScore) * 100),
      breakdown,
      missing,
      completed,
      conversationStats: {
        totalLength: conversationText.length,
        meetsMinLength: hasMinimumDescription,
        minRequired: MIN_DESCRIPTION_LENGTH,
        userMessageCount: userMessages.length,
        hasImages: userMessages.some((m) => (m.images || []).length > 0),
        zipCodeDetected: /\b\d{5}(?:-\d{4})?\b/.test(conversationText),
        sampleText:
          conversationText.substring(0, 100) +
          (conversationText.length > 100 ? "..." : ""),
      },
    });
  }

  let percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  if (
    userReadyForSearch &&
    hasScopeDescription &&
    /\b\d{5}(?:-\d{4})?\b/.test(conversationText) &&
    hasExplicitTimeline &&
    hasPropertyContext &&
    percentage >= 75
  ) {
    percentage = Math.max(percentage, VENDOR_SEARCH_READY_PERCENT);
  }

  return { percentage, missing, completed, score: totalScore };
}

/** Explains what finishing the meter unlocks (vendor bids). Desktop shows the checklist beside this.
 * Percent bands are internal UX only (not a payment or legal rule). */
function jobProgressMotivationCopy(
  percentage,
  checkoutUnlocked,
  diagnosisUnveiled
) {
  if (checkoutUnlocked) {
    // Phase 3: Secured Checkout
    return "Blueprint complete. Use **Pay $49 · Start search** in the Vendor Quotes panel to launch your automated bid search.";
  }
  if (diagnosisUnveiled) {
    // Phase 2: Diagnostic Unveiled - one question left before checkout
    return "Free diagnosis is ready. Answer Clumse AI's final question in chat to unlock your bid search.";
  }
  if (percentage >= 85) {
    return "Almost there. Answer Clumse AI's last question to unlock your project blueprint.";
  }
  if (percentage >= 60) {
    return "Good progress. A bit more detail and your free diagnosis and blueprint will be generated.";
  }
  return "Add scope, location, and timing in chat. Your free diagnosis and cost range unlock as this fills in.";
}

/** Local time label for each transcript line (ISO or Date from server). M/D/YYYY + time. */
function formatMsgLocalTime(ts) {
  if (ts == null || ts === "") return "";
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** True when a job record indicates bid-search payment / dispatch. */
function isJobRecordDispatched(job) {
  if (!job) return false;
  if (job.dispatched === true) return true;
  if (job.dispatchPaidAt) return true;
  const status = String(
    job.status || job.clumseDispatchStatus || ""
  ).toUpperCase();
  return ["DISPATCHED", "BIDS_RECEIVED", "CLOSED"].includes(status);
}

/** Visual for thread row from job id on thread + live vendor bids. */
function getThreadBidVisual(thread, bids, dispatchedTicketIds) {
  const idle = {
    kind: "chat",
    Icon: CommentOutlined,
    color: "#80868b",
    label:
      "Chat - vendor quotes appear when Clumse opens a job from this thread",
    spin: false,
  };
  if (!thread?.linkedTicketId) return idle;
  const tid = String(thread.linkedTicketId);

  const isPaid =
    thread.dispatched === true ||
    (dispatchedTicketIds && dispatchedTicketIds.has(tid));

  // Dispatched: user has paid - shield icon + green styling.
  if (isPaid) {
    const forTicket = (bids || []).filter(
      (b) => b?.ticketId != null && String(b.ticketId) === tid
    );
    const hasBids = forTicket.length > 0;
    return {
      kind: "dispatched",
      Icon: SafetyCertificateOutlined,
      color: "#2e7d32",
      label: hasBids
        ? "Bid search paid. Quotes are in."
        : "Bid search paid. Reaching out to contractors.",
      spin: false,
    };
  }

  const forTicket = (bids || []).filter(
    (b) => b?.ticketId != null && String(b.ticketId) === tid
  );
  if (!forTicket.length) {
    return {
      kind: "link_waiting",
      Icon: LinkOutlined,
      color: "#faad14",
      label: "Job from this chat: waiting for bids",
      spin: false,
    };
  }
  if (forTicket.some((b) => !b.status || b.status === "quote")) {
    return {
      kind: "bidding",
      Icon: LoadingOutlined,
      color: "#1a73e8",
      label: "Vendor quotes available",
      spin: false,
    };
  }
  if (
    forTicket.some((b) => b.status === "selected" || b.status === "accepted")
  ) {
    return {
      kind: "awarded",
      Icon: CheckCircleFilled,
      color: "#34a853",
      label: "Vendor selected: contact them directly",
      spin: false,
    };
  }
  return {
    kind: "closed",
    Icon: MinusCircleOutlined,
    color: "#9aa0a6",
    label: "No open bids for this job",
    spin: false,
  };
}

function loadThreads(userId) {
  try {
    const storageKey = getStorageKey(userId);
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) {
      const threads = parsed.map(normalizeThread).filter(Boolean);
      return prepareThreadsForLocalStorage(threads);
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Never persist base64 blobs in localStorage; URLs only (uploaded to server). */
function prepareThreadsForLocalStorage(
  threads,
  { omitAllImages = false } = {}
) {
  return threads.map((th) => ({
    ...th,
    messages: (th.messages || []).map((m) => {
      if (m.role !== "user" || !m.images?.length) return m;
      if (omitAllImages) {
        const { images, ...rest } = m;
        return rest;
      }
      const urlImages = m.images.filter((img) => isImageUrl(img));
      if (urlImages.length === m.images.length) return m;
      const { images, ...rest } = m;
      return urlImages.length ? { ...rest, images: urlImages } : rest;
    }),
  }));
}

function isStorageQuotaError(e) {
  return (
    e?.name === "QuotaExceededError" ||
    e?.code === 22 ||
    String(e?.message || "")
      .toLowerCase()
      .includes("quota")
  );
}

function saveThreads(threads, userId) {
  const key = getStorageKey(userId);
  const attempts = [
    () => prepareThreadsForLocalStorage(threads),
    () => prepareThreadsForLocalStorage(threads, { omitAllImages: true }),
  ];
  for (const build of attempts) {
    try {
      localStorage.setItem(key, JSON.stringify(build()));
      return;
    } catch (e) {
      if (!isStorageQuotaError(e)) return;
    }
  }
}

async function resizeImageFileToJpegDataUrl(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  if (file.size > MAX_IMAGE_INPUT_BYTES) {
    throw new Error("Each image must be about 12MB or smaller.");
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      // Use window.Image: `Image` is also imported from antd and would shadow HTMLImageElement.
      const image = new window.Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not read that image."));
      image.src = objectUrl;
    });
    const maxW = 1600;
    const maxH = 1600;
    let w = img.naturalWidth;
    let h = img.naturalHeight;
    const scale = Math.min(maxW / w, maxH / h, 1);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not process image.");
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.82);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function threadTitleFromMessage(text, images) {
  const t = String(text || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!t && images?.length) return "Image message";
  if (!t) return "New chat";
  return t.length > 44 ? `${t.slice(0, 44)}…` : t;
}

function bidJobId(bid) {
  return String(bid?.ticketId ?? bid?.jobRequestId ?? "").trim();
}

/** Optional Google Places / Maps style fields on vendor bids. */
function bidGoogleReviewsFromBid(bid) {
  const rawR = bid?.googlePlaceRating ?? bid?.vendorRating ?? bid?.placeRating;
  const rawC =
    bid?.googleReviewCount ?? bid?.reviewCount ?? bid?.googleUserRatingsTotal;
  const rating = Number(rawR);
  const count = Number(rawC);
  return {
    rating: Number.isFinite(rating) ? Math.min(5, Math.max(0, rating)) : null,
    count: Number.isFinite(count) && count >= 0 ? Math.round(count) : null,
  };
}

/** Approximate road / straight-line miles from job to vendor (API may set any one). */
function bidDistanceMilesFromBid(bid) {
  const raw =
    bid?.distanceMiles ??
    bid?.milesFromJob ??
    bid?.milesFromSite ??
    bid?.milesFromProperty ??
    bid?.drivingDistanceMiles ??
    bid?.approxMilesFromProperty;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(8000, n);
}

function formatBidDistanceLine(miles) {
  if (miles == null) {
    return "Approx. miles from your job once routed";
  }
  const rounded =
    miles >= 100 ? Math.round(miles) : Math.round(miles * 10) / 10;
  return `~${rounded} mi from your job address`;
}

/**
 * Vendor-side Clumse AI thread: optional fields from Firestore / API.
 * Prefer `vendorChatSummary` (short prose); also reads availability / timeline.
 */
function vendorClumseContextFromBid(bid) {
  if (!bid || typeof bid !== "object") {
    return {
      paragraphs: [],
      bullets: [],
      hasAny: false,
    };
  }
  const raw = String(
    bid.vendorChatSummary ||
      bid.clumseVendorChatSummary ||
      bid.vendorSummary ||
      bid.aiVendorSummary ||
      ""
  ).trim();
  const availability = String(
    bid.vendorAvailability ||
      bid.availabilityText ||
      bid.estimatedStart ||
      bid.earliestStart ||
      bid.startWindow ||
      ""
  ).trim();
  const timeline = String(
    bid.timeline || bid.proposedTimeline || bid.vendorTimeline || ""
  ).trim();

  const bullets = [];
  if (availability) {
    bullets.push({
      key: "start",
      label: "Start / availability",
      text: availability,
    });
  }
  if (timeline) {
    bullets.push({ key: "timeline", label: "Timeline", text: timeline });
  }

  const paragraphs = raw
    ? raw
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
    : [];

  const hasAny = paragraphs.length > 0 || bullets.length > 0;

  return { paragraphs, bullets, hasAny };
}

/** Recent job requests the landlord can tie this chat to (ownerId / landlordId). */
async function fetchOwnerJobPicklist(uid) {
  const merged = new Map();
  for (const field of ["ownerId", "landlordId"]) {
    try {
      const q = query(
        collection(db, "jobRequests"),
        where(field, "==", uid),
        limit(40)
      );
      const snap = await getDocs(q);
      snap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const title = String(d.title || d.description || "").trim();
        merged.set(docSnap.id, {
          id: docSnap.id,
          title: title || `Job ${docSnap.id.slice(0, 8)}…`,
          updatedAt:
            typeof d.updatedAt?.toMillis === "function"
              ? d.updatedAt.toMillis()
              : Number(d.updatedAt) || 0,
          status: String(d.status || "").toUpperCase(),
          dispatchPaidAt: d.dispatchPaidAt ?? null,
          dispatched: isJobRecordDispatched(d),
        });
      });
    } catch (e) {
      // Rules often allow one index shape but not both; chat must not crash the page.
      const code = e && typeof e === "object" ? e.code : "";
      console.warn(
        `[ClumseChat] jobRequests picklist query (${field}) skipped:`,
        code || e
      );
    }
  }
  return Array.from(merged.values()).sort(
    (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)
  );
}

/** Mirrors backend PROCUREMENT_SAVINGS_FEE_RATE for UI display only. */
function dispatchPrimaryLabel(procurement, loading) {
  if (loading) return "Starting…";
  if (!procurement) return "Start vendor outreach";
  if (
    procurement.dispatchOrchestrationIncluded ||
    Number(procurement.dispatchOrchestrationFeeUsd) === 0
  ) {
    return "Start vendor outreach (included in your plan)";
  }
  const n = Number(procurement.dispatchOrchestrationFeeUsd ?? CLUMSE_SEARCH_FEE_USD);
  return `Pay $${n} · Start search`;
}

function savingsFeeRateFromUserInfo(userInfo) {
  const k = String(userInfo?.selectedPlan || userInfo?.subscription || "free")
    .toLowerCase()
    .trim();
  const m = {
    starter: 0.2,
    growth: 0.1,
    scale: 0.05,
    enterprise: 0.05,
    retail: 0.1,
    professional: 0.1,
    institutional: 0.05,
    free: 0.2,
    pro: 0.1,
  };
  return m[k] ?? 0.2;
}

function bidStatusTagColor(status) {
  switch (status) {
    case "contacted":
      return "success";
    case "declined":
      return "error";
    case "withdrawn":
      return "default";
    default:
      return "blue"; // Default "quote" status
  }
}

/** Human-readable label for bid status tags. */
function formatBidStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "contacted") return "Contacted";
  if (s === "declined") return "Declined";
  if (s === "withdrawn") return "Withdrawn";
  if (!s || s === "quote") return "Quote";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Phone or email the owner can use (matches ContactVendor actions). */
function bidVendorHasDirectContact(bid) {
  if (!bid || typeof bid !== "object") return false;
  const phone = String(bid.vendorPhone || bid.contractorPhone || "").trim();
  const email = String(bid.vendorEmail || bid.contractorEmail || "").trim();
  return Boolean(phone) || Boolean(email);
}

function bidVendorContactDisplayLine(bid) {
  if (!bid) return "";
  const phone = String(bid.vendorPhone || bid.contractorPhone || "").trim();
  const email = String(bid.vendorEmail || bid.contractorEmail || "").trim();
  const parts = [];
  if (phone) parts.push(phone);
  if (email) parts.push(email);
  return parts.join(" · ");
}

/**
 * Split "Work summary (123 Main St · Unit 2)" for a two-line header.
 * If there is no trailing "(...)" group, the whole string is the primary line.
 */
function splitLinkedJobLabelLines(label) {
  if (label == null) return { primary: "", secondary: null };
  const s = String(label).trim();
  if (!s) return { primary: "", secondary: null };
  const m = s.match(/^(.*)\s+\(([^)]+)\)\s*$/);
  if (m && String(m[1]).trim()) {
    return { primary: String(m[1]).trim(), secondary: String(m[2]).trim() };
  }
  return { primary: s, secondary: null };
}

/**
 * Full-page Clumse chat (Gemini-inspired layout): thread rail on the left,
 * scrollable transcript, composer pinned to the bottom (no page footer on /chat).
 */
function getInitialThreadsState() {
  // Don't load from localStorage at startup - wait for user authentication.
  // Hydrate (or the signed-out stub effect) creates the first thread only when none exist.
  return { threads: [], activeId: null };
}

const ClumseChat = () => {
  const { isMobile } = useBreakpoints();
  const { user, userInfo, loading: authLoading } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { chatId, slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const apiUrl = getApiUrl();
  const isMarketingSandbox = useMemo(
    () => isMarketingSandboxActive(location.pathname, searchParams),
    [location.pathname, searchParams]
  );

  const isDemo = useMemo(
    () =>
      isMarketingSandbox ||
      isActiveDemoQueryParam(
        searchParams.has("demo"),
        searchParams.get("demo")
      ),
    [searchParams, isMarketingSandbox]
  );

  const [marketingPhase, setMarketingPhase] = useState(() => {
    if (typeof window !== "undefined") {
      if (isMarketingSandboxPath(window.location.pathname)) {
        return normalizeMarketingPhase(
          new URLSearchParams(window.location.search).get("phase")
        );
      }
    }
    return "live_bids";
  });
  const [marketingForceUnreadBadge, setMarketingForceUnreadBadge] =
    useState(false);

  const showMarketingControls = useMemo(
    () => isMarketingDebugControlsEnabled(location.pathname, searchParams),
    [location.pathname, searchParams]
  );

  const [threads, setThreads] = useState(
    () => resolveInitialChatState().threads
  );
  const [activeId, setActiveId] = useState(
    () => resolveInitialChatState().activeId
  );

  /** Client-side navigation to `?demo` / `?demo=true` without full reload. */
  useEffect(() => {
    if (!isDemo || isMarketingSandbox) return;
    setThreads((prev) => {
      if (prev?.[0]?.__demo && !prev?.[0]?.__marketingSandbox) return prev;
      const next = getDemoThreads();
      queueMicrotask(() => setActiveId(next[0].id));
      return next;
    });
  }, [isDemo, isMarketingSandbox]);

  /** Marketing sandbox: auto-pin amplified $ pulse on live-bids phase for mobile grabs. */
  useEffect(() => {
    if (!isMarketingSandbox || !showMarketingControls) return;
    setMarketingForceUnreadBadge(marketingPhase === "live_bids");
  }, [isMarketingSandbox, showMarketingControls, marketingPhase]);

  /** Leaving demo mode: drop stub threads; hydrate reloads saved chats or creates one if empty. */
  useEffect(() => {
    if (isDemo) return;
    setThreads((prev) => {
      if (!prev?.some((t) => t.__demo)) return prev;
      queueMicrotask(() => setActiveId(null));
      return [];
    });
  }, [isDemo]);

  /** Signed-out visitors: one local draft so the composer works before sign-in. */
  useEffect(() => {
    if (authLoading || isDemo || user?.uid) return;
    setThreads((prev) => {
      if (prev?.length) return prev;
      const fresh = createThread();
      queueMicrotask(() => setActiveId(fresh.id));
      return [fresh];
    });
  }, [authLoading, isDemo, user?.uid]);
  const [inputValue, setInputValue] = useState(() => {
    // Pre-populate from SEO landing page CTA link: ?preMessage=...
    const pre = searchParams.get("preMessage");
    return pre ? decodeURIComponent(pre) : "";
  });

  /** Remove SEO pre-hydration params from the URL after they've been consumed. */
  useEffect(() => {
    const hasPreMessage = searchParams.has("preMessage");
    const hasZip = searchParams.has("zip");
    if (!hasPreMessage && !hasZip) return;
    const next = new URLSearchParams(searchParams);
    next.delete("preMessage");
    next.delete("zip");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount only

  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  /** True while a user message is in flight and Clumse AI has not finished replying. */
  const composerLocked = isLoading || isTyping;
  const [showBidsDrawer, setShowBidsDrawer] = useState(false);
  const [bidsPanelTab, setBidsPanelTab] = useState("quotes");
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  /** In-flight image uploads: local preview until server URL is ready. */
  const [uploadingImages, setUploadingImages] = useState([]);
  const isUploadingImages = uploadingImages.length > 0;
  const attachedImageCount = pendingImages.length + uploadingImages.length;
  /** Latest Graydient remodel preview for this Firestore thread (optional background gen). */
  const [remodelPreviewUi, setRemodelPreviewUi] = useState(null);
  const [renameThreadId, setRenameThreadId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deleteThreadId, setDeleteThreadId] = useState(null);
  const [bidDetailModal, setBidDetailModal] = useState(null);
  const [dispatchCheckoutLoading, setDispatchCheckoutLoading] = useState(false);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [escrowSecuring, setEscrowSecuring] = useState(false);
  const [titleAnimations, setTitleAnimations] = useState(new Map()); // Map<threadId, { title: string, startTime: number }>
  const [bidSortBy, setBidSortBy] = useState("best_overall"); // Sorting state for bids

  /** Vendor SMS negotiation: owner deep-link from escalation alert (?negotiationUnblock=threadId). */
  const [negotiationUnblockOpen, setNegotiationUnblockOpen] = useState(false);
  const [negotiationUnblockThreadId, setNegotiationUnblockThreadId] = useState(
    null
  );
  const [
    negotiationOwnerAnswerDraft,
    setNegotiationOwnerAnswerDraft,
  ] = useState("");
  const [
    negotiationUnblockSubmitting,
    setNegotiationUnblockSubmitting,
  ] = useState(false);

  // Cleanup completed title animations
  useEffect(() => {
    const cleanup = () => {
      setTitleAnimations((prev) => {
        const now = Date.now();
        const next = new Map();
        for (const [threadId, animation] of prev) {
          // Keep animations that started less than 5 seconds ago
          if (now - animation.startTime < 5000) {
            next.set(threadId, animation);
          }
        }
        return next.size !== prev.size ? next : prev;
      });
    };

    const interval = setInterval(cleanup, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isDemo) return;
    const tid = searchParams.get("negotiationUnblock")?.trim();
    if (!tid) {
      setNegotiationUnblockOpen(false);
      setNegotiationUnblockThreadId(null);
      return;
    }
    setNegotiationUnblockThreadId(tid);
    setNegotiationUnblockOpen(true);
  }, [searchParams, isDemo]);

  const closeNegotiationUnblockModal = useCallback(() => {
    setNegotiationUnblockOpen(false);
    setNegotiationOwnerAnswerDraft("");
    const next = new URLSearchParams(searchParams);
    next.delete("negotiationUnblock");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const submitVendorNegotiationOwnerAnswer = useCallback(async () => {
    const text = negotiationOwnerAnswerDraft.trim();
    if (!text || !negotiationUnblockThreadId) return;
    if (!user?.uid) {
      message.error("Sign in to send this update.", 6.5);
      return;
    }
    try {
      setNegotiationUnblockSubmitting(true);
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) throw new Error("Not signed in");
      const res = await fetch(`${apiUrl}/protocol/negotiation/owner-answer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          threadId: negotiationUnblockThreadId,
          answer: text,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || "Could not send update");
      }
      message.success("Update sent. The vendor will be texted.");
      closeNegotiationUnblockModal();
    } catch (e) {
      message.error(e?.message || "Failed to submit", 6.5);
    } finally {
      setNegotiationUnblockSubmitting(false);
    }
  }, [
    apiUrl,
    closeNegotiationUnblockModal,
    negotiationOwnerAnswerDraft,
    negotiationUnblockThreadId,
    user?.uid,
  ]);

  const {
    data: bids = [],
    isLoading: bidsLoading,
    isError: bidsFetchError,
  } = useQuery({
    queryKey: isMarketingSandbox
      ? ["clumse-chat-bids", "marketing", marketingPhase]
      : isDemo
        ? ["clumse-chat-bids", "demo"]
        : ["bids", user?.uid],
    queryFn: async () => {
      if (isMarketingSandbox && marketingPhase === "live_bids") {
        return buildMarketingSandboxBids();
      }
      if (isDemo) return getDemoBids();
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Not signed in");
      }
      const response = await fetch(`${apiUrl}/vendor-bids/my-bids`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Failed to load bids");
      }
      const data = await response.json();
      return data.bids || [];
    },
    enabled:
      (isMarketingSandbox && marketingPhase === "live_bids") ||
      isDemo ||
      (!!user?.uid && !authLoading),
    retry: 2,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const mainTopBarRef = useRef(null);
  const bidsPaneRef = useRef(null);
  const openBidsPanel = useCallback(
    (tab = "quotes") => {
      if (!marketingForceUnreadBadge) {
        acknowledgeBidsPanelRef.current();
      }
      setBidsPanelTab(tab);
      if (isMobile) {
        setShowBidsDrawer(true);
      } else {
        bidsPaneRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "nearest",
        });
      }
    },
    [isMobile, marketingForceUnreadBadge]
  );
  const [mobileChatHeaderStackPx, setMobileChatHeaderStackPx] = useState(
    MOBILE_CHAT_HEADER_STACK_PX
  );

  const activeIdRef = useRef(activeId);
  const threadsRef = useRef(threads);
  /** Per-thread homeowner edits to structured scope (keyed by server or client thread id). */
  const editedScopeByThreadRef = useRef(new Map());
  const [editedScopePayload, setEditedScopePayload] = useState(null);
  /** Firestore ids deleted this session; filtered out of hydrate merge if the list is briefly stale. */
  const deletedServerConversationIdsRef = useRef(new Set());
  /** Avoid duplicate handling when React Strict Mode or deps re-run the same Stripe return URL. */
  const stripeDispatchReturnHandledRef = useRef("");
  const vendorClarifyHandledRef = useRef("");
  /** True after Stripe return + confirm-return until procurement refetch catches up. */
  const [dispatchPaidConfirmed, setDispatchPaidConfirmed] = useState(false);
  /** Per-thread snapshot when the mobile bids panel was last opened (ack baseline). */
  const bidsPanelAckRef = useRef(new Map());
  const bidsPanelSnapshotRef = useRef(null);
  const acknowledgeBidsPanelRef = useRef(() => {});
  const [bidsPanelAckTick, setBidsPanelAckTick] = useState(0);
  const lastUserIdRef = useRef(null);
  const sendInFlightRef = useRef(false);
  activeIdRef.current = activeId;
  threadsRef.current = threads;

  useEffect(() => {
    setDispatchPaidConfirmed(false);
  }, [activeId]);

  // SECURITY: Clear localStorage from previous user when a different user logs in
  useEffect(() => {
    if (!user?.uid) return;

    if (lastUserIdRef.current && lastUserIdRef.current !== user.uid) {
      deletedServerConversationIdsRef.current.clear();
      // Different user logged in - clear old user's localStorage
      try {
        const oldStorageKey = getStorageKey(lastUserIdRef.current);
        localStorage.removeItem(oldStorageKey);
        console.log(
          `🧹 [SECURITY] Cleared localStorage for previous user: ${lastUserIdRef.current}`
        );
      } catch (error) {
        console.error(
          "❌ [SECURITY] Failed to clear previous user's localStorage:",
          error
        );
      }
    }

    lastUserIdRef.current = user.uid;

    // Load threads for current user from localStorage
    if (!isDemo) {
      const loaded = loadThreads(user.uid);
      if (loaded?.length) {
        const normalized = loaded
          .map((t) => normalizeThread(t))
          .filter(Boolean);
        const deduped = dedupeEmptyClientDraftThreads(normalized, null);
        setThreads(deduped);
        setActiveId(deduped[0].id);
        console.log(
          `✅ [SECURITY] Loaded ${deduped.length} threads for user: ${user.uid}`
        );
      }
    }
  }, [user?.uid, isDemo]);

  /** Load a specific chat by ID from URL parameter (declared before hydrate effect that calls it). */
  const loadSpecificChat = useCallback(
    async (targetChatId, token) => {
      try {
        if (deletedServerConversationIdsRef.current.has(targetChatId)) {
          console.log(
            "[ClumseChat:hydrate] loadSpecificChat skipped (tombstone)",
            {
              targetChatId,
              tombstoneIds: [...deletedServerConversationIdsRef.current],
            }
          );
          navigate("/chat", { replace: true });
          message.warning("This chat was deleted.", 6.5);
          return;
        }
        const oneUrl = `${apiUrl}/conversations/${encodeURIComponent(
          targetChatId
        )}`;
        console.log("[ClumseChat:hydrate] loadSpecificChat GET", { oneUrl });
        const res = await fetch(oneUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          if (res.status === 404) {
            console.warn("[ClumseChat:hydrate] loadSpecificChat 404", {
              targetChatId,
            });
            navigate("/chat", { replace: true });
            message.error(
              "Chat not found. Redirected to your recent conversations.",
              6.5
            );
            return;
          }
          if (res.status === 403) {
            console.warn("[ClumseChat:hydrate] loadSpecificChat 403", {
              targetChatId,
            });
            navigate("/chat", { replace: true });
            message.error(
              "Access denied. This chat belongs to another user.",
              6.5
            );
            return;
          }
          throw new Error("Failed to load chat");
        }

        const data = await res.json();
        if (data?.success && data.conversation) {
          if (data.conversation.userId !== user.uid) {
            console.warn(
              "[ClumseChat:hydrate] loadSpecificChat userId mismatch",
              {
                targetChatId,
                expectedUid: user.uid,
                docUserId: data.conversation.userId,
              }
            );
            navigate("/chat", { replace: true });
            message.error(
              "Access denied. This chat belongs to another user.",
              6.5
            );
            return;
          }

          const newThread = threadFromServerConversation(data.conversation);
          console.log(
            "[ClumseChat:hydrate] loadSpecificChat merged into rail",
            {
              threadId: newThread.id,
              serverConversationId: newThread.serverConversationId,
            }
          );
          setThreads((prev) => {
            const exists = prev.find(
              (t) =>
                t.id === newThread.id ||
                t.serverConversationId === newThread.serverConversationId
            );
            if (exists) return prev;
            return [newThread, ...prev];
          });
          setActiveId(newThread.id);

          const canonicalSlug =
            newThread.slug ||
            generateChatSlug(newThread.title, newThread.messages);
          if (!isSlugCanonical(urlSlug, canonicalSlug)) {
            const redirectId = newThread.serverConversationId || newThread.id;
            const targetUrl = canonicalSlug
              ? `/chat/${redirectId}/${canonicalSlug}`
              : `/chat/${redirectId}`;
            navigate(targetUrl, { replace: true });
          }
        }
      } catch (e) {
        console.warn("[ClumseChat:hydrate] loadSpecificChat error", {
          targetChatId,
          error: e,
        });
        navigate("/chat", { replace: true });
        message.error(
          "Failed to load the requested chat. Please try again.",
          6.5
        );
      }
    },
    [navigate, user?.uid, apiUrl, urlSlug]
  );

  /** Load Clumse AI threads from the server so the same account sees chats on any device. */
  useEffect(() => {
    if (!user?.uid || isDemo) return;
    let cancelled = false;
    (async () => {
      try {
        // Get authentication token
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token || cancelled) return;

        const params = new URLSearchParams({
          userId: user.uid,
          userType: "landlord",
          conversationType: "ai_chat",
          propertyId: user.uid,
          limit: "50",
        });
        const listUrl = `${apiUrl}/conversations?${params}`;
        console.log("[ClumseChat:hydrate] GET conversations", {
          listUrl,
          chatId: chatId ?? null,
          urlSlug: urlSlug ?? null,
          tombstoneIds: [...deletedServerConversationIdsRef.current],
        });
        const res = await fetch(listUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok || cancelled) {
          console.warn("[ClumseChat:hydrate] list fetch failed", {
            ok: res.ok,
            status: res.status,
            cancelled,
            listUrl,
          });
          if (!cancelled && res.status === 404) {
            message.warning(
              "Could not load saved chats from the server. You may only see chats saved on this browser until the API is updated.",
              6.5
            );
          }
          return;
        }
        const data = await res.json();
        if (!data?.success || !Array.isArray(data.conversations) || cancelled) {
          console.log(
            "[ClumseChat:hydrate] list payload invalid or cancelled",
            {
              success: data?.success,
              isArray: Array.isArray(data?.conversations),
              count: Array.isArray(data?.conversations)
                ? data.conversations.length
                : null,
              cancelled,
            }
          );
          return;
        }
        const serverIds = data.conversations.map((c) => c?.id).filter(Boolean);
        console.log("[ClumseChat:hydrate] server returned", {
          count: data.conversations.length,
          serverIds,
        });
        let merged = mergeServerAiThreadsIntoLocal(
          data.conversations,
          threadsRef.current
        );

        const mergedIdsBeforeTomb = merged.map((t) => ({
          id: t.id,
          serverConversationId: t.serverConversationId ?? null,
        }));
        const tomb = deletedServerConversationIdsRef.current;
        if (tomb.size > 0) {
          const removedByTomb = [];
          merged = merged.filter((thread) => {
            const sid =
              thread.serverConversationId ||
              (thread.id && !isClientUiThreadId(thread.id) ? thread.id : null);
            const drop = sid && tomb.has(sid);
            if (drop) removedByTomb.push(sid);
            return !drop;
          });
          console.log("[ClumseChat:hydrate] tombstone filter", {
            tombstoneIds: [...tomb],
            removedServerIds: removedByTomb,
            mergedCountAfter: merged.length,
          });
        }
        console.log(
          "[ClumseChat:hydrate] merged threads (pre empty-draft logic)",
          {
            count: merged.length,
            threads: mergedIdsBeforeTomb,
          }
        );

        // Only auto-create a draft when the account has no chats at all; otherwise use + New chat.
        let finalThreads = merged;
        if (finalThreads.length === 0) {
          const freshThread = createThread();
          finalThreads = [freshThread];
          console.log(
            "[ClumseChat:hydrate] created first empty draft (no chats exist)",
            { freshId: freshThread.id }
          );
        }

        // Draft threads use client ids `t_<timestamp>`; they are not Firestore docs. Recover from
        // localStorage when the URL points at one that did not come back in the server merge.
        if (chatId && isClientUiThreadId(chatId)) {
          const already = finalThreads.some(
            (t) => t.id === chatId || t.serverConversationId === chatId
          );
          if (!already) {
            const fromDisk = loadThreads(user.uid)?.find(
              (t) => t.id === chatId || t.serverConversationId === chatId
            );
            if (fromDisk) {
              const normalized = normalizeThread(fromDisk);
              const deduped = new Map(
                [normalized, ...finalThreads].map((t) => [t.id, t])
              );
              finalThreads = [...deduped.values()].sort(
                (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)
              );
            }
          }
        }

        finalThreads = dedupeEmptyClientDraftThreads(
          finalThreads,
          isClientUiThreadId(chatId) ? chatId : null
        );

        if (cancelled) {
          console.log(
            "[ClumseChat:hydrate] cancelled before setThreads; skipping apply"
          );
          return;
        }
        console.log("[ClumseChat:hydrate] applying setThreads", {
          count: finalThreads.length,
          ids: finalThreads.map((t) => ({
            id: t.id,
            serverConversationId: t.serverConversationId ?? null,
          })),
        });
        setThreads(finalThreads);

        // Handle URL-based chat selection with slug validation
        if (chatId) {
          const urlChat = finalThreads.find(
            (t) => t.id === chatId || t.serverConversationId === chatId
          );
          if (urlChat) {
            setActiveId(urlChat.id);

            // Check if slug needs canonical redirect
            const canonicalSlug =
              urlChat.slug || generateChatSlug(urlChat.title, urlChat.messages);
            if (!isSlugCanonical(urlSlug, canonicalSlug)) {
              const redirectId = urlChat.serverConversationId || urlChat.id;
              const targetUrl = canonicalSlug
                ? `/chat/${redirectId}/${canonicalSlug}`
                : `/chat/${redirectId}`;
              navigate(targetUrl, { replace: true });
            }
          } else if (isClientUiThreadId(chatId)) {
            const params = new URLSearchParams(location.search);
            const isPostPaymentReturn = params.get("dispatch") === "success";
            const returnJobId =
              params.get("jobRequestId") || params.get("ticketId");

            if (isPostPaymentReturn) {
              const byJob =
                returnJobId &&
                finalThreads.find(
                  (t) => String(t.linkedTicketId || "") === returnJobId
                );
              const fallback = byJob || finalThreads[0];
              if (fallback) {
                setActiveId(fallback.id);
                const redirectId =
                  fallback.serverConversationId || fallback.id;
                const canonicalSlug =
                  fallback.slug ||
                  generateChatSlug(fallback.title, fallback.messages);
                const targetUrl = canonicalSlug
                  ? `/chat/${redirectId}/${canonicalSlug}`
                  : `/chat/${redirectId}`;
                navigate(targetUrl, { replace: true });
              } else {
                navigate("/chat", { replace: true });
              }
            } else {
              navigate("/chat", { replace: true });
              message.warning(
                "That draft chat is no longer available. Opened your chats instead.",
                6.5
              );
            }
          } else {
            // Server conversation id; fetch from API
            await loadSpecificChat(chatId, token);
          }
        } else {
          // No chatId in URL - redirect to most recent chat or stay on /chat if it's a fresh empty thread
          const mostRecentThread = finalThreads[0];
          if (mostRecentThread && location.pathname === "/chat") {
            // Only redirect if the thread has content or is persisted to server
            const hasContent = mostRecentThread.messages?.some(
              (m) =>
                m.role === "user" && (m.content?.trim() || m.images?.length > 0)
            );
            const isPersisted = mostRecentThread.serverConversationId;

            if (hasContent || isPersisted) {
              const urlId =
                mostRecentThread.serverConversationId || mostRecentThread.id;
              const threadSlug =
                mostRecentThread.slug ||
                generateChatSlug(
                  mostRecentThread.title,
                  mostRecentThread.messages
                );
              const targetUrl = threadSlug
                ? `/chat/${urlId}/${threadSlug}`
                : `/chat/${urlId}`;
              navigate(targetUrl, { replace: true });
            } else {
              // Fresh empty thread - stay on /chat without redirect
              setActiveId(mostRecentThread.id);
            }
          } else {
            setActiveId((cur) =>
              finalThreads.some((x) => x.id === cur) ? cur : finalThreads[0].id
            );
          }
        }
      } catch (e) {
        console.warn("[ClumseChat:hydrate] Failed to hydrate conversations", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    user?.uid,
    isDemo,
    apiUrl,
    chatId,
    urlSlug,
    loadSpecificChat,
    navigate,
    location.pathname,
    location.search,
  ]);

  /** Handle URL synchronization when chatId changes */
  useEffect(() => {
    if (!chatId || isDemo) return;

    // If we have a chatId in URL but it doesn't match activeId, try to switch to it
    const targetThread = threads.find(
      (t) => t.id === chatId || t.serverConversationId === chatId
    );
    if (targetThread && targetThread.id !== activeId) {
      setActiveId(targetThread.id);
    }
  }, [chatId, threads, activeId, isDemo]);

  /** Check if a thread is active based on URL */
  const isThreadActive = (thread) => {
    if (!chatId) return thread.id === activeId;
    return thread.id === chatId || thread.serverConversationId === chatId;
  };

  /** Sync slug to Firestore when chat title changes */
  const syncSlugToFirestore = useCallback(
    async (conversationId, title, messages = []) => {
      if (!conversationId || !user?.uid || isDemo) return;

      try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;

        const slug = generateChatSlug(title, messages);

        // Update the conversation document with the new slug
        const response = await fetch(
          `${apiUrl}/conversations/${conversationId}/slug`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ slug }),
          }
        );

        if (!response.ok) {
          console.warn(
            `Failed to sync slug for conversation ${conversationId}`
          );
        }
      } catch (error) {
        console.warn(
          `Error syncing slug for conversation ${conversationId}:`,
          error
        );
      }
    },
    [apiUrl, user?.uid, isDemo]
  );

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeId) || threads[0],
    [threads, activeId]
  );

  useLayoutEffect(() => {
    if (!isMobile) return;
    const el = mainTopBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const sync = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) setMobileChatHeaderStackPx(h);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile, activeId, isDemo, activeThread?.title]);

  const linkedTicketId = activeThread?.linkedTicketId || null;

  const { data: ownerJobPicklist = [], isLoading: picklistLoading } = useQuery({
    queryKey: ["clumse-chat-owner-jobs", user?.uid],
    queryFn: () => fetchOwnerJobPicklist(user.uid),
    enabled: Boolean(user?.uid) && !isDemo,
    staleTime: 60_000,
    retry: false,
  });

  /** Job IDs where bid-search payment / dispatch is confirmed (from Firestore). */
  const dispatchedTicketIds = useMemo(() => {
    const ids = new Set();
    (ownerJobPicklist || []).forEach((job) => {
      if (job?.id && isJobRecordDispatched(job)) ids.add(String(job.id));
    });
    return ids;
  }, [ownerJobPicklist]);

  // Keep local thread badges in sync when server job records show paid dispatch.
  useEffect(() => {
    if (!dispatchedTicketIds.size) return;
    setThreads((prev) => {
      let changed = false;
      const next = prev.map((th) => {
        const tid = String(th.linkedTicketId || "");
        if (!tid || !dispatchedTicketIds.has(tid) || th.dispatched) return th;
        changed = true;
        return { ...th, dispatched: true };
      });
      return changed ? next : prev;
    });
  }, [dispatchedTicketIds]);

  const scopedBids = useMemo(() => {
    if (!linkedTicketId) return [];
    const tid = String(linkedTicketId);
    return (bids || []).filter((b) => bidJobId(b) === tid);
  }, [bids, linkedTicketId]);

  const jobLinkSelectOptions = useMemo(() => {
    const m = new Map();
    (ownerJobPicklist || []).forEach((j) => {
      m.set(j.id, { value: j.id, label: j.title });
    });
    (bids || []).forEach((b) => {
      const id = bidJobId(b);
      if (!id || m.has(id)) return;
      m.set(id, {
        value: id,
        label: b.ticketTitle || `Job …${id.slice(-8)}`,
      });
    });
    return Array.from(m.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [ownerJobPicklist, bids]);

  const linkedJobLabel = useMemo(() => {
    if (!linkedTicketId) return null;
    const opt = jobLinkSelectOptions.find(
      (o) => String(o.value) === String(linkedTicketId)
    );
    return opt?.label || `Job …${String(linkedTicketId).slice(-8)}`;
  }, [linkedTicketId, jobLinkSelectOptions]);

  /** "Job title (address · unit)" → two lines for mobile bids header */
  const linkedJobLabelLines = useMemo(
    () => splitLinkedJobLabelLines(linkedJobLabel),
    [linkedJobLabel]
  );

  const bidStats = useMemo(() => {
    if (!linkedTicketId) {
      return {
        vendorChosen: false,
        bidRangeLabel: "-",
        bidRangeParts: null,
      };
    }
    const list = scopedBids;
    const contacted = list.filter((b) => b.status === "contacted");
    const vendorChosen = contacted.length > 0;
    const amounts = list
      .map((b) => Number(b.bidAmount))
      .filter((n) => Number.isFinite(n) && n > 0);
    let bidRangeLabel = "No bids yet";
    let bidRangeParts = null;
    if (amounts.length === 1) {
      bidRangeLabel = `$${amounts[0].toLocaleString()}`;
    } else if (amounts.length > 1) {
      const min = Math.min(...amounts);
      const max = Math.max(...amounts);
      if (min === max) {
        bidRangeLabel = `$${min.toLocaleString()}`;
      } else {
        bidRangeLabel = `$${min.toLocaleString()}-$${max.toLocaleString()}`;
        bidRangeParts = { min, max };
      }
    }
    return { vendorChosen, bidRangeLabel, bidRangeParts };
  }, [linkedTicketId, scopedBids]);

  const { data: procurement, isLoading: procurementLoading } = useQuery({
    queryKey: isMarketingSandbox
      ? ["procurement", "marketing", marketingPhase, linkedTicketId]
      : isDemo
        ? ["procurement", "demo", linkedTicketId]
        : ["procurement", linkedTicketId],
    queryFn: async () => {
      if (!linkedTicketId) return null;
      if (
        isMarketingSandbox &&
        linkedTicketId === MARKETING_SANDBOX_JOB_ID
      ) {
        return buildMarketingProcurement(marketingPhase);
      }
      if (isDemo && linkedTicketId === DEMO_JOB_ID) {
        return getDemoProcurement();
      }
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(
        `${apiUrl}/repairs/${linkedTicketId}/procurement-summary`,
        { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to load job status");
      }
      return res.json();
    },
    enabled:
      Boolean(linkedTicketId) &&
      (!!user?.uid ||
        (isDemo && linkedTicketId === DEMO_JOB_ID) ||
        (isMarketingSandbox && linkedTicketId === MARKETING_SANDBOX_JOB_ID)),
  });

  useEffect(() => {
    setRemodelPreviewUi(null);
    if (isDemo || !user?.uid) return undefined;
    // Poll for inspiration renders as soon as a server conversation ID is known - // no need to wait for a paid job (linkedTicketId) to exist.
    const convId = activeThread?.serverConversationId;
    if (!convId) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 48;

    const pickLatestGen = (gens) => {
      if (!Array.isArray(gens) || !gens.length) return null;
      return [...gens].sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      )[0];
    };

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > maxAttempts) return;
      try {
        const data = await fetchLandlordConversation(convId);
        const conv = data?.conversation;
        const latest = pickLatestGen(conv?.context?.visualVisionGenerations);
        if (!latest?.sessionId || cancelled) return;
        setRemodelPreviewUi((prev) => {
          if (prev?.sessionId === latest.sessionId && prev?.dismissed) {
            return prev;
          }
          return {
            sessionId: latest.sessionId,
            status: latest.status,
            imageUrls: Array.isArray(latest.imageUrls) ? latest.imageUrls : [],
            revealed:
              prev?.sessionId === latest.sessionId ? prev.revealed : false,
            dismissed:
              prev?.sessionId === latest.sessionId ? prev.dismissed : false,
          };
        });
      } catch {
        /* non-fatal */
      }
    };

    const id = setInterval(() => void tick(), 4000);
    void tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isDemo, user?.uid, activeThread?.serverConversationId, linkedTicketId]);

  useEffect(() => {
    const d = searchParams.get("dispatch");
    if (d === "cancel") {
      message.info("Checkout canceled", 6.5);
      const next = new URLSearchParams(searchParams);
      next.delete("dispatch");
      setSearchParams(next, { replace: true });
      return;
    }

    const tid =
      searchParams.get("jobRequestId") || searchParams.get("ticketId");
    if (d !== "success" || !tid || isDemo) {
      if (d !== "success") stripeDispatchReturnHandledRef.current = "";
      return;
    }

    const dedupeKey = `success:${tid}`;
    if (stripeDispatchReturnHandledRef.current === dedupeKey) return;
    stripeDispatchReturnHandledRef.current = dedupeKey;

    const convFromRoute = String(chatId || "").trim();

    if (convFromRoute) {
      const hit = threads.find(
        (th) =>
          th.id === convFromRoute || th.serverConversationId === convFromRoute
      );
      if (hit) setActiveId(hit.id);
    }

    setThreads((prev) =>
      prev.map((th) => {
        const sid = String(th.serverConversationId || "").trim();
        const matchesConv =
          convFromRoute && (sid === convFromRoute || th.id === convFromRoute);
        const matchesActive =
          !convFromRoute && String(th.id) === String(activeId);
        const matchesJob = String(th.linkedTicketId || "") === tid;
        if (matchesConv || matchesActive || matchesJob) {
          return { ...th, linkedTicketId: tid };
        }
        return th;
      })
    );

    void (async () => {
      let outreach = false;
      let activated = false;
      let conversationIdForRefresh = convFromRoute || "";
      try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          const confirmRes = await fetch(
            `${apiUrl}/protocol/dispatch/confirm-return`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ jobRequestId: tid }),
            }
          );
          const confirmJson = await confirmRes.json().catch(() => ({}));
          if (confirmRes.ok && confirmJson.activated) {
            activated = true;
            setDispatchPaidConfirmed(true);
            // Persist dispatched badge to this thread so the sidebar shows it on reload.
            setThreads((prev) =>
              prev.map((th) =>
                String(th.linkedTicketId || "") === tid
                  ? { ...th, dispatched: true }
                  : th
              )
            );
            openBidsPanel("quotes");
            const refreshedScope = normalizeStructuredScope(
              confirmJson.structuredScope
            );
            if (refreshedScope) {
              const scopeThreadKey =
                convFromRoute ||
                threads.find((th) => String(th.linkedTicketId || "") === tid)
                  ?.id ||
                activeId ||
                "default";
              editedScopeByThreadRef.current.delete(scopeThreadKey);
              setEditedScopePayload(null);
            }
          }
          if (confirmJson.conversationId) {
            conversationIdForRefresh = String(confirmJson.conversationId);
          }

          const r = await fetch(
            `${apiUrl}/repairs/${encodeURIComponent(tid)}/procurement-summary`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (r.ok) {
            const j = await r.json();
            outreach = Boolean(j.vendorOutreachAfterDispatch);
            if (
              ["DISPATCHED", "BIDS_RECEIVED", "CLOSED"].includes(
                String(j.clumseDispatchStatus || "").toUpperCase()
              )
            ) {
              setDispatchPaidConfirmed(true);
              // Persist dispatched badge to thread for sidebar rendering on reload.
              setThreads((prev) =>
                prev.map((th) =>
                  String(th.linkedTicketId || "") === tid
                    ? { ...th, dispatched: true }
                    : th
                )
              );
            }
          }

          const refreshConvId =
            conversationIdForRefresh ||
            String(
              threads.find(
                (th) =>
                  String(th.linkedTicketId || "") === tid ||
                  th.id === convFromRoute ||
                  th.serverConversationId === convFromRoute
              )?.serverConversationId || ""
            ).trim();
          if (refreshConvId) {
            try {
              const data = await fetchLandlordConversation(refreshConvId);
              const conv = data?.conversation;
              if (conv?.messages?.length) {
                const msgs = serverChatMessagesToUiMessages(conv.messages);
                setThreads((prev) =>
                  prev.map((th) => {
                    const matches =
                      th.id === refreshConvId ||
                      th.serverConversationId === refreshConvId ||
                      String(th.linkedTicketId || "") === tid;
                    if (!matches) return th;
                    return {
                      ...th,
                      serverConversationId: conv.id,
                      linkedTicketId: tid,
                      messages: msgs,
                      updatedAt: Date.now(),
                    };
                  })
                );
                setTimeout(() => {
                  messagesEndRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }, 200);
              }
            } catch {
              /* non-fatal */
            }
          }
        }
      } catch {
        /* ignore */
      }
      if (activated) {
        if (outreach) {
          message.success(
            "Payment received. Check your inbox for a confirmation - vendor outreach is running."
          );
        } else {
          message.success(
            "Payment received. Check your inbox for a confirmation email with your project summary."
          );
        }
      } else if (outreach) {
        message.success(
          "Payment received. Vendor outreach is running for this job."
        );
      } else {
        message.success("Payment received. Syncing your job status…");
      }
      void queryClient.invalidateQueries({ queryKey: ["procurement", tid] });
      void queryClient.invalidateQueries({ queryKey: ["bids", user?.uid] });
      const next = new URLSearchParams(searchParams);
      next.delete("dispatch");
      next.delete("ticketId");
      next.delete("jobRequestId");
      setSearchParams(next, { replace: true });
    })();
  }, [
    isDemo,
    searchParams,
    setSearchParams,
    queryClient,
    user?.uid,
    activeId,
    chatId,
    apiUrl,
    threads,
    openBidsPanel,
  ]);

  const hireAndFundVault = useCallback(async () => {
    if (!linkedTicketId || !bidDetailModal?.id) return;
    if (isDemo) {
      message.info("Demo mode: Hire & Fund is not sent to the server.", 6.5);
      setBidDetailModal(null);
      return;
    }
    setVaultLoading(true);
    setEscrowSecuring(true);

    // Show escrow securing state for transparency
    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Sign in required");
      }
      if (bidDetailModal.status !== "accepted") {
        const acceptRes = await fetch(
          `${apiUrl}/vendor-bids/bids/${bidDetailModal.id}/status`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: "accepted" }),
          }
        );
        const acceptData = await acceptRes.json().catch(() => ({}));
        if (!acceptRes.ok) {
          throw new Error(
            acceptData.error ||
              acceptData.message ||
              "Could not select this bid for direct contact"
          );
        }
        await queryClient.invalidateQueries({ queryKey: ["bids", user?.uid] });
        await queryClient.invalidateQueries({
          queryKey: ["procurement", linkedTicketId],
        });
      }
      const bench = Number(procurement?.lockedBenchmarkUsd || 0);
      const bidAmt = Number(bidDetailModal.bidAmount || 0);
      const rate = savingsFeeRateFromUserInfo(userInfo);
      const gap = Math.max(0, bench - bidAmt);
      const tierCalculatedFee = Math.round(gap * rate * 100) / 100;
      const res = await fetch(`${apiUrl}/protocol/escrow/intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          jobRequestId: linkedTicketId,
          ticketId: linkedTicketId,
          tierCalculatedFee,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.error || "Could not setup direct contractor contact"
        );
      }
      if (data.clientSecret) {
        message.info(
          "Contact setup initialized. Complete setup to contact the contractor directly.",
          6.5
        );
      } else {
        message.success("Contact setup ready.");
      }
      setBidDetailModal(null);
    } catch (e) {
      message.error(e?.message || "Vault checkout failed", 6.5);
    } finally {
      setEscrowSecuring(false);
      setVaultLoading(false);
    }
  }, [
    apiUrl,
    bidDetailModal,
    linkedTicketId,
    procurement?.lockedBenchmarkUsd,
    queryClient,
    user?.uid,
    userInfo,
    isDemo,
  ]);

  const connectWithVendor = useCallback(() => {
    const bid = bidDetailModal;
    if (!bid?.id || !user?.uid) return;

    if (isDemo) {
      message.success(
        "Connection sent! Both you and the vendor will receive project details via email and SMS."
      );
      setBidDetailModal(null);
      return;
    }

    Modal.confirm({
      title: "Connect with this vendor?",
      content:
        "This will send both you and the vendor a summary email and SMS with all project details, contact info, and next steps. You'll handle everything directly from there.",
      okText: "Send Connection",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();
          if (!token) {
            message.error("Sign in required", 6.5);
            return;
          }

          // Generate project summary from chat
          let projectSummary = `Project: ${
            bid.workDescription || "Property repair work"
          }

Quote: $${(bid.bidAmount || bid.estimatedCost || 0).toLocaleString()}
Timeline: ${bid.timeline || "To be determined"}

This project was discussed and quoted through Clumse AI. Both parties are aligned on the scope and pricing.`;

          try {
            const summaryRes = await fetch(
              `${apiUrl}/bid-connect/generate-summary`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  chatId: activeId,
                  bidId: bid.id,
                }),
              }
            );

            if (summaryRes.ok) {
              const summaryData = await summaryRes.json();
              projectSummary =
                summaryData.data.projectSummary || projectSummary;
            }
          } catch (summaryError) {
            console.warn(
              "[ConnectVendor] Summary generation failed, using fallback:",
              summaryError
            );
          }

          // Send connection notifications
          const connectRes = await fetch(
            `${apiUrl}/bid-connect/send-notifications`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                bidId: bid.id,
                vendorId: bid.vendorId || bid.vendor_id,
                projectSummary,
                jobDetails: {
                  description: bid.workDescription || "Property repair project",
                  location: bid.propertyAddress || "Property location",
                  timeline: bid.timeline || "To be determined",
                  budget: `$${(
                    bid.bidAmount ||
                    bid.estimatedCost ||
                    0
                  ).toLocaleString()}`,
                },
              }),
            }
          );

          const connectData = await connectRes.json().catch(() => ({}));
          if (!connectRes.ok) {
            throw new Error(
              connectData.error ||
                connectData.message ||
                "Could not send connection notifications"
            );
          }

          message.success({
            content:
              "Connection sent! Both you and the vendor will receive project details via email and SMS.",
            duration: 6.5,
          });

          await queryClient.invalidateQueries({
            queryKey: ["bids", user.uid],
          });
          await queryClient.invalidateQueries({
            queryKey: ["procurement", linkedTicketId],
          });
          setBidDetailModal(null);
        } catch (e) {
          message.error(
            e?.message || "Could not send connection notifications",
            6.5
          );
          throw e;
        }
      },
    });
  }, [
    apiUrl,
    bidDetailModal,
    activeId,
    isDemo,
    linkedTicketId,
    queryClient,
    user?.uid,
  ]);

  const messages = useMemo(() => {
    const th = threads.find((t) => t.id === activeId);
    if (!th) {
      // Fallback to first thread if activeId not found
      const fallback = threads[0];
      return Array.isArray(fallback?.messages) ? fallback.messages : [];
    }
    return Array.isArray(th.messages) ? th.messages : [];
  }, [threads, activeId]);

  const jobInfoCompleteness = useMemo(
    () => calculateJobInformationCompleteness(messages),
    [messages]
  );

  const transcriptCheckoutReady = useMemo(
    () => isTranscriptReadyForVendorSearchCheckout(messages),
    [messages]
  );

  /** True once the Value Shock diagnosis has been delivered in this conversation. */
  const transcriptDiagnosisReady = useMemo(
    () => isTranscriptDiagnosisReady(messages),
    [messages]
  );

  const jobMeterMaxRef = useRef({ threadId: null, max: 0 });
  const jobMeterTarget = useMemo(() => {
    const raw = jobInfoCompleteness.percentage;
    // Three-phase meter:
    //   Active Chat      →  up to 89% (capped until diagnosis delivered)
    //   Diagnostic Unveiled → 95% (blueprint locked, awaiting final-details reply)
    //   Secured Checkout  → 100% (ready for payment)
    let effective;
    if (transcriptCheckoutReady) {
      effective = 100;
    } else if (transcriptDiagnosisReady) {
      effective = Math.max(raw, 95);
    } else {
      effective = Math.min(raw, VENDOR_SEARCH_READY_PERCENT - 1);
    }
    if (jobMeterMaxRef.current.threadId !== activeId) {
      jobMeterMaxRef.current = { threadId: activeId, max: effective };
      return effective;
    }
    const max = Math.max(jobMeterMaxRef.current.max, effective);
    jobMeterMaxRef.current.max = max;
    return max;
  }, [
    jobInfoCompleteness.percentage,
    activeId,
    transcriptCheckoutReady,
    transcriptDiagnosisReady,
  ]);
  const [jobMeterDisplayed, setJobMeterDisplayed] = useState(jobMeterTarget);
  const jobMeterInitRef = useRef(false);
  const jobMeterPrevActiveRef = useRef(activeId);
  const jobMeterDisplayRef = useRef(jobMeterTarget);
  const jobMeterRafRef = useRef(null);

  useLayoutEffect(() => {
    const target = jobMeterTarget;

    if (!jobMeterInitRef.current) {
      jobMeterInitRef.current = true;
      jobMeterPrevActiveRef.current = activeId;
      jobMeterDisplayRef.current = target;
      setJobMeterDisplayed(target);
      return;
    }

    if (jobMeterRafRef.current != null) {
      cancelAnimationFrame(jobMeterRafRef.current);
      jobMeterRafRef.current = null;
    }

    const switchedChat = jobMeterPrevActiveRef.current !== activeId;
    jobMeterPrevActiveRef.current = activeId;

    if (switchedChat) {
      jobMeterDisplayRef.current = 0;
      setJobMeterDisplayed(0);
    }

    const from = switchedChat ? 0 : jobMeterDisplayRef.current;

    if (from === target) {
      jobMeterDisplayRef.current = target;
      setJobMeterDisplayed(target);
      return;
    }

    const durationMs = 720;
    const t0 = performance.now();
    const startVal = from;

    const tick = (now) => {
      const u = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - (1 - u) ** 3;
      const v = Math.round(startVal + (target - startVal) * eased);
      jobMeterDisplayRef.current = v;
      setJobMeterDisplayed(v);
      if (u < 1) {
        jobMeterRafRef.current = requestAnimationFrame(tick);
      } else {
        jobMeterDisplayRef.current = target;
        setJobMeterDisplayed(target);
        jobMeterRafRef.current = null;
      }
    };

    jobMeterRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (jobMeterRafRef.current != null) {
        cancelAnimationFrame(jobMeterRafRef.current);
        jobMeterRafRef.current = null;
      }
    };
  }, [activeId, jobMeterTarget]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isDemo || threads.some((t) => t.__demo) || !user?.uid) return;
    saveThreads(threads, user.uid);
  }, [threads, isDemo, user?.uid]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, activeId]);

  const scrollToLatestAiMessage = useCallback(() => {
    if (!chatScrollRef.current) return;

    // Find the last assistant message element
    const messageElements = chatScrollRef.current.querySelectorAll(
      '[data-message-role="assistant"]'
    );
    const lastAiMessage = messageElements[messageElements.length - 1];

    if (lastAiMessage) {
      // Scroll to show the beginning of the AI message with some padding
      const scrollContainer = chatScrollRef.current;
      const messageRect = lastAiMessage.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate how much to scroll to bring message to top with padding
      const scrollOffset = messageRect.top - containerRect.top - 20; // 20px padding from top

      if (scrollOffset !== 0) {
        scrollContainer.scrollBy({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (composerLocked) return;
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [activeId, composerLocked]);

  // Auto-scroll to new AI messages (focus is deferred until composer unlocks)
  const prevMessagesLength = useRef(0);
  useLayoutEffect(() => {
    if (messages.length > prevMessagesLength.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.role === "assistant") {
        setTimeout(() => scrollToLatestAiMessage(), 200);
      }
    }
    prevMessagesLength.current = messages.length;
  }, [messages, scrollToLatestAiMessage]);

  const prevComposerLockedRef = useRef(composerLocked);
  useEffect(() => {
    const wasLocked = prevComposerLockedRef.current;
    prevComposerLockedRef.current = composerLocked;
    if (wasLocked && !composerLocked) {
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [composerLocked]);

  const updateActiveMessages = useCallback(
    (updater) => {
      setThreads((prev) =>
        prev.map((th) => {
          if (th.id !== activeId) return th;
          const nextMessages = updater(th.messages);
          const lastUser = [...nextMessages]
            .reverse()
            .find((m) => m.role === "user");
          const title =
            th.messages.length <= 1 && lastUser
              ? threadTitleFromMessage(lastUser.content, lastUser.images)
              : th.title === "New chat" && lastUser
              ? threadTitleFromMessage(lastUser.content, lastUser.images)
              : th.title;
          return {
            ...th,
            messages: nextMessages,
            title,
            updatedAt: Date.now(),
          };
        })
      );
    },
    [activeId]
  );

  useEffect(() => {
    const vendorClarify = searchParams.get("vendorClarify");
    const clarificationId = String(
      searchParams.get("clarificationId") || ""
    ).trim();
    const jobId = String(
      searchParams.get("jobRequestId") || linkedTicketId || ""
    ).trim();
    if (vendorClarify !== "1" || !clarificationId || !jobId || isDemo) return;

    const handledKey = `clarify:${clarificationId}`;
    if (vendorClarifyHandledRef.current === handledKey) return;

    void (async () => {
      try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        const res = await fetch(
          `${getApiUrl()}/repairs/${encodeURIComponent(jobId)}/clarification-prompt?clarificationId=${encodeURIComponent(clarificationId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        const prompt = String(data.prompt || "").trim();
        if (!prompt) return;

        vendorClarifyHandledRef.current = handledKey;
        updateActiveMessages((m) => [
          ...m,
          {
            id: `clarify-${clarificationId}`,
            role: "assistant",
            content: prompt,
            timestamp: new Date().toISOString(),
            metadata: { vendorClarificationPrompt: true, clarificationId },
          },
        ]);
      } catch (err) {
        console.warn("[CLUMSE-CHAT] vendorClarify prompt failed", err);
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("vendorClarify");
        next.delete("clarificationId");
        setSearchParams(next, { replace: true });
      }
    })();
  }, [
    searchParams,
    linkedTicketId,
    isDemo,
    updateActiveMessages,
    setSearchParams,
  ]);

  const handleImageFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || composerLocked) return;

    const slotsLeft =
      MAX_CHAT_IMAGES - pendingImages.length - uploadingImages.length;
    if (slotsLeft <= 0) {
      message.warning(`You can attach up to ${MAX_CHAT_IMAGES} images.`, 6.5);
      return;
    }

    const batch = files.slice(0, slotsLeft).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    if (files.length > slotsLeft) {
      message.warning(`You can attach up to ${MAX_CHAT_IMAGES} images.`, 6.5);
    }

    setUploadingImages((prev) => [...prev, ...batch]);

    const uploadOne = async (item) => {
      try {
        const dataUrl = await resizeImageFileToJpegDataUrl(item.file);
        const result = await uploadImage(dataUrl, item.file.name);
        if (!result?.url) {
          throw new Error("Upload did not return a URL.");
        }
        setPendingImages((prev) => [...prev, result.url]);
      } catch (err) {
        console.error("Failed to upload image:", err);
        message.error(err?.message || "Could not upload image.", 6.5);
      } finally {
        URL.revokeObjectURL(item.previewUrl);
        setUploadingImages((prev) => prev.filter((u) => u.id !== item.id));
      }
    };

    await Promise.allSettled(batch.map(uploadOne));
  };

  const removePendingImage = (index) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    const text = inputValue.trim();
    const imgs = pendingImages.slice();
    if (
      (!text && !imgs.length) ||
      composerLocked ||
      isUploadingImages ||
      sendInFlightRef.current
    )
      return;
    sendInFlightRef.current = true;
    setIsLoading(true);
    setIsTyping(true);

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: text,
      ...(imgs.length ? { images: imgs } : {}),
      timestamp: new Date().toISOString(),
    };

    const prior = messages;
    updateActiveMessages((m) => [...m, userMessage]);
    setInputValue("");
    setPendingImages([]);

    try {
      if (isDemo) {
        setIsTyping(false);
        updateActiveMessages((m) => [
          ...m,
          {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content:
              "**Demo mode** - nothing was sent to the server. Use this to preview the chat layout on mobile and desktop.\n\nRemove `?demo` from the URL (or set `?demo=false`) when you want the real Clumse AI.",
            timestamp: new Date().toISOString(),
            metadata: { demoStub: true },
          },
        ]);

        setTimeout(() => scrollToLatestAiMessage(), 100);

        sendInFlightRef.current = false;
        return;
      }

      const threadIdBeforeSend = activeId;
      const chatIdParam =
        activeThread?.serverConversationId || threadIdBeforeSend;

      // Calculate job completion for Hard Gate enforcement (relaxed after vendor outreach)
      const jobCompletion = calculateJobInformationCompleteness([
        ...prior,
        userMessage,
      ]);
      const jobCompletionWithFlag = {
        percentage: jobCompletion.percentage,
        isComplete: jobCompletion.percentage >= VENDOR_SEARCH_READY_PERCENT,
        missing: jobCompletion.missing,
        completed: jobCompletion.completed,
        ...(linkedTicketId && dispatchUnlocked
          ? { postVendorOutreach: true }
          : {}),
      };

      console.debug(
        `[CLUMSE-CHAT] Sending message: chatId=${
          chatIdParam || "(new)"
        } msgLen=${text.length} images=${imgs.length} jobCompletion=${
          jobCompletionWithFlag?.percentage ?? "?"
        }%`
      );
      const response = await sendChatMessage([...prior, userMessage], {
        chatId: chatIdParam,
        jobCompletion: jobCompletionWithFlag,
        ...(linkedTicketId ? { jobRequestId: linkedTicketId } : {}),
      });
      setIsTyping(false);
      if (response) {
        const convId = String(response.metadata?.conversationId || "").trim();
        const suggestedTitle = String(
          response.metadata?.chatTitle || ""
        ).trim();
        const safeTitle =
          suggestedTitle.length > 60
            ? `${suggestedTitle.slice(0, 57)}...`
            : suggestedTitle;
        const newJobId = jobIdFromSendChatPayload(response.metadata);
        const assistantMsg = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: response.message || response,
          timestamp: new Date().toISOString(),
          metadata: {
            ...(response.metadata || {}),
            ...(response.metadata?.intakeReadyForSearch
              ? { intakeReadyForSearch: true }
              : {}),
            ...(response.metadata?.summaryReviewLocked
              ? { summaryReviewLocked: true }
              : {}),
            ...(response.metadata?.intakePhase
              ? { intakePhase: response.metadata.intakePhase }
              : {}),
            ...(response.metadata?.structuredScope
              ? { structuredScope: response.metadata.structuredScope }
              : {}),
            ...(response.metadata?.checkoutFeeUsd
              ? { checkoutFeeUsd: Number(response.metadata.checkoutFeeUsd) }
              : {}),
            ...(response.metadata?.diagnosisComplete
              ? { diagnosisComplete: true }
              : {}),
          },
        };

        setThreads((prev) =>
          prev.map((th) => {
            if (th.id !== threadIdBeforeSend) return th;
            let next = { ...th };
            if (convId) {
              next.serverConversationId = convId;
              if (isClientUiThreadId(threadIdBeforeSend)) {
                next.id = convId;
              }
            }
            if (safeTitle) {
              next.title = safeTitle;
              // Generate and store slug for the new title
              const newSlug = generateChatSlug(safeTitle, next.messages);
              next.slug = newSlug;

              // Sync slug to Firestore if we have a server conversation ID
              if (next.serverConversationId) {
                syncSlugToFirestore(
                  next.serverConversationId,
                  safeTitle,
                  next.messages
                );
              }
              // Trigger title animation for this thread
              setTitleAnimations(
                (prev) =>
                  new Map(
                    prev.set(threadIdBeforeSend, {
                      title: safeTitle,
                      startTime: Date.now(),
                    })
                  )
              );
            }
            if (newJobId) next.linkedTicketId = newJobId;
            next.messages = [...(th.messages || []), assistantMsg];
            next.updatedAt = Date.now();
            return next;
          })
        );

        setTimeout(() => scrollToLatestAiMessage(), 100);

        if (convId && isClientUiThreadId(threadIdBeforeSend)) {
          setActiveId(convId);
        }
        if (newJobId && user?.uid) {
          void queryClient.invalidateQueries({
            queryKey: ["bids", user.uid],
          });
          void queryClient.invalidateQueries({
            queryKey: ["procurement", newJobId],
          });
        }
      } else {
        throw new Error("No response");
      }
    } catch (error) {
      console.debug(
        "[CLUMSE-CHAT] sendChat error details:",
        "\n  type:",
        error?.constructor?.name,
        "\n  message:",
        error?.message,
        "\n  stack:",
        error?.stack
      );
      console.error("[CLUMSE-CHAT] Chat send failed:", error?.message || error);
      setIsTyping(false);
      updateActiveMessages((m) => [
        ...m,
        {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: "I'm having trouble connecting. Please try again shortly.",
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
      message.error("Could not send message.", 6.5);

      setTimeout(() => scrollToLatestAiMessage(), 100);
    } finally {
      sendInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (composerLocked || isUploadingImages) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const newChat = () => {
    const th = createThread();
    setThreads((prev) =>
      dedupeEmptyClientDraftThreads([th, ...(prev || [])], th.id)
    );
    setActiveId(th.id);
    setMobileRailOpen(false);
    // Keep the URL aligned with the new draft so URL→active sync and deep links stay consistent.
    const draftSlug =
      th.slug || generateChatSlug(th.title, th.messages) || "new-chat";
    navigate(`/chat/${th.id}/${draftSlug}`, { replace: true });
  };

  const selectThread = (id) => {
    // Use navigation to update URL and sync state
    const targetThread = threads.find((t) => t.id === id);
    if (targetThread) {
      // Use serverConversationId if available for cleaner URLs, otherwise use local id
      const urlId = targetThread.serverConversationId || id;
      const threadSlug =
        targetThread.slug ||
        generateChatSlug(targetThread.title, targetThread.messages);
      const targetUrl = threadSlug
        ? `/chat/${urlId}/${threadSlug}`
        : `/chat/${urlId}`;
      navigate(targetUrl);
    } else {
      // Fallback to state update if thread not found
      setActiveId(id);
    }
    setMobileRailOpen(false);
  };

  /**
   * Threads are cached in localStorage and synced with server `ai_chat` conversations
   * (see hydrate effect). Removing a thread hides it from the user and soft-deletes on
   * the server when persisted; conversation data is retained internally.
   */
  const openRenameThread = useCallback((e, th) => {
    e.stopPropagation();
    setRenameThreadId(th.id);
    setRenameDraft(String(th.title || "").trim() || "New chat");
  }, []);

  const closeRenameModal = useCallback(() => {
    setRenameThreadId(null);
    setRenameDraft("");
  }, []);

  const applyRenameThread = useCallback(() => {
    if (!renameThreadId) return;
    const next = renameDraft.trim();
    if (!next) {
      message.warning("Enter a chat name.", 6.5);
      return;
    }
    setThreads((prev) =>
      prev.map((t) =>
        t.id === renameThreadId
          ? { ...t, title: next, updatedAt: Date.now() }
          : t
      )
    );
    message.success("Chat renamed.");
    closeRenameModal();
  }, [renameThreadId, renameDraft, closeRenameModal]);

  const closeDeleteModal = useCallback(() => setDeleteThreadId(null), []);

  const requestDeleteThread = useCallback((e, id) => {
    e.stopPropagation();
    setDeleteThreadId(id);
  }, []);

  const applyDeleteThread = useCallback(async () => {
    const id = deleteThreadId;
    if (!id) return;
    const prev = threadsRef.current;
    const thread = prev.find((t) => t.id === id);
    const persistedConversationId =
      thread?.serverConversationId ||
      (thread?.id && !isClientUiThreadId(thread.id) ? thread.id : null);

    console.log("[ClumseChat:delete] start", {
      uiThreadId: id,
      persistedConversationId,
      serverConversationId: thread?.serverConversationId ?? null,
      threadCount: prev.length,
      isDemo,
      hasAuthUid: Boolean(user?.uid),
    });

    if (persistedConversationId && user?.uid && !isDemo) {
      try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          console.warn(
            "[ClumseChat:delete] no ID token; skipping API soft delete; chat may still appear in history after refresh"
          );
        } else {
          const deleteUrl = `${apiUrl}/conversations/${encodeURIComponent(
            persistedConversationId
          )}`;
          console.log("[ClumseChat:delete] DELETE", deleteUrl);
          const res = await fetch(deleteUrl, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });
          const resBodyText = await res.clone().text();
          console.log("[ClumseChat:delete] response", {
            status: res.status,
            ok: res.ok,
            bodyPreview: resBodyText.slice(0, 500),
          });
          if (!res.ok && res.status !== 404) {
            let err = {};
            try {
              err = JSON.parse(resBodyText);
            } catch {
              /* ignore */
            }
            message.error(
              err.error ||
                `Could not remove this chat from your history (${res.status}). Try again.`,
              6.5
            );
            setDeleteThreadId(null);
            return;
          }
          if (res.status === 404) {
            console.log(
              "[ClumseChat:delete] API returned 404; treating as deleted (doc already missing)"
            );
          }
          deletedServerConversationIdsRef.current.add(persistedConversationId);
          console.log("[ClumseChat:delete] tombstone added", {
            id: persistedConversationId,
            tombstoneCount: deletedServerConversationIdsRef.current.size,
            tombstoneIds: [...deletedServerConversationIdsRef.current],
          });
        }
      } catch (e) {
        console.warn(
          "[ClumseChat:delete] fetch error (server delete failed)",
          e
        );
        message.error(
          "Could not remove this chat from your history. Check your connection and try again.",
          6.5
        );
        setDeleteThreadId(null);
        return;
      }
    } else {
      console.log("[ClumseChat:delete] local-only / skipped API", {
        reason: !persistedConversationId
          ? "no persistedConversationId (draft t_* only)"
          : !user?.uid
          ? "no user.uid"
          : isDemo
          ? "demo mode"
          : "unknown",
      });
    }

    if (prev.length <= 1) {
      const fresh = createThread();
      setThreads([fresh]);
      setActiveId(fresh.id);
      const draftSlug =
        fresh.slug ||
        generateChatSlug(fresh.title, fresh.messages) ||
        "new-chat";
      navigate(`/chat/${fresh.id}/${draftSlug}`, { replace: true });
      console.log("[ClumseChat:delete] replaced sole thread with fresh draft", {
        freshId: fresh.id,
      });
    } else {
      const remaining = prev.filter((t) => t.id !== id);
      setThreads(remaining);
      console.log("[ClumseChat:delete] local state updated", {
        removedUiId: id,
        remainingCount: remaining.length,
        remainingIds: remaining.map((t) => ({
          id: t.id,
          serverConversationId: t.serverConversationId ?? null,
        })),
      });
      if (activeIdRef.current === id) {
        const next = remaining[0];
        if (next) {
          setActiveId(next.id);
          const urlId = next.serverConversationId || next.id;
          const threadSlug =
            next.slug || generateChatSlug(next.title, next.messages);
          const targetUrl = threadSlug
            ? `/chat/${urlId}/${threadSlug}`
            : `/chat/${urlId}`;
          navigate(targetUrl, { replace: true });
          console.log("[ClumseChat:delete] navigate after delete", {
            targetUrl,
          });
        }
      }
    }
    setDeleteThreadId(null);
    message.success("Chat deleted.");
    console.log("[ClumseChat:delete] done");
  }, [deleteThreadId, navigate, apiUrl, user?.uid, isDemo]);

  const sortedThreads = useMemo(
    () => [...threads].sort((a, b) => b.updatedAt - a.updatedAt),
    [threads]
  );

  const liveBidsForPanel = useMemo(
    () =>
      [...scopedBids].sort((a, b) => {
        const aReviews = bidGoogleReviewsFromBid(a);
        const bReviews = bidGoogleReviewsFromBid(b);
        const aAmount = Number(a.bidAmount || 0);
        const bAmount = Number(b.bidAmount || 0);
        const aRating = aReviews.rating || 0;
        const bRating = bReviews.rating || 0;
        const aReviewCount = aReviews.count || 0;
        const bReviewCount = bReviews.count || 0;

        // Parse timeline to get availability score (lower is better/sooner)
        const getAvailabilityScore = (bid) => {
          const timeline = String(bid.timeline || "").toLowerCase();
          if (timeline.includes("today") || timeline.includes("immediately"))
            return 1;
          if (timeline.includes("tomorrow") || timeline.includes("1 day"))
            return 2;
          if (timeline.includes("2 days") || timeline.includes("2-3 days"))
            return 3;
          if (timeline.includes("week") || timeline.includes("7 days"))
            return 7;
          if (timeline.includes("2 weeks") || timeline.includes("14 days"))
            return 14;
          if (timeline.includes("month")) return 30;
          return 7; // Default moderate timeline
        };

        const aAvailability = getAvailabilityScore(a);
        const bAvailability = getAvailabilityScore(b);

        switch (bidSortBy) {
          case "lowest_amount":
            if (aAmount !== bAmount) return aAmount - bAmount;
            break;
          case "best_reviews":
            if (aRating !== bRating) return bRating - aRating;
            if (aReviewCount !== bReviewCount)
              return bReviewCount - aReviewCount;
            break;
          case "fastest_start":
            if (aAvailability !== bAvailability)
              return aAvailability - bAvailability;
            break;
          case "newest_first": {
            const ta = new Date(a.createdAt || a.submittedAt || 0).getTime();
            const tb = new Date(b.createdAt || b.submittedAt || 0).getTime();
            return tb - ta;
          }
          case "best_overall":
          default: {
            // Best Overall: Composite score balancing multiple factors
            // Rating weight: 30%, Price weight: 25%, Availability weight: 25%, Review count weight: 20%
            const maxAmount = Math.max(aAmount, bAmount) || 1;
            const aPriceScore = 1 - aAmount / maxAmount; // Lower price = higher score
            const bPriceScore = 1 - bAmount / maxAmount;
            const aRatingScore = aRating / 5; // Normalize to 0-1
            const bRatingScore = bRating / 5;
            const maxReviewCount = Math.max(aReviewCount, bReviewCount) || 1;
            const aReviewCountScore = aReviewCount / maxReviewCount;
            const bReviewCountScore = bReviewCount / maxReviewCount;
            const aAvailabilityScore = 1 - Math.min(aAvailability / 30, 1); // Sooner = higher score
            const bAvailabilityScore = 1 - Math.min(bAvailability / 30, 1);

            const aComposite =
              aRatingScore * 0.3 +
              aPriceScore * 0.25 +
              aAvailabilityScore * 0.25 +
              aReviewCountScore * 0.2;
            const bComposite =
              bRatingScore * 0.3 +
              bPriceScore * 0.25 +
              bAvailabilityScore * 0.25 +
              bReviewCountScore * 0.2;

            if (Math.abs(aComposite - bComposite) > 0.01)
              return bComposite - aComposite;
            break;
          }
        }

        // Fallback to newest first
        const ta = new Date(a.createdAt || a.submittedAt || 0).getTime();
        const tb = new Date(b.createdAt || b.submittedAt || 0).getTime();
        return tb - ta;
      }),
    [scopedBids, bidSortBy]
  );

  /** Oldest bid for linked job = #1 (same rule as bid detail modal title). */
  const bidChronoNumberById = useMemo(() => {
    if (!linkedTicketId) return new Map();
    const tid = String(linkedTicketId);
    const sameJob = scopedBids.filter((b) => bidJobId(b) === tid);
    const byTime = [...sameJob].sort((a, b) => {
      const ta = new Date(a.createdAt || a.submittedAt || 0).getTime();
      const tb = new Date(b.createdAt || b.submittedAt || 0).getTime();
      return ta - tb;
    });
    const m = new Map();
    byTime.forEach((b, i) => {
      if (b?.id) m.set(b.id, i + 1);
    });
    return m;
  }, [scopedBids, linkedTicketId]);

  // Payment lock: also check the persisted thread.dispatched flag so the pay
  // button never flashes while the procurement query is still loading on return.
  const dispatchUnlocked =
    dispatchPaidConfirmed ||
    activeThread?.dispatched === true ||
    (procurement &&
      ["DISPATCHED", "BIDS_RECEIVED", "CLOSED"].includes(
        String(procurement.clumseDispatchStatus || "").toUpperCase()
      ));

  // paymentStatusSettled: we must not render any payment UI until we actually
  // know whether this chat has been paid. Without this gate, `transcriptCheckoutReady`
  // fires immediately from message metadata while `procurement` and `ownerJobPicklist`
  // are still in-flight, causing the pay button to flash on every reload.
  //
  // Fast path: if the local thread already has dispatched=true, or dispatchPaidConfirmed
  // is set, we know the answer immediately - no need to wait for network.
  // Slow path: if a linkedTicketId exists, wait for BOTH queries to settle so we can
  // verify whether the ticket is already paid before deciding to show the pay button.
  const paymentStatusSettled = useMemo(() => {
    // If already confirmed paid via local state, settle instantly.
    if (dispatchPaidConfirmed || activeThread?.dispatched === true) return true;
    // Demo mode: no real payment state to wait for.
    if (isDemo) return true;
    // If no ticket is linked, there's nothing to check - settle immediately.
    if (!linkedTicketId) return true;
    // A linked ticket exists: wait for the procurement query and picklist to finish.
    return !procurementLoading && !picklistLoading;
  }, [
    dispatchPaidConfirmed,
    activeThread?.dispatched,
    isDemo,
    linkedTicketId,
    procurementLoading,
    picklistLoading,
  ]);

  const bidsSidebarLocked =
    Boolean(linkedTicketId) && Boolean(!dispatchUnlocked);

  const conversationTextForTier = useMemo(() => {
    return messages
      .filter((msg) => msg.role === "user" || msg.from === "user")
      .map((msg) => msg.content || msg.text || "")
      .join("\n");
  }, [messages]);

  const detectedProcurementTier = useMemo(
    () => classifyJobTier(conversationTextForTier),
    [conversationTextForTier]
  );

  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;

  const quotedSearchFeeUsd = useMemo(
    () => findLatestQuotedSearchFeeUsd(messages),
    [messages]
  );

  const checkoutTierKey = useMemo(() => {
    if (quotedSearchFeeUsd != null) {
      return tierKeyFromSearchFeeUsd(quotedSearchFeeUsd);
    }
    return detectedProcurementTier;
  }, [quotedSearchFeeUsd, detectedProcurementTier]);

  /**
   * True when the user has signalled they already have a contractor / crew, or are
   * just comparing prices. Shifts CTA and panel copy from "Find Pros" to "Blueprint Pack".
   */
  const blueprintOnlyMode = useMemo(
    () => detectExistingCrewIntentInMessages(messages),
    [messages]
  );

  /** Pay unlock: server flag, checkout handoff text, or post–final-details user reply. */
  const serverIntakeReady = transcriptCheckoutReady;

  const marketingSummaryCheckout =
    isMarketingSandbox && marketingPhase === "summary_review";

  // Only evaluate readiness after payment status is confirmed - prevents the pay
  // button from flashing on load while procurement/picklist queries are in-flight.
  const readyForVendorSearch =
    paymentStatusSettled &&
    (!isDemo || marketingSummaryCheckout) &&
    !dispatchUnlocked &&
    serverIntakeReady;

  // Only show the pay CTA when intake is actually complete. Do NOT unlock checkout
  // solely because a linked job exists and is unpaid (bidsSidebarLocked).
  const showVendorSearchCheckout = readyForVendorSearch;

  /** Diagnosis delivered but user has not answered the final "anything else?" yet. */
  const awaitingFinalDetailsReply =
    paymentStatusSettled &&
    transcriptDiagnosisReady &&
    !transcriptCheckoutReady &&
    !dispatchUnlocked;

  /** Desktop: pay CTA only in Bids panel; mobile: Bids + rail above composer. */
  const showDispatchPayRail = showVendorSearchCheckout && isMobile;

  const composerPlaceholder = useMemo(() => {
    if (composerLocked) {
      return "Waiting for Clumse AI…";
    }
    if (dispatchUnlocked) {
      return isMobile
        ? "Notes for contractors…"
        : "Ask about your bids, or add notes for contractors…";
    }
    if (linkedTicketId && !dispatchUnlocked) {
      return isMobile ? "Add details…" : "Add job details before checkout…";
    }
    return "Describe your project...";
  }, [composerLocked, dispatchUnlocked, linkedTicketId, isMobile]);

  useEffect(() => {
    if (dispatchUnlocked) {
      setBidsPanelTab("quotes");
    }
  }, [dispatchUnlocked, linkedTicketId]);

  const threadScopeKey = useMemo(
    () => activeThread?.serverConversationId || activeId || "default",
    [activeThread?.serverConversationId, activeId]
  );

  useEffect(() => {
    const saved = editedScopeByThreadRef.current.get(threadScopeKey);
    setEditedScopePayload(saved || null);
  }, [threadScopeKey]);

  const baseStructuredScope = useMemo(() => {
    const fromJob = normalizeStructuredScope(procurement?.structuredScope);
    let scope = fromJob || findLatestStructuredScopeInMessages(messages);
    if (!scope) {
      scope = buildClientScopeFallback(conversationTextForTier, messages);
    }
    return scope ? mergeSupplementalIntoScope(scope, messages) : null;
  }, [procurement?.structuredScope, messages, conversationTextForTier]);

  const effectiveScopePayload = editedScopePayload || baseStructuredScope;

  const bidsPanelSnapshot = useMemo(
    () =>
      buildBidsPanelAckSnapshot({
        scopedBids,
        scopeSummary: effectiveScopePayload?.projectSummary,
        checkoutReady: showVendorSearchCheckout,
        diagnosisReady: transcriptDiagnosisReady,
        intakeReady: transcriptCheckoutReady,
      }),
    [
      scopedBids,
      effectiveScopePayload?.projectSummary,
      showVendorSearchCheckout,
      transcriptDiagnosisReady,
      transcriptCheckoutReady,
    ]
  );

  bidsPanelSnapshotRef.current = bidsPanelSnapshot;

  acknowledgeBidsPanelRef.current = () => {
    const key = String(activeIdRef.current || "");
    if (!key || !bidsPanelSnapshotRef.current) return;
    bidsPanelAckRef.current.set(key, bidsPanelSnapshotRef.current);
    setBidsPanelAckTick((t) => t + 1);
  };

  useEffect(() => {
    const key = String(activeId || "");
    if (!key || bidsPanelAckRef.current.has(key)) return;
    bidsPanelAckRef.current.set(key, bidsPanelSnapshot);
  }, [activeId, bidsPanelSnapshot]);

  const hasUnreadBidsPanel = useMemo(() => {
    if (marketingForceUnreadBadge && isMobile && !showBidsDrawer) {
      return true;
    }
    if (!isMobile || showBidsDrawer) return false;
    const key = String(activeId || "");
    const ack = bidsPanelAckRef.current.get(key);
    if (!ack) return false;
    return bidsPanelHasUnreadUpdates(ack, bidsPanelSnapshot);
    // bidsPanelAckTick: re-run after user dismisses via $ button
  }, [
    isMobile,
    showBidsDrawer,
    activeId,
    bidsPanelSnapshot,
    bidsPanelAckTick,
    marketingForceUnreadBadge,
  ]);

  /** Marketing sandbox: phase-driven St. Louis three-season room fixture. */
  useEffect(() => {
    if (!isMarketingSandbox) return;
    const nextThreads = getMarketingSandboxThreads(marketingPhase);
    setThreads(nextThreads);
    setActiveId(nextThreads[0].id);
    setShowBidsDrawer(false);
    setEditedScopePayload(null);
    editedScopeByThreadRef.current.delete(nextThreads[0].id);
    bidsPanelAckRef.current.delete(nextThreads[0].id);
    setBidsPanelAckTick((t) => t + 1);
  }, [isMarketingSandbox, marketingPhase]);

  const handleScopeEdit = useCallback(
    (next) => {
      setEditedScopePayload(next);
      editedScopeByThreadRef.current.set(threadScopeKey, next);
    },
    [threadScopeKey]
  );

  const scopeForDispatch = effectiveScopePayload;

  const dispatchFeeForCheckout = Number(
    quotedSearchFeeUsd ??
      procurement?.dispatchOrchestrationFeeUsd ??
      CLUMSE_SEARCH_FEE_USD
  );

  const dispatchIncludedForCheckout = Boolean(
    procurement?.dispatchOrchestrationIncluded ||
      Number(procurement?.dispatchOrchestrationFeeUsd) === 0
  );

  const startDispatchCheckout = useCallback(
    async (tierKey = checkoutTierKey) => {
      if (isDemo) {
        message.info(
          "Demo mode: dispatch checkout is not sent to the server.",
          6.5
        );
        return;
      }
      const serverCheckoutReady =
        isTranscriptReadyForVendorSearchCheckout(messages);
      if (!serverCheckoutReady) {
        message.warning(
          "Reply in chat to Clumse AI's last question (anything else about the project?) before starting checkout.",
          6.5
        );
        return;
      }
      setDispatchCheckoutLoading(true);
      try {
        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };
        const convId = activeThread?.serverConversationId || "";
        const clientThreadId = convId ? "" : String(activeId || "").trim();
        const returnChatPath = buildCheckoutReturnChatPath({
          pathname: location.pathname,
          thread: activeThread,
        });

        const ensureJobDescription = buildEnsureJobDescription(
          scopeForDispatch,
          conversationTextForTier,
          messages
        );
        const ensureJobZip =
          scopeForDispatch?.zipCode ||
          extractZipFromText(conversationTextForTier) ||
          extractZipFromMessages(messages) ||
          String(procurement?.structuredScope?.zipCode || "").trim();
        const ensureJobScope = buildEnsureJobScopePayload(
          scopeForDispatch,
          conversationTextForTier,
          messages
        );

        let jobRequestId = linkedTicketId;
        if (!jobRequestId) {
          const canUseConversationFallback =
            Boolean(convId) &&
            (serverCheckoutReady ||
              conversationTextForTier.length >= ENSURE_JOB_MIN_DESC_CHARS ||
              ensureJobDescription.length >= 1);
          if (
            !serverCheckoutReady &&
            ensureJobDescription.length < ENSURE_JOB_MIN_DESC_CHARS &&
            !canUseConversationFallback
          ) {
            message.warning(
              "Add a bit more project detail in chat before starting vendor search.",
              6.5
            );
            return;
          }
          if (!ensureJobZip && !serverCheckoutReady) {
            message.warning(
              "Add your ZIP code in chat before starting vendor search.",
              6.5
            );
            return;
          }
          const ensureRes = await fetch(
            `${apiUrl}/protocol/dispatch/ensure-job`,
            {
              method: "POST",
              headers,
              body: JSON.stringify({
                tierKey,
                jobDescription: ensureJobDescription,
                zipCode: ensureJobZip,
                ...(convId ? { conversationId: convId } : {}),
                ...(ensureJobScope
                  ? { editedScopePayload: ensureJobScope }
                  : {}),
              }),
            }
          );
          const ensureData = await ensureRes.json().catch(() => ({}));
          if (!ensureRes.ok) {
            throw new Error(
              ensureData.message ||
                ensureData.error ||
                "Could not prepare your job for vendor search"
            );
          }
          jobRequestId = ensureData.jobRequestId;
          if (jobRequestId) {
            setThreads((prev) =>
              prev.map((th) =>
                th.id === activeId
                  ? { ...th, linkedTicketId: jobRequestId }
                  : th
              )
            );
            void queryClient.invalidateQueries({
              queryKey: ["procurement", jobRequestId],
            });
          }
        }

        if (!jobRequestId) {
          throw new Error("Could not prepare job for checkout");
        }

        const res = await fetch(`${apiUrl}/protocol/dispatch/checkout`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            jobRequestId,
            tierKey,
            returnChatPath,
            ...(convId ? { conversationId: convId } : {}),
            ...(clientThreadId ? { clientThreadId } : {}),
            ...(ensureJobScope ? { editedScopePayload: ensureJobScope } : {}),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const extra =
            [
              Array.isArray(data.procurementPlanMissing) &&
              data.procurementPlanMissing.length
                ? `Job record: ${data.procurementPlanMissing.join(", ")}.`
                : "",
              Array.isArray(data.intakeMissing) && data.intakeMissing.length
                ? `Chat intake: ${data.intakeMissing.join(", ")}.`
                : "",
              typeof data.message === "string" && data.message.trim()
                ? data.message.trim()
                : "",
            ]
              .filter(Boolean)
              .join(" ") || "";
          const detail =
            typeof data.details === "string" && data.details.trim()
              ? `: ${data.details.trim()}`
              : "";
          throw new Error(
            (data.error || "Could not start dispatch") + detail + extra
          );
        }
        if (data.activated) {
          message.success(
            data.dispatchOrchestrationFeeUsd === 0
              ? "Vendor outreach started. Orchestration is included on your subscription."
              : "Vendor outreach started."
          );
          await queryClient.invalidateQueries({
            queryKey: ["procurement", jobRequestId],
          });
          await queryClient.invalidateQueries({
            queryKey: ["bids", user?.uid],
          });
          return;
        }
        if (!data.url) {
          throw new Error("Could not start checkout");
        }
        window.location.href = data.url;
      } catch (e) {
        message.error(e?.message || "Could not start checkout", 6.5);
      } finally {
        setDispatchCheckoutLoading(false);
      }
    },
    [
      apiUrl,
      linkedTicketId,
      isDemo,
      queryClient,
      user?.uid,
      activeThread?.serverConversationId,
      activeId,
      activeThread,
      location.pathname,
      conversationTextForTier,
      setThreads,
      checkoutTierKey,
      scopeForDispatch,
      messages,
      procurement?.structuredScope?.zipCode,
    ]
  );

  const bidsPanelBody = useMemo(
    () => (
      <BidsPanelInner key={`panel-${activeId}-${linkedTicketId || "none"}`}>
        <BidsPanelTabRow>
          <BidsPanelTab
            type="button"
            $active={bidsPanelTab === "scope"}
            onClick={() => setBidsPanelTab("scope")}
          >
            Scope of Work
          </BidsPanelTab>
          <BidsPanelTab
            type="button"
            $active={bidsPanelTab === "quotes"}
            onClick={() => setBidsPanelTab("quotes")}
          >
            Vendor Quotes
          </BidsPanelTab>
        </BidsPanelTabRow>
        {bidsPanelTab === "scope" ? (
          <BidsPanelBodyScroll>
            <EditableScopeCard
              scope={effectiveScopePayload}
              onScopeChange={handleScopeEdit}
              compact={isMobile}
            />
          </BidsPanelBodyScroll>
        ) : bidsLoading ? (
          <BidsPanelLoading>
            <SharkStatusLog isActive={true} variant="bids" />
          </BidsPanelLoading>
        ) : (
          <>
            <BidsPanelFixedTop>
              {bidsFetchError ? (
                <MutedHelp>
                  No bids yet. Keep chatting, job progress below is from this
                  thread. Refresh the page or check your connection to reload
                  quotes.
                </MutedHelp>
              ) : null}
              <BidsStatGrid
                key={`stats-${activeId}-${linkedTicketId || "no-job"}`}
              >
                {isMobile ? (
                  <GemStatCard
                    $gemStatMobileCenter
                    $bidRangeFullWidth
                    $bidRangeMobileStack={Boolean(bidStats.bidRangeParts)}
                  >
                    {bidStats.bidRangeParts ? (
                      <Statistic
                        title="Bid range"
                        value={bidStats.bidRangeParts.min}
                        formatter={() => (
                          <BidRangeMobileValue>
                            <span className="brv-amt">
                              ${bidStats.bidRangeParts.min.toLocaleString()}
                            </span>
                            <span className="brv-sep" aria-hidden="true">
                              -
                            </span>
                            <span className="brv-amt">
                              ${bidStats.bidRangeParts.max.toLocaleString()}
                            </span>
                          </BidRangeMobileValue>
                        )}
                      />
                    ) : (
                      <Statistic
                        title="Bid range"
                        value={bidStats.bidRangeLabel}
                      />
                    )}
                  </GemStatCard>
                ) : (
                  <Tooltip
                    title={
                      linkedTicketId
                        ? "Lowest and highest dollar amounts among all quotes for this job. One amount if there’s only one bid."
                        : "Shows once this chat has a job with vendor quotes."
                    }
                  >
                    <BidRangeStatWrap>
                      <GemStatCard $bidRangeFullWidth>
                        <Statistic
                          title="Bid range"
                          value={bidStats.bidRangeLabel}
                        />
                      </GemStatCard>
                    </BidRangeStatWrap>
                  </Tooltip>
                )}
              </BidsStatGrid>
              <MutedHelp>
                Summary is for this chat’s job only. Use{" "}
                <strong>View details</strong> on a quote to compare,{" "}
                <strong>select your preferred vendor</strong>, then contact them
                directly.
              </MutedHelp>
            </BidsPanelFixedTop>
            <BidsPanelBodyScroll>
              {awaitingFinalDetailsReply ? (
                <BidsPanelLockedBlock>
                  <BidSectionTitle>
                    Almost ready for vendor search
                  </BidSectionTitle>
                  <BidsSearchPendingCard>
                    Your project blueprint is complete. Reply in the chat to
                    Clumse AI&apos;s last question (
                    <em>anything else about the project?</em>) to unlock{" "}
                    <strong>Pay $49 · Start search</strong>.
                  </BidsSearchPendingCard>
                </BidsPanelLockedBlock>
              ) : showVendorSearchCheckout ? (
                <BidsPanelLockedBlock>
                  <BidSectionTitle>
                    {blueprintOnlyMode
                      ? "Project Blueprint Pack"
                      : linkedTicketId
                        ? `Vendor quotes (${liveBidsForPanel.length})`
                        : "Start vendor search"}
                  </BidSectionTitle>
                  <BidsSearchCtaCard>
                    <BidsSearchInvestmentShield>
                      {blueprintOnlyMode ? (
                        <>
                          Your scope is locked. Hand this blueprint directly to
                          your crew so everyone prices the{" "}
                          <strong>same job at the same spec</strong> — no
                          mid-rehab change orders, no surprises.
                        </>
                      ) : (
                        <>
                          Your pre-scoped blueprints are complete. Launching the
                          bid search forces contractors to price against your
                          exact parameters - saving an average of{" "}
                          <strong>$600 in blind on-site markups</strong> and
                          diagnostic fees.
                        </>
                      )}
                    </BidsSearchInvestmentShield>
                    {!blueprintOnlyMode && (
                      <VendorSearchCheckoutExplainer userInfo={userInfo} />
                    )}
                    <PaymentSecurityGroup
                      onDispatch={(tierKey) => {
                        void startDispatchCheckout(tierKey);
                      }}
                      disabled={dispatchCheckoutLoading}
                      loading={dispatchCheckoutLoading}
                      dispatchFee={dispatchFeeForCheckout}
                      included={dispatchIncludedForCheckout}
                      autoDetectedTier={checkoutTierKey}
                      showTierSelector={false}
                      density="compact"
                      trustVariant="short"
                      blueprintMode={blueprintOnlyMode}
                    />
                  </BidsSearchCtaCard>
                </BidsPanelLockedBlock>
              ) : (
                <>
                  <BidSectionTitleRow>
                    <BidSectionTitle>
                      Vendor quotes ({liveBidsForPanel.length})
                    </BidSectionTitle>
                    {liveBidsForPanel.length > 1 && (
                      <BidSortDropdown>
                        <Select
                          value={bidSortBy}
                          onChange={setBidSortBy}
                          size="small"
                          suffixIcon={
                            <DownOutlined className="bid-sort-caret" />
                          }
                          style={{ width: 140 }}
                          options={[
                            { value: "best_overall", label: "Best Overall" },
                            { value: "lowest_amount", label: "Lowest Amount" },
                            { value: "best_reviews", label: "Best Reviews" },
                            { value: "fastest_start", label: "Fastest Start" },
                            { value: "newest_first", label: "Newest First" },
                          ]}
                        />
                      </BidSortDropdown>
                    )}
                  </BidSectionTitleRow>
                  {linkedTicketId && liveBidsForPanel.length > 0 ? (
                    <BidsPanelListScroll>
                      <BidListStack
                        key={`has-bids-${activeId}-${linkedTicketId}`}
                      >
                        {liveBidsForPanel.map((bid) => {
                          const bidReviews = bidGoogleReviewsFromBid(bid);
                          const bidMiles = bidDistanceMilesFromBid(bid);
                          const bidChrono = bidChronoNumberById.get(bid.id);
                          return (
                            <BidMiniCard key={bid.id}>
                              <BidMiniTop>
                                <BidMiniTopLeft>
                                  <Tag color={bidStatusTagColor(bid.status)}>
                                    {formatBidStatusLabel(bid.status)}
                                  </Tag>
                                </BidMiniTopLeft>
                                <BidMiniTitleRow>
                                  {bidChrono != null ? (
                                    <BidMiniChrono>
                                      Bid #{bidChrono}
                                    </BidMiniChrono>
                                  ) : null}
                                  <BidMiniAmount>
                                    $
                                    {Number(
                                      bid.bidAmount || 0
                                    ).toLocaleString()}
                                  </BidMiniAmount>
                                </BidMiniTitleRow>
                              </BidMiniTop>
                              <BidMiniVendorName>
                                {bid.vendorName || "Vendor"}
                              </BidMiniVendorName>
                              <BidMiniMetaBlock>
                                <BidMiniRatingRow>
                                  {bidReviews.rating != null ? (
                                    <>
                                      <Rate
                                        allowHalf
                                        disabled
                                        value={bidReviews.rating}
                                        style={{
                                          fontSize: 13,
                                          color: "var(--color-action, #ff5722)",
                                        }}
                                      />
                                      <BidMiniRatingText>
                                        <strong>
                                          {bidReviews.rating.toFixed(1)}
                                        </strong>
                                        {bidReviews.count != null
                                          ? ` · ${bidReviews.count.toLocaleString()} reviews`
                                          : ""}
                                      </BidMiniRatingText>
                                    </>
                                  ) : (
                                    <BidMiniMetaMuted>
                                      Star rating when this vendor is linked on
                                      Maps
                                    </BidMiniMetaMuted>
                                  )}
                                </BidMiniRatingRow>
                                <BidMiniDistance>
                                  {formatBidDistanceLine(bidMiles)}
                                </BidMiniDistance>
                              </BidMiniMetaBlock>
                              <LinkJobBtn
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBidDetailModal(bid);
                                }}
                              >
                                View details
                              </LinkJobBtn>
                            </BidMiniCard>
                          );
                        })}
                      </BidListStack>
                    </BidsPanelListScroll>
                  ) : !linkedTicketId ? (
                    <React.Fragment key={`no-job-${activeId}`}>
                      <JobProgressCard>
                        <JobProgressHeader>
                          <JobProgressTitle>Job Information</JobProgressTitle>
                        </JobProgressHeader>
                        <JobProgressMeter>
                          <Progress
                            type="circle"
                            percent={jobMeterDisplayed}
                            size={124}
                            strokeLinecap="round"
                            strokeColor="var(--color-action, #ff5722)"
                            trailColor="#e8eaed"
                            strokeWidth={10}
                            aria-label={`Job information ${jobMeterTarget} percent complete`}
                            format={(pct) => (
                              <JobProgressCircleInner aria-hidden>
                                <JobProgressCircleValue>
                                  {pct}
                                </JobProgressCircleValue>
                                <JobProgressCircleSuffix>
                                  %
                                </JobProgressCircleSuffix>
                              </JobProgressCircleInner>
                            )}
                          />
                        </JobProgressMeter>
                        <JobProgressText>
                          {jobProgressMotivationCopy(
                            jobMeterTarget,
                            transcriptCheckoutReady,
                            transcriptDiagnosisReady
                          )}
                        </JobProgressText>

                        {((jobInfoCompleteness.completed &&
                          jobInfoCompleteness.completed.length > 0) ||
                          (jobInfoCompleteness.missing &&
                            jobInfoCompleteness.missing.length > 0 &&
                            jobInfoCompleteness.percentage < 85)) && (
                          <JobProgressDetailsRow>
                            {jobInfoCompleteness.completed &&
                              jobInfoCompleteness.completed.length > 0 && (
                                <JobProgressCompleted>
                                  <JobProgressCompletedTitle>
                                    ✓ Already provided:
                                  </JobProgressCompletedTitle>
                                  {(jobInfoCompleteness.completed || []).map(
                                    (item, i) => (
                                      <JobProgressCompletedItem key={i}>
                                        {item}
                                      </JobProgressCompletedItem>
                                    )
                                  )}
                                </JobProgressCompleted>
                              )}

                            {jobInfoCompleteness.missing &&
                              jobInfoCompleteness.missing.length > 0 &&
                              jobInfoCompleteness.percentage < 85 && (
                                <JobProgressMissing>
                                  <JobProgressMissingTitle>
                                    Still needed:
                                  </JobProgressMissingTitle>
                                  {(jobInfoCompleteness.missing || []).map(
                                    (item, i) => (
                                      <JobProgressMissingItem key={i}>
                                        {item}
                                      </JobProgressMissingItem>
                                    )
                                  )}
                                </JobProgressMissing>
                              )}
                          </JobProgressDetailsRow>
                        )}
                      </JobProgressCard>
                      <MutedHelp>
                        Once you've shared enough detail, Clumse can finalize
                        your project scope when you're ready, then invite
                        vendors; their bids will show up here.
                      </MutedHelp>
                    </React.Fragment>
                  ) : (
                    <React.Fragment
                      key={`no-bids-${activeId}-${linkedTicketId}`}
                    >
                      <MutedHelp>
                        No bids for this job yet. We're waiting on quotes from
                        invited pros. They'll appear here as vendors respond.
                      </MutedHelp>
                    </React.Fragment>
                  )}
                </>
              )}
            </BidsPanelBodyScroll>
          </>
        )}
      </BidsPanelInner>
    ),
    [
      activeId,
      bidsPanelTab,
      bidsLoading,
      bidsFetchError,
      bidStats,
      liveBidsForPanel,
      bidChronoNumberById,
      showVendorSearchCheckout,
      awaitingFinalDetailsReply,
      checkoutTierKey,
      dispatchFeeForCheckout,
      dispatchIncludedForCheckout,
      linkedTicketId,
      jobInfoCompleteness,
      jobMeterDisplayed,
      jobMeterTarget,
      setBidDetailModal,
      isMobile,
      bidSortBy,
      dispatchCheckoutLoading,
      startDispatchCheckout,
      effectiveScopePayload,
      handleScopeEdit,
    ]
  );
  const bidModalBench = Number(procurement?.lockedBenchmarkUsd || 0);
  const bidModalThis = Number(bidDetailModal?.bidAmount || 0);
  const bidModalGap = Math.max(0, bidModalBench - bidModalThis);
  const bidModalRate = savingsFeeRateFromUserInfo(userInfo);
  const bidModalTierFee = Math.round(bidModalGap * bidModalRate * 100) / 100;
  const bidModalVaultTotal =
    Math.round((bidModalThis + bidModalTierFee) * 100) / 100;
  // LEAN BID-GATHERER: Vendor info is always available after dispatch
  const vaultPiiReleased =
    procurement?.status === "DISPATCHED" ||
    procurement?.status === "BIDS_RECEIVED";
  const bidModalGoogleReviews = useMemo(
    () => bidGoogleReviewsFromBid(bidDetailModal || {}),
    [bidDetailModal]
  );
  const bidModalVendorContext = useMemo(
    () => vendorClumseContextFromBid(bidDetailModal || {}),
    [bidDetailModal]
  );
  const bidModalBenchSavingsUsd = useMemo(() => {
    const bench = Number(procurement?.lockedBenchmarkUsd || 0);
    const bid = Number(bidDetailModal?.bidAmount || 0);
    if (
      !Number.isFinite(bench) ||
      bench <= 0 ||
      !Number.isFinite(bid) ||
      bid <= 0
    ) {
      return null;
    }
    if (bid >= bench) return null;
    return Math.round((bench - bid) * 100) / 100;
  }, [procurement?.lockedBenchmarkUsd, bidDetailModal?.bidAmount]);

  const bidModalChronoNumber =
    bidDetailModal?.id != null
      ? bidChronoNumberById.get(bidDetailModal.id) ?? null
      : null;

  return (
    <PageRoot
      style={
        isMobile
          ? {
              "--chat-mobile-header-stack": `${mobileChatHeaderStackPx}px`,
            }
          : undefined
      }
    >
      <VarianceMarketModalChrome />
      <ChatShellModalChrome />
      <BidsDrawerHeaderChrome />
      {isMobile && mobileRailOpen ? (
        <MobileBackdrop
          type="button"
          aria-label="Close chat list"
          onClick={() => setMobileRailOpen(false)}
        />
      ) : null}

      <ThreadRail $open={!isMobile || mobileRailOpen}>
        <RailHeader>
          <RailBrand>Chats</RailBrand>
          <NewChatGeminiButton type="button" onClick={newChat}>
            <PlusOutlined /> New chat
          </NewChatGeminiButton>
        </RailHeader>
        <ThreadList>
          {sortedThreads.map((th) => {
            const visual = getThreadBidVisual(th, bids, dispatchedTicketIds);
            const { Icon: StatusIcon, color, label, spin, kind } = visual;
            const isPaidThread = kind === "dispatched";
            return (
              <ThreadRow
                key={th.id}
                role="button"
                tabIndex={0}
                $active={isThreadActive(th)}
                onClick={() => selectThread(th.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    selectThread(th.id);
                  }
                }}
              >
                <ThreadRowMain>
                  <Tooltip title={label} placement="right">
                    <ThreadStatusIcon $paid={isPaidThread} aria-hidden>
                      <StatusIcon
                        spin={spin}
                        style={{ fontSize: "1.05rem", color }}
                      />
                    </ThreadStatusIcon>
                  </Tooltip>
                  <ThreadRowTextCol>
                    <ThreadRowTitle>
                      <TypingTitle
                        finalText={titleAnimations.get(th.id)?.title}
                        isActive={titleAnimations.has(th.id)}
                      >
                        {th.title}
                      </TypingTitle>
                    </ThreadRowTitle>
                    <ThreadRowMeta>
                      {isPaidThread ? (
                        <ThreadPaidBadge>Bid search paid</ThreadPaidBadge>
                      ) : null}
                      {new Date(th.updatedAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}
                    </ThreadRowMeta>
                  </ThreadRowTextCol>
                </ThreadRowMain>
                <ThreadRename
                  type="button"
                  aria-label="Rename chat"
                  onClick={(e) => openRenameThread(e, th)}
                >
                  <EditOutlined />
                </ThreadRename>
                <ThreadDelete
                  type="button"
                  aria-label="Remove chat from history"
                  onClick={(e) => requestDeleteThread(e, th.id)}
                >
                  ×
                </ThreadDelete>
              </ThreadRow>
            );
          })}
        </ThreadList>
      </ThreadRail>

      <MainColumn>
        <MainTopBar ref={mainTopBarRef}>
          {isMobile ? (
            <IconBtn
              type="button"
              aria-expanded={mobileRailOpen}
              aria-label={mobileRailOpen ? "Close chat list" : "Open chats"}
              onClick={() => setMobileRailOpen((open) => !open)}
            >
              <MenuOutlined />
            </IconBtn>
          ) : (
            <div style={{ width: 40 }} aria-hidden />
          )}
          <MainTitleGroup>
            <MainTitle>
              <TypingTitle
                finalText={titleAnimations.get(activeId)?.title}
                isActive={titleAnimations.has(activeId)}
              >
                {activeThread?.title || "Chat"}
              </TypingTitle>
              {!isLive && <SandboxIndicator>SANDBOX</SandboxIndicator>}
            </MainTitle>
          </MainTitleGroup>
          <TopBarActions>
            {isMobile ? (
              <BidsTopBtn
                type="button"
                aria-label={
                  hasUnreadBidsPanel
                    ? "Open vendor quotes and scope — new updates"
                    : "Open vendor quotes and scope of work"
                }
                onClick={() => openBidsPanel("quotes")}
              >
                <DollarOutlined aria-hidden />
                {hasUnreadBidsPanel ? (
                  <span
                    className="pointer-events-none absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center overflow-visible"
                    aria-hidden
                  >
                    <span className="absolute inline-flex h-2.5 w-2.5 animate-marketing-pulse rounded-full bg-orange-500 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-orange-600" />
                  </span>
                ) : null}
              </BidsTopBtn>
            ) : null}
          </TopBarActions>
        </MainTopBar>

        {isMobile && !linkedTicketId && jobInfoCompleteness.percentage < 85 ? (
          <MobileJobProgressBanner>
            <MobileJobProgressContent>
              <MobileJobProgressMeter>
                <Progress
                  type="circle"
                  percent={jobMeterDisplayed}
                  size={44}
                  strokeLinecap="round"
                  strokeColor="var(--color-action, #ff5722)"
                  trailColor="#e8eaed"
                  strokeWidth={6}
                  format={(pct) => (
                    <MobileJobProgressValue>{pct}%</MobileJobProgressValue>
                  )}
                />
              </MobileJobProgressMeter>
              <MobileJobProgressText>
                <MobileJobProgressTitle>Job setup</MobileJobProgressTitle>
                <MobileJobProgressSubtext>
                  Keep chatting below. This ring fills as details add up.
                </MobileJobProgressSubtext>
              </MobileJobProgressText>
            </MobileJobProgressContent>
          </MobileJobProgressBanner>
        ) : null}

        <TranscriptScroll ref={chatScrollRef}>
          <TranscriptInner
            $roomAboveComposer={readyForVendorSearch || showDispatchPayRail}
          >
            {messages.map((msg) => (
              <MsgRow
                key={msg.id}
                $user={msg.role === "user"}
                data-message-role={msg.role}
              >
                {msg.role === "assistant" ? (
                  <GemAvatarWrap>
                    <ChatStampAvatar aria-hidden>
                      <ChatStampImg src="/logo.png" alt="" />
                    </ChatStampAvatar>
                  </GemAvatarWrap>
                ) : null}
                {msg.role === "assistant" ? (
                  <AssistantMsgStack>
                    <MsgAssistantName>
                      {CLUMSE_AI_DISPLAY_NAME}
                    </MsgAssistantName>
                    <GemBubble $user={false} $error={msg.isError}>
                      {msg.content ? (
                        <MsgText>
                          {formatChatMessageSegments(
                            formatClumseAiBubbleText(msg.content),
                            {
                              linkifyBidsPanel: isMobile,
                              onOpenBidsPanel: openBidsPanel,
                            }
                          )}
                        </MsgText>
                      ) : null}
                      {msg.content && msg.content.length > 800 ? (
                        <ScrollIndicator
                          messageId={msg.id}
                          messages={messages}
                        />
                      ) : null}
                    </GemBubble>
                    {formatMsgLocalTime(msg.timestamp) ? (
                      <MsgTimestamp $user={false}>
                        {formatMsgLocalTime(msg.timestamp)}
                      </MsgTimestamp>
                    ) : null}
                  </AssistantMsgStack>
                ) : (
                  <UserMsgStack>
                    <GemBubble $user={true} $error={msg.isError}>
                      {msg.role === "user" && msg.images?.length ? (
                        <ChatMessageImageThumbs
                          images={msg.images}
                          altPrefix="Uploaded image"
                          marginBottom
                        />
                      ) : null}
                      {msg.content ? (
                        <MsgText>{formatChatBoldSegments(msg.content)}</MsgText>
                      ) : null}
                    </GemBubble>
                    {formatMsgLocalTime(msg.timestamp) ? (
                      <MsgTimestamp $user={true}>
                        {formatMsgLocalTime(msg.timestamp)}
                      </MsgTimestamp>
                    ) : null}
                  </UserMsgStack>
                )}
              </MsgRow>
            ))}
            {isTyping ? (
              <MsgRow $user={false}>
                <GemAvatarWrap>
                  <ChatStampAvatar aria-hidden>
                    <ChatStampImg src="/logo.png" alt="" />
                  </ChatStampAvatar>
                </GemAvatarWrap>
                <AssistantMsgStack>
                  <MsgAssistantName>{CLUMSE_AI_DISPLAY_NAME}</MsgAssistantName>
                  <TypingGem>
                    <span />
                    <span />
                    <span />
                  </TypingGem>
                </AssistantMsgStack>
              </MsgRow>
            ) : null}
            <div ref={messagesEndRef} />
          </TranscriptInner>
        </TranscriptScroll>

        {showDispatchPayRail ? (
          <DispatchRail>
            {dispatchUnlocked ? (
              <DispatchPaidNote>
                <SafetyCertificateOutlined
                  style={{ marginRight: "6px", color: "#059669" }}
                />
                {isMobile ? (
                  <>
                    Dispatched. We are reaching out to vendors.{" "}
                    <BidsPanelInlineButton
                      type="button"
                      onClick={() => openBidsPanel("quotes")}
                    >
                      View vendor quotes
                    </BidsPanelInlineButton>
                  </>
                ) : (
                  "Dispatched. We are reaching out to vendors for this job."
                )}
              </DispatchPaidNote>
            ) : linkedTicketId &&
              procurement &&
              !procurement.procurementPlanReady ? (
              <DispatchPrepNote>
                {blueprintOnlyMode
                  ? "Finish the scope details in chat so your Blueprint Pack is accurate enough to hand to your crew."
                  : procurement.dispatchOrchestrationIncluded
                    ? "Finish the job details in chat, then start vendor outreach (included on your plan)."
                    : "Finish the job details in chat so vendors get category, scope, and service area (ZIP or unit)."}
              </DispatchPrepNote>
            ) : dispatchCheckoutLoading ? (
              <DispatchLoadingContainer>
                <SharkStatusLog
                  isActive={true}
                  variant="dispatch"
                  size="compact"
                />
              </DispatchLoadingContainer>
            ) : (
              <PaymentSecurityGroup
                onDispatch={(tierKey) => void startDispatchCheckout(tierKey)}
                disabled={dispatchCheckoutLoading}
                loading={dispatchCheckoutLoading}
                dispatchFee={dispatchFeeForCheckout}
                included={dispatchIncludedForCheckout}
                autoDetectedTier={checkoutTierKey}
                showTierSelector={false}
                density="comfortable"
                trustVariant="short"
                blueprintMode={blueprintOnlyMode}
              />
            )}
          </DispatchRail>
        ) : null}

        {remodelPreviewUi && !remodelPreviewUi.dismissed ? (
          <RemodelPreviewRail>
            {remodelPreviewUi.status === "pending" ? (
              <RemodelPreviewPending>
                <LoadingOutlined style={{ marginRight: 8 }} />
                Building your photorealistic inspiration render in the
                background...
              </RemodelPreviewPending>
            ) : null}
            {remodelPreviewUi.status === "completed" &&
            !remodelPreviewUi.imageUrls?.length ? (
              <RemodelPreviewPending>
                <LoadingOutlined style={{ marginRight: 8 }} />
                Finalizing your inspiration render…
              </RemodelPreviewPending>
            ) : null}
            {remodelPreviewUi.status === "completed" &&
            remodelPreviewUi.imageUrls?.length ? (
              <Alert
                type="success"
                showIcon
                message="Your inspiration render is ready"
                description={
                  remodelPreviewUi.revealed ? (
                    <RemodelPreviewImages>
                      {remodelPreviewUi.imageUrls.map((url) => (
                        <RemodelPreviewImg
                          key={url}
                          src={url}
                          alt="Remodel preview"
                        />
                      ))}
                    </RemodelPreviewImages>
                  ) : (
                    <div>
                      <p style={{ margin: "0 0 10px" }}>
                        We generated an optional style preview from your thread.
                        Open it when you like. Nothing extra runs until you
                        choose.
                      </p>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          setRemodelPreviewUi((prev) =>
                            prev ? { ...prev, revealed: true } : prev
                          )
                        }
                      >
                        Show preview
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          setRemodelPreviewUi((prev) =>
                            prev ? { ...prev, dismissed: true } : prev
                          )
                        }
                        style={{ marginLeft: 4 }}
                      >
                        Not now
                      </Button>
                    </div>
                  )
                }
              />
            ) : null}
            {remodelPreviewUi.status === "failed" ? (
              <Alert
                type="warning"
                showIcon
                closable
                message="Preview unavailable"
                description="We could not finish the optional remodel preview this time. Your chat and scope are unchanged."
                onClose={() =>
                  setRemodelPreviewUi((prev) =>
                    prev ? { ...prev, dismissed: true } : prev
                  )
                }
              />
            ) : null}
          </RemodelPreviewRail>
        ) : null}

        {dispatchUnlocked && linkedTicketId && !isDemo ? (
          <DispatchConfirmedZone>
            <DispatchConfirmedCard>
              <DispatchConfirmedIcon aria-hidden="true">
                ✓
              </DispatchConfirmedIcon>
              <DispatchConfirmedBody>
                {(() => {
                  const bidsReceived =
                    procurement &&
                    ["BIDS_RECEIVED", "CLOSED"].includes(
                      String(
                        procurement.clumseDispatchStatus || ""
                      ).toUpperCase()
                    );
                  if (!isMobile) {
                    return (
                      <DispatchConfirmedText>
                        {bidsReceived ? (
                          <>
                            <strong>Bids are coming in.</strong> Contractors
                            have started responding. See Vendor Quotes on the
                            right.
                          </>
                        ) : (
                          <>
                            <strong>Bid search is live.</strong> Payment
                            confirmed. We're reaching out to vetted contractors.
                            Initial bids expected in 1-3 business days.
                          </>
                        )}
                      </DispatchConfirmedText>
                    );
                  }
                  if (bidsReceived) {
                    return (
                      <>
                        <DispatchConfirmedMobileLine>
                          Bids are coming in. Contractors have started
                          responding.
                        </DispatchConfirmedMobileLine>
                        <DispatchConfirmedMobileLine>
                          <DispatchConfirmedLink
                            type="button"
                            onClick={() => openBidsPanel("quotes")}
                          >
                            Review quotes
                          </DispatchConfirmedLink>
                        </DispatchConfirmedMobileLine>
                      </>
                    );
                  }
                  return (
                    <>
                      <DispatchConfirmedMobileLine>
                        Payment confirmed. Reaching out to contractors.
                      </DispatchConfirmedMobileLine>
                      <DispatchConfirmedMobileLine>
                        Bids usually arrive in 1-3 business days.{" "}
                        <DispatchConfirmedLink
                          type="button"
                          onClick={() => openBidsPanel("quotes")}
                        >
                          Track quotes
                        </DispatchConfirmedLink>
                      </DispatchConfirmedMobileLine>
                    </>
                  );
                })()}
              </DispatchConfirmedBody>
            </DispatchConfirmedCard>
          </DispatchConfirmedZone>
        ) : readyForVendorSearch ? (
          <ChatVendorSearchReadyZone>
            <ChatVendorSearchReadyBanner>
              <ChatVendorSearchReadyText>
                {isMobile ? (
                  <>
                    Your scope is complete. Tap{" "}
                    <BidsPanelInlineButton
                      type="button"
                      onClick={() => openBidsPanel("quotes")}
                    >
                      Vendor Quotes
                    </BidsPanelInlineButton>{" "}
                    then <strong>Pay $49 · Start search</strong> to complete your
                    secure one-time payment and unlock matched vendor quotes.
                    You will get a confirmation email and we will reach out to
                    vetted pros in your area.
                  </>
                ) : (
                  <>
                    Your scope is complete. Use{" "}
                    <strong>Pay $49 · Start search</strong> in the{" "}
                    <strong>Vendor Quotes</strong> panel on the right to
                    complete your secure one-time payment and unlock matched
                    vendor quotes. You will get a confirmation email with your
                    project summary; we will reach out to vetted pros next.
                  </>
                )}
              </ChatVendorSearchReadyText>
            </ChatVendorSearchReadyBanner>
          </ChatVendorSearchReadyZone>
        ) : null}

        <ComposerDock>
          {attachedImageCount ? (
            <PendingAttachBar>
              {uploadingImages.map((item) => (
                <PendingThumbWrap key={item.id} $isUploading>
                  <PendingUploadPreview src={item.previewUrl} alt="" />
                  <PendingUploadOverlay aria-hidden="true">
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 18 }} spin />
                      }
                    />
                  </PendingUploadOverlay>
                </PendingThumbWrap>
              ))}
              <Image.PreviewGroup>
                {pendingImages.map((src, idx) => (
                  <PendingThumbWrap key={src}>
                    <Image
                      src={src}
                      alt=""
                      styles={PENDING_CHAT_IMAGE_STYLES}
                      preview={{ mask: "View" }}
                    />
                    <PendingRemove
                      type="button"
                      aria-label="Remove image"
                      onClick={() => removePendingImage(idx)}
                    >
                      <CloseOutlined />
                    </PendingRemove>
                  </PendingThumbWrap>
                ))}
              </Image.PreviewGroup>
              {isUploadingImages ? (
                <PendingUploadStatus aria-live="polite">
                  Uploading{" "}
                  {uploadingImages.length === 1
                    ? "image"
                    : `${uploadingImages.length} images`}
                  …
                </PendingUploadStatus>
              ) : null}
            </PendingAttachBar>
          ) : null}
          <ComposerRow>
            <ComposerShell>
              <ComposerAttachBtn
                type="button"
                aria-label={
                  isUploadingImages ? "Uploading images" : "Add images"
                }
                aria-busy={isUploadingImages}
                disabled={
                  composerLocked ||
                  isUploadingImages ||
                  attachedImageCount >= MAX_CHAT_IMAGES
                }
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploadingImages ? (
                  <LoadingOutlined spin />
                ) : (
                  <PictureOutlined />
                )}
              </ComposerAttachBtn>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageFilesSelected}
              />
              <ComposerInput
                ref={inputRef}
                value={inputValue}
                onChange={(e) => {
                  if (composerLocked) return;
                  setInputValue(e.target.value);
                }}
                onPressEnter={handleKeyPress}
                placeholder={composerPlaceholder}
                disabled={composerLocked || isUploadingImages}
                readOnly={composerLocked}
                maxLength={8000}
                autoSize={
                  isMobile
                    ? { minRows: 1, maxRows: inputValue.includes("\n") ? 4 : 1 }
                    : { minRows: 1, maxRows: 6 }
                }
                variant="borderless"
              />
            </ComposerShell>
            <SendGemWrap>
              <SendGem
                type="button"
                aria-label="Send"
                aria-busy={composerLocked || isUploadingImages}
                disabled={
                  (!inputValue.trim() && !pendingImages.length) ||
                  composerLocked ||
                  isUploadingImages
                }
                onClick={handleSendMessage}
              >
                <SendOutlined />
              </SendGem>
            </SendGemWrap>
          </ComposerRow>
          <ComposerHintsWrap>
            {isMobile ? (
              <ComposerHint>
                Clumse AI can make mistakes. Always verify contractor bids and
                scope before you commit. Read the{" "}
                <DisclaimerLink
                  href={APP_TERMS_ROUTE}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </DisclaimerLink>{" "}
                for limitations, disclaimers, and how Clumse operates.
              </ComposerHint>
            ) : (
              <>
                <ComposerHint>
                  Clumse AI can make mistakes. Always verify contractor bids and
                  scope before you commit.
                </ComposerHint>
                <ComposerHint>
                  Read the{" "}
                  <DisclaimerLink
                    href={APP_TERMS_ROUTE}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </DisclaimerLink>{" "}
                  for limitations, disclaimers, and how Clumse operates.
                </ComposerHint>
              </>
            )}
          </ComposerHintsWrap>
        </ComposerDock>
      </MainColumn>

      {!isMobile ? (
        <BidsPane ref={bidsPaneRef}>
          <BidsPaneHeader>
            <BidsPaneHeaderInner>
              <BidsPaneTitle>Scope &amp; Bids</BidsPaneTitle>
              {/*
              {linkedJobLabel ? (
                <BidsPaneSubtitle title={linkedJobLabel}>
                  {linkedJobLabel}
                </BidsPaneSubtitle>
              ) : !isDemo ? (
                <BidsPaneSubtitle $muted>
                  Updates when Clumse opens a job from this chat
                </BidsPaneSubtitle>
              ) : null}
              */}
              {/* Demo tag removed for clean screenshots */}
            </BidsPaneHeaderInner>
          </BidsPaneHeader>
          {bidsPanelBody}
        </BidsPane>
      ) : null}

      <Modal
        title={
          <ChatModalTitleRow>
            <RenameChatModalIcon aria-hidden>
              <EditOutlined />
            </RenameChatModalIcon>
            <span>Rename chat</span>
          </ChatModalTitleRow>
        }
        open={renameThreadId != null}
        onCancel={closeRenameModal}
        footer={
          <ChatModalFooterBar>
            <ChatModalGhostBtn type="button" onClick={closeRenameModal}>
              Cancel
            </ChatModalGhostBtn>
            <RenameChatSaveBtn type="button" onClick={applyRenameThread}>
              Save
            </RenameChatSaveBtn>
          </ChatModalFooterBar>
        }
        centered
        destroyOnClose
        maskClosable={false}
        width={440}
        styles={{
          ...CHAT_MODAL_SHELL_STYLES,
          body: { padding: "18px 22px 8px" },
        }}
      >
        <RenameChatInput
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          onPressEnter={applyRenameThread}
          placeholder="Chat name"
          maxLength={120}
          autoFocus
        />
      </Modal>

      <Modal
        title={
          <ChatModalTitleRow>
            <DeleteChatModalIcon aria-hidden>
              <DeleteOutlined />
            </DeleteChatModalIcon>
            <span>Delete this chat?</span>
          </ChatModalTitleRow>
        }
        open={deleteThreadId != null}
        onCancel={closeDeleteModal}
        footer={
          <ChatModalFooterBar>
            <ChatModalGhostBtn type="button" onClick={closeDeleteModal}>
              Cancel
            </ChatModalGhostBtn>
            <DeleteChatConfirmBtn type="button" onClick={applyDeleteThread}>
              Remove chat
            </DeleteChatConfirmBtn>
          </ChatModalFooterBar>
        }
        centered
        destroyOnClose
        maskClosable={false}
        width={440}
        rootClassName="clumse-chat-delete-modal"
        styles={CHAT_MODAL_SHELL_STYLES}
      >
        <DeleteChatModalBody>
          <DeleteChatModalP>
            This will delete the conversation from your chat history. This
            action cannot be undone.
          </DeleteChatModalP>
        </DeleteChatModalBody>
      </Modal>

      <Modal
        title={
          <ChatModalTitleRow>
            <PhoneOutlined aria-hidden style={{ marginRight: 8 }} />
            <span>Unblock vendor text thread</span>
          </ChatModalTitleRow>
        }
        open={
          !isDemo &&
          negotiationUnblockOpen &&
          Boolean(negotiationUnblockThreadId)
        }
        onCancel={closeNegotiationUnblockModal}
        footer={
          <ChatModalFooterBar>
            <ChatModalGhostBtn
              type="button"
              onClick={closeNegotiationUnblockModal}
              disabled={negotiationUnblockSubmitting}
            >
              Cancel
            </ChatModalGhostBtn>
            <RenameChatSaveBtn
              type="button"
              disabled={
                negotiationUnblockSubmitting ||
                !negotiationOwnerAnswerDraft.trim() ||
                !user?.uid
              }
              onClick={() => void submitVendorNegotiationOwnerAnswer()}
            >
              Send to vendor
            </RenameChatSaveBtn>
          </ChatModalFooterBar>
        }
        centered
        destroyOnClose
        maskClosable={!negotiationUnblockSubmitting}
        width={480}
        styles={{
          ...CHAT_MODAL_SHELL_STYLES,
          body: { padding: "18px 22px 8px" },
        }}
      >
        {!user?.uid ? (
          <Alert
            type="info"
            showIcon
            message="Sign in required"
            description="Sign in with the property owner account, then submit the information the vendor needs."
            style={{ marginBottom: 12 }}
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Vendor is waiting on Clumse SMS"
            description="Paste the facts the vendor asked for (scope, access, materials, timeline, etc.). Clumse will relay them to the vendor by SMS."
            style={{ marginBottom: 12 }}
          />
        )}
        <Input.TextArea
          value={negotiationOwnerAnswerDraft}
          onChange={(e) => setNegotiationOwnerAnswerDraft(e.target.value)}
          placeholder="e.g. Owner approves GAF Timberline HDZ in Weathered Wood; garage access code is …"
          autoSize={{ minRows: 4, maxRows: 10 }}
          maxLength={8000}
          disabled={negotiationUnblockSubmitting}
        />
      </Modal>

      <Modal
        title={
          <VarianceModalTitleBlock>
            <BidModalHeaderGrid>
              <BidModalMainTitle>
                {bidModalChronoNumber != null
                  ? `Bid #${bidModalChronoNumber}`
                  : "Bid Details"}
              </BidModalMainTitle>
              <BidModalAmount>
                ${Number(bidDetailModal?.bidAmount || 0).toLocaleString()}
              </BidModalAmount>
              <BidModalVendorName>
                {bidDetailModal?.vendorName || "Vendor"}
              </BidModalVendorName>
            </BidModalHeaderGrid>
          </VarianceModalTitleBlock>
        }
        rootClassName="clumse-variance-market-modal"
        closeIcon={<CloseOutlined style={{ color: "#ffffff", fontSize: 15 }} />}
        open={bidDetailModal != null}
        onCancel={() => setBidDetailModal(null)}
        footer={
          <VarianceModalFooterBar>
            <BidModalFooterInner>
              {(!bidDetailModal?.status ||
                bidDetailModal?.status === "quote") &&
              !bidsSidebarLocked ? (
                <BidModalSelectBtn
                  type="button"
                  disabled={vaultLoading}
                  onClick={() => void connectWithVendor()}
                >
                  <MailOutlined aria-hidden />
                  Connect with vendor
                </BidModalSelectBtn>
              ) : null}
              {bidDetailModal?.status === "contacted" ||
              bidVendorHasDirectContact(bidDetailModal) ? (
                <ContactVendor
                  vendorName={bidDetailModal?.vendorName || "Vendor"}
                  vendorPhone={
                    bidDetailModal?.vendorPhone ||
                    bidDetailModal?.contractorPhone
                  }
                  vendorEmail={
                    bidDetailModal?.vendorEmail ||
                    bidDetailModal?.contractorEmail
                  }
                  onMarkCompleted={() => {
                    // TODO: Implement project completion logic
                    message.success("Project marked as complete!");
                  }}
                  isCompleted={bidDetailModal?.status === "contacted"}
                  compactLabels
                  inlineFooter
                />
              ) : null}
              <BidModalCloseBtn
                type="button"
                onClick={() => setBidDetailModal(null)}
              >
                Close
              </BidModalCloseBtn>
            </BidModalFooterInner>
          </VarianceModalFooterBar>
        }
        centered
        destroyOnClose
        maskClosable={!vaultLoading}
        width={isMobile ? "min(520px, calc(100vw - 20px))" : 700}
        styles={VARIANCE_MODAL_STYLES}
      >
        {bidDetailModal ? (
          <VarianceModalBody>
            <VarianceTotalsPanel>
              <VarianceGapLine $flush>
                <strong>
                  Quote Amount: $
                  {(
                    bidDetailModal?.acceptedAmount ||
                    bidDetailModal?.estimatedCost ||
                    bidDetailModal?.bidAmount ||
                    0
                  ).toLocaleString()}
                </strong>
              </VarianceGapLine>
              {bidDetailModal?.timeline && (
                <VarianceGapLine>
                  <strong>Timeline:</strong> {bidDetailModal.timeline}
                </VarianceGapLine>
              )}
            </VarianceTotalsPanel>
            <VarianceSectionRule />
            <VarianceSectionTitle>
              <CommentOutlined aria-hidden />
              Vendor Details
            </VarianceSectionTitle>
            <VarianceClumseIntro>
              Key details from their conversation with Clumse AI
            </VarianceClumseIntro>
            {bidModalVendorContext.hasAny ? (
              <VarianceClumseBody>
                {bidModalVendorContext.bullets.map((b) => (
                  <VarianceClumseBullet key={b.key}>
                    <VarianceClumseBulletLabel>
                      {b.label}
                    </VarianceClumseBulletLabel>
                    <VarianceClumseBulletText>
                      {formatChatBoldSegments(b.text)}
                    </VarianceClumseBulletText>
                  </VarianceClumseBullet>
                ))}
                {bidModalVendorContext.paragraphs.map((para, idx) => (
                  <VarianceClumsePara key={idx}>
                    <MsgText as="div">{formatChatBoldSegments(para)}</MsgText>
                  </VarianceClumsePara>
                ))}
              </VarianceClumseBody>
            ) : (
              <VarianceMuted>
                <VarianceInlineIconRow>
                  <FileTextOutlined aria-hidden />
                  <span>
                    Vendor conversation details will appear here once they
                    respond
                  </span>
                </VarianceInlineIconRow>
              </VarianceMuted>
            )}
            <VarianceSectionRule />
            <VarianceSectionTitle>
              <ShopOutlined aria-hidden />
              Vendor Info
            </VarianceSectionTitle>

            <div style={{ marginBottom: "16px" }}>
              <VarianceLabel>
                <StarOutlined aria-hidden />
                Google Maps rating
              </VarianceLabel>
              {bidModalGoogleReviews.rating != null ? (
                <VarianceRatingBlock>
                  <Rate
                    allowHalf
                    disabled
                    value={bidModalGoogleReviews.rating}
                    style={{
                      fontSize: 17,
                      color: "var(--color-action, #ff5722)",
                    }}
                  />
                  <VarianceRatingMeta>
                    <strong>{bidModalGoogleReviews.rating.toFixed(1)}</strong>
                    {bidModalGoogleReviews.count != null
                      ? ` · ${bidModalGoogleReviews.count.toLocaleString()} reviews`
                      : null}
                  </VarianceRatingMeta>
                </VarianceRatingBlock>
              ) : (
                <VarianceMuted>
                  <VarianceInlineIconRow>
                    <InfoCircleOutlined aria-hidden />
                    <span>Rating will appear when linked to Google Maps</span>
                  </VarianceInlineIconRow>
                </VarianceMuted>
              )}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <VarianceLabel>
                <IdcardOutlined aria-hidden />
                Business name
              </VarianceLabel>
              <VarianceMuted $strong={true}>
                {String(bidDetailModal.vendorName || "").trim() || "Vendor"}
              </VarianceMuted>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <VarianceLabel>
                <PhoneOutlined aria-hidden />
                Contact info
              </VarianceLabel>
              {bidVendorHasDirectContact(bidDetailModal) ? (
                <VarianceMuted>
                  {bidVendorContactDisplayLine(bidDetailModal)}
                </VarianceMuted>
              ) : String(bidDetailModal.vendorContactInfo || "").trim() ? (
                <VarianceMuted>
                  {String(bidDetailModal.vendorContactInfo || "").trim()}
                </VarianceMuted>
              ) : (
                <BidModalContactMissingNotice
                  type="warning"
                  showIcon
                  message="Contact info not on this quote yet"
                  description="There’s no phone or email on file for this vendor. You can still use Connect with vendor so both sides get a summary, or continue in chat until details are added."
                />
              )}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <VarianceLabel>
                <HomeOutlined aria-hidden />
                Property address
              </VarianceLabel>
              <VarianceMuted>
                {bidDetailModal.propertyExactAddress ||
                  bidDetailModal.unitAddress ||
                  "Not specified"}
              </VarianceMuted>
            </div>
            {bidsSidebarLocked ? (
              <VarianceHint>
                Dispatch this job first to receive contractor bids and contact
                info.
              </VarianceHint>
            ) : null}
          </VarianceModalBody>
        ) : null}
      </Modal>

      <Drawer
        rootClassName="clumse-bids-drawer"
        classNames={{ header: "clumse-bids-drawer-header" }}
        closable={{ placement: "end" }}
        title={
          <BidsDrawerTitleWrap>
            <BidsPaneTitle as="div" $bidsDrawer>
              Scope &amp; Bids
            </BidsPaneTitle>
            {linkedJobLabel ? (
              <BidsDrawerSubtitleStack title={linkedJobLabel}>
                <BidsDrawerSubtitleLine>
                  {linkedJobLabelLines.primary}
                </BidsDrawerSubtitleLine>
                {linkedJobLabelLines.secondary ? (
                  <BidsDrawerSubtitleLine $address>
                    {linkedJobLabelLines.secondary}
                  </BidsDrawerSubtitleLine>
                ) : null}
              </BidsDrawerSubtitleStack>
            ) : !isDemo ? (
              <BidsDrawerSubtitleStack>
                <BidsDrawerSubtitleLine $muted>
                  Updates when Clumse opens a job from this chat
                </BidsDrawerSubtitleLine>
              </BidsDrawerSubtitleStack>
            ) : null}
            {/* Demo tag removed for clean screenshots */}
          </BidsDrawerTitleWrap>
        }
        placement="right"
        onClose={() => setShowBidsDrawer(false)}
        open={showBidsDrawer && isMobile}
        width="100%"
        destroyOnClose={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          header: {
            borderBottom: "1px solid #e8eaed",
            textAlign: "center",
            flexShrink: 0,
            padding: "10px 12px 12px",
          },
          body: {
            padding: "12px 6px 12px 14px",
            background: "#f8fafc",
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <BidsDrawerBodyShell>{bidsPanelBody}</BidsDrawerBodyShell>
      </Drawer>

      {showMarketingControls ? (
        <MarketingSandboxControls
          phase={marketingPhase}
          onPhaseChange={(nextPhase) => {
            setMarketingPhase(nextPhase);
            const next = new URLSearchParams(searchParams);
            next.set("phase", nextPhase);
            setSearchParams(next, { replace: true });
          }}
          forceUnreadBadge={marketingForceUnreadBadge}
          onToggleForceUnreadBadge={() =>
            setMarketingForceUnreadBadge((on) => !on)
          }
          onMobileGrabPreset={() => {
            setMarketingPhase("live_bids");
            setMarketingForceUnreadBadge(true);
            setShowBidsDrawer(false);
            const next = new URLSearchParams(searchParams);
            next.set("phase", "live_bids");
            setSearchParams(next, { replace: true });
          }}
          onOpenMobileBids={() => openBidsPanel("quotes")}
          onOpenDesktopBids={() => openBidsPanel("quotes")}
          isMobile={isMobile}
        />
      ) : null}
    </PageRoot>
  );
};

/* Gemini-like shell: fixed below global nav */
const CHAT_PAGE_GRID = `
 linear-gradient(to right, rgba(15, 23, 42, 0.05) 1px, transparent 1px),
 linear-gradient(to bottom, rgba(15, 23, 42, 0.05) 1px, transparent 1px)
`;

const PageRoot = styled.div`
  position: fixed;
  top: var(--navbar-height, 120px);
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  background-color: #f0f4f9;
  background-image: ${CHAT_PAGE_GRID};
  background-size: 24px 24px;
  z-index: 5;
  font-family: var(
    --font-stack-sans,
    "Inter",
    system-ui,
    -apple-system,
    sans-serif
  );

  @media ${device.mobile} {
    top: var(--navbar-height-mobile, 100px);
  }
`;

const MobileBackdrop = styled.button`
  position: fixed;
  top: calc(
    var(--navbar-height-mobile, 100px) +
      var(--chat-mobile-header-stack, ${MOBILE_CHAT_HEADER_STACK_PX}px)
  );
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 25;
  border: none;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.45);
  cursor: pointer;
`;

const ThreadRail = styled.aside`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-tertiary, #f8fafc);
  border-right: 1px solid var(--color-border-light, #e2e8f0);
  z-index: 20;
  min-height: 0;

  @media ${device.mobile} {
    position: fixed;
    top: calc(
      var(--navbar-height-mobile, 100px) +
        var(--chat-mobile-header-stack, ${MOBILE_CHAT_HEADER_STACK_PX}px)
    );
    bottom: 0;
    left: 0;
    width: min(300px, 86vw);
    max-width: 320px;
    z-index: 30;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
    transform: translateX(${(p) => (p.$open ? "0" : "-105%")});
    transition: transform 0.22s ease;
  }
`;

const RailHeader = styled.div`
  padding: 12px 12px 10px;
  border-bottom: 1px solid var(--color-border-light, #e2e8f0);
  flex-shrink: 0;
`;

const RailBrand = styled.div`
  font-family: var(--font-stack-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 10px;
`;

const NewChatGeminiButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.65rem 0.75rem;
  border-radius: 999px;
  border: 2px solid #1e293b;
  background: #ffffff;
  color: var(--color-action, #ff5722);
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(30, 41, 59, 0.08);
  transition: background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 87, 34, 0.08);
    box-shadow: 0 1px 4px rgba(30, 41, 59, 0.1);
    color: #e84615;
  }
`;

const ThreadList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 8px 8px;
  min-height: 0;
`;

/** `div` + role="button" so rename/delete can be real `<button>`s (no nested buttons). */
const ThreadRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  position: relative;
  padding: 10px 58px 10px 10px;
  margin-bottom: 4px;
  border: none;
  border-radius: 12px;
  background: ${(p) => (p.$active ? "#bfdbfe" : "transparent")};
  color: #202124;
  cursor: pointer;
  transition: background 0.12s ease;
  gap: 0;
  font: inherit;

  &:hover {
    background: ${(p) => (p.$active ? "#93c5fd" : "#eef0f4")};
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }
`;

const ThreadRowMain = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ThreadStatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 1px;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${(p) =>
    p.$paid ? "rgba(46, 125, 50, 0.14)" : "rgba(255, 255, 255, 0.65)"};
  border: ${(p) =>
    p.$paid ? "1px solid rgba(46, 125, 50, 0.35)" : "1px solid transparent"};
  box-shadow: ${(p) => (p.$paid ? "0 1px 4px rgba(46, 125, 50, 0.2)" : "none")};
`;

const ThreadRowTextCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const ThreadRowTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.35;
  color: #202124;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ThreadRowMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.6875rem;
  color: #5f6368;
  margin-top: 2px;
`;

const ThreadPaidBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #1b5e20;
  background: #e8f5e9;
  border: 1px solid rgba(46, 125, 50, 0.35);
  line-height: 1.4;
`;

const threadRowIconBtnStyles = `
 position: absolute;
 top: 50%;
 transform: translateY(-50%);
 width: 26px;
 height: 26px;
 border: none;
 border-radius: 8px;
 background: transparent;
 color: #5f6368;
 line-height: 1;
 cursor: pointer;
 opacity: 0.65;
 display: flex;
 align-items: center;
 justify-content: center;

 &:hover {
 background: rgba(0, 0, 0, 0.06);
 opacity: 1;
 }
`;

const ThreadRename = styled.button`
  ${threadRowIconBtnStyles}
  right: 30px;
  font-size: 0.85rem;
`;

const ThreadDelete = styled.button`
  ${threadRowIconBtnStyles}
  right: 4px;
  font-size: 1.1rem;
`;

const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #f0f4f9;
`;

const BidsPane = styled.aside`
  width: min(420px, 32vw);
  min-width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-left: 1px solid #e8eaed;
  min-height: 0;
  z-index: 4;

  @media (max-width: 1200px) {
    width: min(360px, 30vw);
    min-width: 300px;
  }

  @media ${device.mobile} {
    display: none;
  }
`;

const BidsPaneHeader = styled.div`
  flex-shrink: 0;
  padding: 16px 16px 14px;
  border-bottom: 1px solid #e8eaed;
  background: #f8fafc;
`;

const BidsPaneHeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  max-width: 100%;
`;

const BidsPaneTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
  color: #202124;
  letter-spacing: -0.02em;
  line-height: 1.2;

  ${(p) =>
    p.$bidsDrawer
      ? `
 width: 100%;
 text-align: center;
 `
      : ""}
`;

const BidsPaneSubtitle = styled.div`
  font-size: 0.75rem;
  color: ${(p) => (p.$muted ? "#80868b" : "#5f6368")};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 12px;
`;

const BidsDrawerTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  width: 100%;
  max-width: 100%;
  padding: 2px 0 0;
  box-sizing: border-box;
`;

const BidsDrawerSubtitleStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  margin-top: 6px;
  padding: 0 4px;
  box-sizing: border-box;
`;

const BidsDrawerSubtitleLine = styled.div`
  width: 100%;
  max-width: 100%;
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${(p) => {
    if (p.$muted) return "#80868b";
    if (p.$address) return "#6b7280";
    return "#5f6368";
  }};
  font-weight: ${(p) => (p.$address ? 500 : 400)};
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  white-space: normal;

  ${(p) =>
    p.$address
      ? `
 font-size: 0.6875rem;
 letter-spacing: 0.01em;
 `
      : ""}
`;

const BidsPanelInner = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  padding: 10px 8px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media ${device.mobile} {
    padding: 10px 4px 16px 10px;
  }
`;

/** Breathing room between scope/bid cards and the scroll track (desktop + mobile). */
const bidsPanelScrollChrome = css`
  padding-right: 12px;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
`;

const BidsPanelTabRow = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px;
  border-radius: 10px;
  background: #e8eaed;
`;

const BidsPanelTab = styled.button`
  flex: 1;
  border: none;
  background: ${(p) => (p.$active ? "#fff" : "transparent")};
  color: ${(p) => (p.$active ? "#1e293b" : "#5f6368")};
  font-size: 0.75rem;
  font-weight: ${(p) => (p.$active ? 700 : 600)};
  letter-spacing: 0.02em;
  text-transform: none;
  padding: 9px 10px;
  border-radius: 7px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${(p) =>
    p.$active ? "0 1px 3px rgba(60, 64, 67, 0.12)" : "none"};
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    color: #1e293b;
    background: ${(p) => (p.$active ? "#fff" : "rgba(255, 255, 255, 0.45)")};
  }
`;

/** Mobile drawer: flex bridge so the bids column can shrink and inner list scrolls. */
const BidsDrawerBodyShell = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/** Stats + summary copy: stays pinned above vendor quotes / list. */
const BidsPanelFixedTop = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/** Scroll region for everything below the stats (job progress, empty states, or bid list). */
const BidsPanelBodyScroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  ${bidsPanelScrollChrome}
`;

const BidsPanelLockedBlock = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/** Fills space below “Vendor quotes”; only used when there are bid cards to scroll. */
const BidsPanelListScroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  ${bidsPanelScrollChrome}
`;

const BidsPanelLoading = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 1.5rem 1rem;
  color: #5f6368;
  font-size: 0.875rem;
`;

const MutedHelp = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #5f6368;
  text-align: left;
  align-self: stretch;
  width: 100%;
`;

const BidsStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  width: 100%;
`;

const BidRangeStatWrap = styled.span`
  display: block;
  width: 100%;
`;

const BidSectionTitleRow = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const BidSectionTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5f6368;
`;

const BidSortDropdown = styled.div`
  .ant-select {
    .ant-select-selector {
      border: 1px solid #e2e8f0 !important;
      border-radius: 6px !important;
      font-size: 0.75rem !important;
    }
    .ant-select-selection-item {
      font-size: 0.75rem !important;
      font-weight: 500 !important;
      color: #64748b !important;
    }
    .ant-select-arrow {
      color: #94a3b8 !important;
    }
  }

  @media ${device.desktop} {
    .ant-select .ant-select-suffix {
      margin-inline-start: 4px;
    }
    .bid-sort-caret {
      font-size: 9px !important;
      line-height: 1;
    }
    .bid-sort-caret svg {
      width: 9px !important;
      height: 9px !important;
    }
  }
`;

const JobProgressCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const JobProgressHeader = styled.div`
  width: 100%;
  margin-bottom: 14px;
  text-align: center;
`;

const JobProgressTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const JobProgressMeter = styled.div`
  margin: 0 0 4px;
  display: flex;
  justify-content: center;
`;

const JobProgressCircleInner = styled.span`
  display: inline-flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  gap: 1px;
  line-height: 1;
`;

const JobProgressCircleValue = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  font-family: var(
    --font-stack-sans,
    "Inter",
    system-ui,
    -apple-system,
    sans-serif
  );
`;

const JobProgressCircleSuffix = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: #64748b;
  margin-left: 1px;
  transform: translateY(-0.35em);
`;

const JobProgressText = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
  margin-top: 8px;
  line-height: 1.4;
  text-align: center;

  /* Prevent orphaned words */
  hyphens: auto;
  word-break: break-word;

  /* Better text wrapping */
  @supports (text-wrap: balance) {
    text-wrap: balance;
  }
`;

const JobProgressDetailsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const JobProgressCompleted = styled.div`
  background: rgba(6, 182, 212, 0.03);
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e6fffa;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
`;

const JobProgressCompletedTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
  margin-bottom: 6px;
  text-align: left;
  white-space: nowrap;
`;

const JobProgressCompletedItem = styled.div`
  font-size: 0.75rem;
  color: #16a34a;
  margin-bottom: 2px;
  text-align: left;

  &:before {
    content: "✓ ";
    color: #22c55e;
    margin-right: 4px;
    font-weight: 600;
  }
`;

const JobProgressMissing = styled.div`
  background: rgba(107, 114, 128, 0.03);
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #f3f4f6;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
`;

const JobProgressMissingTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 6px;
  text-align: left;
  white-space: nowrap;
`;

const JobProgressMissingItem = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 2px;
  text-align: left;

  &:before {
    content: "• ";
    color: #d1d5db;
    margin-right: 4px;
  }
`;

const MobileJobProgressBanner = styled.div`
  background: rgba(255, 245, 238, 0.8);
  border-bottom: 1px solid #fed7aa;
  padding: 12px 16px;
  flex-shrink: 0;
`;

const MobileJobProgressContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MobileJobProgressMeter = styled.div`
  flex-shrink: 0;
`;

const MobileJobProgressValue = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
`;

const MobileJobProgressText = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileJobProgressTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #ea580c;
  letter-spacing: 0.025em;
  margin-bottom: 2px;
`;

const MobileJobProgressSubtext = styled.div`
  font-size: 0.6875rem;
  color: #9a3412;
  font-weight: 500;
`;

const BidListStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BidMiniCard = styled.div`
  padding: 12px 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e8eaed;
  min-width: 0;
`;

const BidMiniTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

const BidMiniTopLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const BidMiniChrono = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #1e293b;
  line-height: 1.2;
`;

const BidMiniAmount = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #202124;
  flex-shrink: 0;
`;

const BidMiniTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
`;

const BidMiniMetaBlock = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const BidMiniRatingRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 10px;
  min-width: 0;
  width: 100%;
`;

const BidMiniRatingText = styled.span`
  font-size: 0.78rem;
  font-weight: 500;
  color: #202124;
  line-height: 1.35;
  text-align: center;

  strong {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #202124;
  }
`;

const BidMiniMetaMuted = styled.span`
  display: block;
  width: 100%;
  font-size: 0.75rem;
  color: #80868b;
  line-height: 1.35;
  text-align: center;
`;

const BidMiniVendorName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #202124;
  line-height: 1.35;
  margin: 8px 0 4px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BidMiniDistance = styled.div`
  font-size: 0.72rem;
  color: #5f6368;
  line-height: 1.35;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: center;
  width: 100%;
`;

const LinkJobBtn = styled.button`
  margin-top: 8px;
  width: 100%;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a73e8;
  background: #e8f0fe;
  border: 1px solid #aecbfa;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #d2e3fc;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const BidsSearchCtaCard = styled.div`
  margin-top: 8px;
  padding: 14px 12px 12px;
  text-align: left;
  border-radius: 14px;
  background: linear-gradient(165deg, #fff 0%, #fffaf8 55%, #fff5f0 100%);
  border: 1px solid rgba(255, 87, 34, 0.18);
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.06),
    0 4px 14px rgba(255, 87, 34, 0.08);
`;

const BidsSearchPendingCard = styled.div`
  margin-top: 8px;
  padding: 12px;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #3c4043;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid #e8eaed;
`;

const BidsSearchCtaHint = styled.p`
  margin: 0 0 10px;
  font-size: 0.75rem;
  line-height: 1.4;
  color: #3c4043;
  text-align: left;
`;

const BidsSearchInvestmentShield = styled.p`
  margin: 0 0 12px;
  padding: 9px 11px;
  border-radius: 8px;
  background: rgba(46, 125, 50, 0.07);
  border: 1px solid rgba(46, 125, 50, 0.18);
  font-size: 0.74rem;
  line-height: 1.5;
  color: #1b5e20;
  text-align: left;

  strong {
    color: #1b5e20;
    font-weight: 700;
  }
`;

const BidsSearchExplainer = styled.div`
  margin-bottom: 14px;
  padding: 10px 10px 9px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-align: left;
`;

const BidsSearchExplainerTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #3c4043;
  margin-bottom: 8px;
`;

const BidsSearchExplainerSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const BidsSearchExplainerStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const BidsSearchExplainerStepNum = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  background: rgba(255, 87, 34, 0.12);
  color: var(--color-action, #ff5722);
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

const BidsSearchExplainerStepText = styled.span`
  font-size: 0.74rem;
  line-height: 1.4;
  color: #5f6368;
`;

const ChatVendorSearchReadyZone = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 16px 12px 10px;
  box-sizing: border-box;
  background: #f0f4f9;
  border-top: 1px solid #e8eaed;
`;

/* ── Paid / dispatched confirmation banner ─────────────────────────────── */

const DispatchConfirmedZone = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 10px 14px;
  box-sizing: border-box;
  background: #edf7ee;
  border-top: 1px solid rgba(67, 160, 71, 0.45);
  text-align: center;

  @media ${device.mobile} {
    padding: 8px 12px;
  }
`;

const DispatchConfirmedCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
`;

const DispatchConfirmedIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2e7d32;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
  box-shadow: none;

  @media ${device.mobile} {
    width: 20px;
    height: 20px;
    font-size: 0.68rem;
  }
`;

const DispatchConfirmedBody = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  text-align: center;
`;

const DispatchConfirmedText = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.35;
  color: #388e3c;
  text-align: center;

  strong {
    color: #1b5e20;
    font-weight: 700;
  }
`;

const DispatchConfirmedMobileLine = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #388e3c;
  text-align: center;

  & + & {
    margin-top: 3px;
  }
`;

const DispatchConfirmedLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #1b5e20;
  font-size: inherit;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #2e7d32;
  }
`;

const ChatVendorSearchReadyBanner = styled.div`
  flex-shrink: 0;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: 8px 12px;
  box-sizing: border-box;
  border: 1px solid rgba(46, 125, 50, 0.28);
  border-radius: 8px;
  background: #e8f5e9;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  text-align: center;
`;

const ChatVendorSearchReadyText = styled.p`
  margin: 0;
  font-size: 0.74rem;
  line-height: 1.4;
  font-weight: 400;
  color: #2e7d32;
`;

const DispatchRail = styled.div`
  flex-shrink: 0;
  padding: 10px 16px 12px;
  border-top: 1px solid #e8eaed;
  background: #fff;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`;

const DispatchPrimaryBtn = styled.button`
  width: 100%;
  padding: 10px 14px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: #1a73e8;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.25);

  &:hover:not(:disabled) {
    background: #1557b0;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const DispatchPaidNote = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #137333;
  font-weight: 500;
  text-align: center;
`;

const DispatchPrepNote = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #5f6368;
  text-align: center;
`;

const DispatchLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 0;
`;

const VarianceModalTitleBlock = styled.div`
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  width: 100%;
  /* Leave space for the circular close control on the right */
  padding: 2px 46px 2px 12px;

  @media ${device.minTablet} {
    padding: 4px 48px 4px 16px;
  }
`;

/** Mobile: bid #, amount, vendor stacked. Desktop: bid + vendor left, amount right. */
const BidModalHeaderGrid = styled.div`
  display: grid;
  width: 100%;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.12);
  column-gap: 14px;
  row-gap: 8px;
  grid-template-columns: 1fr;
  grid-template-areas:
    "bidtitle"
    "amount"
    "vendor";
  justify-items: center;
  text-align: center;

  @media ${device.minTablet} {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "bidtitle amount"
      "vendor amount";
    justify-items: start;
    text-align: left;
    align-items: center;
    column-gap: 20px;
    row-gap: 4px;
  }
`;

const BidModalMainTitle = styled.div`
  grid-area: bidtitle;
  font-size: 1.125rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.02em;
  line-height: 1.25;

  @media ${device.minTablet} {
    font-size: 1.0625rem;
    letter-spacing: -0.01em;
  }
`;

const BidModalVendorName = styled.div`
  grid-area: vendor;
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.01em;
  line-height: 1.4;
  max-width: 100%;
  padding: 0 2px;
  word-wrap: break-word;
  overflow-wrap: anywhere;

  @media ${device.minTablet} {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.85);
    padding: 0;
  }
`;

const BidModalAmount = styled.div`
  grid-area: amount;
  font-size: 1.45rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  text-align: center;

  @media ${device.minTablet} {
    font-size: 1.25rem;
    text-align: right;
    justify-self: end;
    align-self: center;
    letter-spacing: -0.02em;
  }
`;

const VarianceModalSubtitle = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VarianceModalFooterBar = styled.div`
  padding: 14px 16px 18px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;

  @media ${device.minTablet} {
    padding: 16px 24px 20px;
  }
`;

/** Mobile: stacked full-width controls; desktop: one centered row (wrap). */
const BidModalFooterInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;

  @media ${device.minTablet} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 10px 12px;
  }
`;

const VarianceModalBody = styled.div`
  font-size: 0.875rem;
  color: #202124;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
`;

const VarianceCompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
`;

const VarianceCol = styled.div`
  padding: 14px 14px 12px;
  border-radius: 12px;
  background: ${(p) => (p.$accent ? "#fffaf8" : "#ffffff")};
  border: 1px solid
    ${(p) => (p.$accent ? "rgba(255, 87, 34, 0.25)" : "rgba(60, 64, 67, 0.12)")};
  box-shadow: ${(p) =>
    p.$accent
      ? "0 2px 10px rgba(255, 87, 34, 0.1)"
      : "0 1px 3px rgba(60, 64, 67, 0.06)"};
  ${(p) =>
    p.$accent
      ? `
 border-left: 3px solid var(--color-action, #ff5722);
 padding-left: 11px;
 `
      : ""}
`;

const VarianceLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  color: #5f6368;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  .anticon {
    font-size: 0.75rem;
    color: #80868b;
  }
`;

const VarianceValue = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #202124;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
`;

const VarianceTotalsPanel = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
`;

const VarianceGapLine = styled.p`
  margin: ${(p) => (p.$flush ? "0 0 8px" : "8px 0 0")};
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #3c4043;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    font-weight: 700;
    color: #202124;
    font-variant-numeric: tabular-nums;
  }
`;

const VarianceSectionRule = styled.hr`
  margin: 24px 0 8px;
  border: none;
  border-top: 1px solid #e8eaed;
`;

const VarianceSectionTitle = styled.div`
  margin: 12px 0 16px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;

  .anticon {
    color: #64748b;
    font-size: 0.875rem;
  }
`;

/** Icon + text row inside variance modal (replaces emoji prefixes). */
const VarianceInlineIconRow = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;

  .anticon {
    margin-top: 2px;
    color: #64748b;
    flex-shrink: 0;
  }
`;

const VarianceLockHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #5f6368;

  .anticon {
    color: #80868b;
  }
`;

const VarianceMuted = styled.div`
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${(p) => (p.$strong ? "#202124" : "#5f6368")};
  font-weight: ${(p) => (p.$strong ? 600 : 400)};
`;

const BidModalContactMissingNotice = styled(Alert)`
  margin-top: 6px;
  border-radius: 10px;
  border: 1px solid #fde68a;
  background: #fffbeb;

  .ant-alert-message {
    font-weight: 600;
    color: #92400e;
    font-size: 0.8125rem;
  }

  .ant-alert-description {
    color: #78350f;
    font-size: 0.75rem;
    line-height: 1.5;
    margin-top: 4px;
  }

  .anticon {
    color: #d97706;
  }
`;

const VarianceRatingBlock = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 2px;
  max-width: 100%;
  min-width: 0;
  flex-wrap: wrap;
`;

const VarianceRatingMeta = styled.div`
  font-size: 0.8125rem;
  color: #5f6368;
  line-height: 1.35;
  white-space: nowrap;

  strong {
    font-weight: 700;
    color: #202124;
  }
`;

const VarianceSavingsCallout = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #7c2d12;
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid rgba(255, 87, 34, 0.45);
  box-shadow: 0 1px 3px rgba(255, 87, 34, 0.12);

  strong {
    font-weight: 800;
    color: var(--color-action, #ff5722);
    font-variant-numeric: tabular-nums;
  }
`;

const VarianceClumseIntro = styled.p`
  margin: -6px 0 12px;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #5f6368;
`;

const VarianceClumseBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VarianceClumseBullet = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e8eaed;
`;

const VarianceClumseBulletLabel = styled.div`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #80868b;
  margin-bottom: 4px;
`;

const VarianceClumseBulletText = styled.div`
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #202124;

  strong {
    font-weight: 600;
    color: inherit;
  }
`;

const VarianceClumsePara = styled.div`
  margin: 0;

  strong {
    font-weight: 600;
    color: #202124;
  }
`;

const VarianceHint = styled.p`
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 0.78rem;
  color: #b45309;
  line-height: 1.45;
  background: #fffbeb;
  border: 1px solid #fde68a;
`;

const EscrowSecuringContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  min-width: 280px;
`;

const EscrowExplanation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
`;

const EscrowSubtext = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  line-height: 1.4;
`;

const MainTopBar = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e8eaed;
  background: #f0f4f9;
  box-sizing: border-box;

  @media ${device.desktop} {
    padding: 14px 20px;
  }

  @media ${device.mobile} {
    /* Sit above the fixed thread rail so the menu / title row isn’t covered by the drawer. */
    position: relative;
    z-index: 35;
  }
`;

const MainTitleGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const MainTitle = styled.h1`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 500;
  color: #202124;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;

  @media ${device.desktop} {
    font-size: 1.1875rem;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.01em;
  }
`;

const SandboxIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 500;
  font-family: var(--font-mono, "IBM Plex Mono", monospace);
  letter-spacing: 0.05em;
  color: #b45309;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #f59e0b;
  border-radius: 4px;
  text-transform: uppercase;
  flex-shrink: 0;

  @media ${device.desktop} {
    font-size: 0.6875rem;
    padding: 3px 8px;
  }
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: visible;
`;

/** Mobile top bar: icon-only control to open bids drawer. */
const BidsTopBtn = styled.button`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid #dadce0;
  border-radius: 999px;
  background: #fff;
  color: #1e293b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);

  .anticon {
    font-size: 18px;
    line-height: 1;
  }

  .anticon svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  &:hover {
    background: #f8fafc;
    border-color: #c4c7c5;
  }
`;

const IconBtn = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #5f6368;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0;

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }

  /* Same box for every top-bar glyph (Outlined icons vary in default em sizing). */
  .anticon svg {
    width: 22px;
    height: 22px;
    display: block;
  }

  &:hover {
    background: rgba(60, 64, 67, 0.08);
    color: #202124;
  }
`;

const TranscriptScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const TranscriptInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  /* Extra bottom space when the ready banner sits above the composer. */
  padding: 16px 16px ${(p) => (p.$roomAboveComposer ? "36px" : "20px")};
`;

const MsgRow = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  margin-bottom: 18px;
  flex-direction: row;
  align-items: flex-start;
  justify-content: ${(p) => (p.$user ? "flex-end" : "flex-start")};
`;

/** Column for assistant turns: visible “Clumse AI” label + bubble (or typing). */
const AssistantMsgStack = styled.div`
  min-width: 0;
  flex: 0 1 auto;
  width: fit-content;
  max-width: min(620px, 100%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserMsgStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 0 1 auto;
  width: fit-content;
  max-width: min(620px, 100%);
  min-width: 0;
`;

const MsgTimestamp = styled.div`
  margin-top: 4px;
  font-size: 0.6875rem;
  line-height: 1.2;
  color: #80868b;
  padding: 0 2px;
  align-self: ${(p) => (p.$user ? "flex-end" : "flex-start")};
`;

const MsgAssistantName = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #5f6368;
  line-height: 1.2;
  margin: 0 0 6px;
`;

const GemAvatarWrap = styled.div`
  flex-shrink: 0;
`;

/** Brand mark for assistant turns: B&W mark on white (matches nav profile treatment). */
const ChatStampAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #ffffff;
  border: 2px solid #1e293b;
  overflow: hidden;
`;

const ChatStampImg = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain;
  display: block;
  filter: brightness(0) invert(0);
`;

const ScrollToBottomButton = styled.button`
  display: block;
  margin: 8px auto 0;
  padding: 8px;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #333;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

/** Preserves newlines from the model; wraps long tokens (URLs, etc.). */
const MsgText = styled.span`
  display: block;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: normal;

  strong {
    font-weight: 600;
    color: inherit;
  }
`;

const GemBubble = styled.div`
  min-width: 0;
  width: fit-content;
  max-width: min(620px, 100%);
  padding: ${(p) => (p.$user ? "10px 16px" : "12px 16px")};
  border-radius: ${(p) =>
    p.$user ? "20px 20px 4px 20px" : "0 18px 18px 18px"};
  text-align: left;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: ${(p) => (p.$error ? "#c5221f" : "#202124")};

  @media ${device.desktop} {
    font-size: 0.8125rem;
    line-height: 1.52;
  }
  background: ${(p) => {
    if (p.$error) return "#fce8e6";
    return p.$user ? "#d3e3fd" : "#ffffff";
  }};
  border: 1px solid
    ${(p) => {
      if (p.$error) return "#f9ab9e";
      return p.$user ? "transparent" : "#e8eaed";
    }};
  box-shadow: ${(p) => (p.$user ? "none" : "0 1px 2px rgba(60, 64, 67, 0.08)")};
`;

const TypingGem = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  flex: 0 0 auto;
  width: fit-content;
  min-width: 0;
  max-width: min(620px, 100%);
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 0 18px 18px 18px;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  font-size: 0.9375rem;
  line-height: 1.55;
  text-align: left;
  color: #202124;
  min-height: calc(
    0.9375rem * 1.55 + 24px
  ); /* font-size * line-height + padding */

  @media ${device.desktop} {
    font-size: 0.875rem;
    line-height: 1.52;
    min-height: calc(0.875rem * 1.52 + 24px);
  }

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #5f6368;
    opacity: 0.35;
    animation: gemB 1.1s ease-in-out infinite;
    flex-shrink: 0;
  }
  span:nth-child(1) {
    animation-delay: 0s;
  }
  span:nth-child(2) {
    animation-delay: 0.15s;
  }
  span:nth-child(3) {
    animation-delay: 0.3s;
  }
  @keyframes gemB {
    0%,
    80%,
    100% {
      opacity: 0.25;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-2px);
    }
  }
`;

const RemodelPreviewRail = styled.div`
  flex-shrink: 0;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: 8px 12px 0;
`;

const RemodelPreviewPending = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #5f6368;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e8eaed;
  border-radius: 10px;
`;

const RemodelPreviewImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const RemodelPreviewImg = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e8eaed;
  background: #fff;
`;

const ComposerDock = styled.div`
  flex-shrink: 0;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(to top, #f0f4f9 85%, rgba(240, 244, 249, 0));
  border-top: 1px solid transparent;
`;

const PendingAttachBar = styled.div`
  max-width: 720px;
  margin: 0 auto 8px;
  padding: 0 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const PendingThumbWrap = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${(p) => (p.$isUploading ? "#c2e7ff" : "#dadce0")};
  flex-shrink: 0;
  background: ${(p) => (p.$isUploading ? "#f0f7ff" : "transparent")};
`;

const PendingUploadPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0.72;
`;

const PendingUploadOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  color: #1a73e8;
`;

const PendingUploadStatus = styled.span`
  font-size: 0.78rem;
  font-weight: 500;
  color: #5f6368;
  padding: 0 2px;
`;

const PendingRemove = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 3;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(32, 33, 36, 0.72);
  color: #fff;
  font-size: 0.65rem;
  cursor: pointer;

  &:hover {
    background: rgba(32, 33, 36, 0.9);
  }
`;

const ComposerRow = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 0 4px;
`;

const ComposerShell = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(60, 64, 67, 0.12),
    0 4px 12px rgba(60, 64, 67, 0.06);

  @media ${device.mobile} {
    padding: 4px 8px 4px 4px;
    min-height: 44px;
  }
`;

const ComposerAttachBtn = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #5f6368;
  font-size: 1.15rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(60, 64, 67, 0.08);
    color: #202124;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ComposerInput = styled(Input.TextArea)`
  flex: 1;
  min-width: 0;
  align-self: center;
  font-size: 0.9375rem !important;
  line-height: 1.45 !important;
  color: #202124 !important;
  resize: none !important;

  &.ant-input-borderless,
  &.ant-input-outlined,
  &.ant-input-textarea-affix-wrapper {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
  }

  textarea.ant-input {
    padding: 8px 6px !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
  }

  /* No extra ring inside the shell (overrides globals .ant-input:focus + focus-visible) */
  && textarea.ant-input:focus,
  && textarea.ant-input:focus-visible,
  && .ant-input:focus,
  && .ant-input:focus-visible {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    outline-offset: 0 !important;
  }

  &&.ant-input-affix-wrapper-focused,
  &&:focus-within {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  @media ${device.mobile} {
    textarea.ant-input {
      padding: 4px 4px !important;
      min-height: 22px !important;
      line-height: 1.35 !important;
      overflow: visible !important;
    }

    textarea.ant-input::placeholder {
      font-size: 0.875rem;
      opacity: 1;
    }
  }
`;

/** Fixed width; stretches tall with the composer so the send control stays a narrow rail. */
const SendGemWrap = styled.div`
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  width: 54px;
  min-width: 54px;
  max-width: 54px;
`;

const SendGem = styled.button`
  box-sizing: border-box;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 54px;
  max-height: 100%;
  padding: 0;
  border-radius: 10px;
  border: none;
  background: var(--color-action, #ff5722);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.05rem;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f44e1b;
  }

  &:focus-visible {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
  }
`;

const ComposerHintsWrap = styled.div`
  max-width: 720px;
  margin: 8px auto 0;
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ComposerHint = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  color: #5f6368;
  text-align: center;
  line-height: 1.35;
`;

const DisclaimerLink = styled.a`
  /* globals (styles.css) set a/a:hover to legacy tokens; force readable ink on the composer. */
  color: #1557b0 !important;
  font-size: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(21, 87, 176, 0.35);
  text-underline-offset: 2px;
  transform: none !important;
  transition: color 0.15s ease, text-decoration-color 0.15s ease;

  &:hover {
    color: #0b3d91 !important;
    text-decoration-color: currentColor;
  }

  &:focus-visible {
    color: #0b3d91 !important;
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
  }
`;

const ChatModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #202124;
  letter-spacing: -0.01em;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
`;

const RenameChatModalIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 87, 34, 0.14);
  color: #ff5722;
  font-size: 1.05rem;
  flex-shrink: 0;
`;

const DeleteChatModalIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fce8e6;
  color: #c5221f;
  font-size: 1.05rem;
  flex-shrink: 0;
`;

const RenameChatInput = styled(Input)`
  width: 100%;
  font-family: var(
    --font-stack-sans,
    "Inter",
    system-ui,
    sans-serif
  ) !important;

  && .ant-input {
    border-radius: 8px !important;
    border: 1px solid #dadce0 !important;
    padding: 10px 12px !important;
    font-size: 0.9375rem !important;
    color: #202124 !important;
    line-height: 1.45 !important;
  }

  &&:hover .ant-input {
    border-color: #bdc1c6 !important;
  }

  &&:focus-within .ant-input,
  && .ant-input:focus {
    border-color: #ff5722 !important;
    box-shadow: 0 0 0 2px rgba(255, 87, 34, 0.18) !important;
  }
`;

const RenameChatSaveBtn = styled.button`
  padding: 8px 18px;
  border-radius: 999px;
  border: 2px solid #ffffff;
  background: #ff5722;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  cursor: pointer;
  letter-spacing: 0.01em;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.12);
  transition: background 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: #f44e1b;
    box-shadow: 0 2px 8px rgba(255, 87, 34, 0.35);
  }

  &:active {
    background: #e84615;
  }
`;

const DeleteChatModalBody = styled.div`
  font-size: 0.875rem;
  line-height: 1.55;
  color: #3c4043;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
`;

const DeleteChatModalP = styled.p`
  margin: 0 0 14px;
  text-align: center;
`;

const ChatModalFooterBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 16px 22px 20px;
  background: #f8fafc;
  border-top: 1px solid #e8eaed;
`;

const ChatModalGhostBtn = styled.button`
  padding: 8px 10px;
  margin: 0;
  border-radius: 6px;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #5f6368;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  cursor: pointer;
  transition: color 0.15s ease, text-decoration-color 0.15s ease;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 3px;

  &:hover {
    color: #202124;
    text-decoration-color: currentColor;
  }

  &:focus-visible {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
  }
`;

const BidModalSelectBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  margin: 0;
  min-height: 44px;
  width: 100%;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(245, 87, 34, 0.3);
  transition: all 0.15s ease;
  letter-spacing: 0.02em;
  white-space: nowrap;

  @media ${device.minTablet} {
    width: auto;
    min-width: 168px;
    padding: 10px 20px;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #f4511e 0%, #e64a19 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(245, 87, 34, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(245, 87, 34, 0.3);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid #ff5722;
    outline-offset: 2px;
  }
`;

const BidModalCloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  margin: 0;
  min-height: 44px;
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  cursor: pointer;
  transition: all 0.15s ease;

  @media ${device.minTablet} {
    width: auto;
    min-width: 88px;
    padding: 8px 16px;
    min-height: 40px;
  }

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
    color: #475569;
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #ff5722;
    outline-offset: 2px;
  }
`;

const DeleteChatConfirmBtn = styled.button`
  padding: 8px 18px;
  border-radius: 999px;
  border: none;
  background: #c5221f;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.12);
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #a50e0e;
    box-shadow: 0 2px 6px rgba(197, 34, 31, 0.35);
  }
`;

/** Mobile bid range: one baseline-aligned row (min - max), not a tall newline stack */
const BidRangeMobileValue = styled.span`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 0.35em;
  row-gap: 0;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 1.0625rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: #202124;

  .brv-sep {
    font-weight: 500;
    font-size: 0.72em;
    opacity: 0.5;
    line-height: 1;
    padding: 0 0.02em;
    transform: translateY(-0.06em);
  }

  .brv-amt {
    white-space: nowrap;
  }
`;

const GemStatCard = styled(Card)`
  border-radius: 12px !important;
  border: 1px solid #e8eaed !important;
  box-shadow: none !important;
  overflow: hidden !important;
  background: #ffffff !important;
  width: ${(p) => (p.$bidRangeFullWidth ? "100%" : "auto")};

  /* Clip body to the same radius so white fill doesn’t square off past the border. */
  & .ant-card-body {
    border-radius: 12px !important;
    overflow: hidden;
  }

  /* Keep counts, currency, and “N/A” the same visual weight as the top row */
  .ant-statistic .ant-statistic-content-value {
    font-size: 1.125rem !important;
    font-weight: 600 !important;
    line-height: 1.25 !important;
    color: #202124 !important;
  }

  .ant-statistic .ant-statistic-content-prefix {
    margin-inline-end: 6px !important;
  }

  ${(p) =>
    p.$gemStatMobileCenter
      ? `
 @media ${device.mobile} {
 .ant-card-body {
 display: flex;
 flex-direction: column;
 align-items: stretch;
 min-height: 118px;
 padding: 12px 10px 14px;
 }

 /* Title stays at the top; content uses remaining height */
 .ant-statistic {
 display: flex;
 flex-direction: column;
 align-items: stretch;
 justify-content: flex-start;
 flex: 1 1 auto;
 min-height: 0;
 width: 100%;
 margin: 0;
 text-align: center;
 }

 .ant-statistic-title {
 flex: 0 0 auto;
 margin-bottom: 0 !important;
 padding-bottom: 6px !important;
 display: flex !important;
 justify-content: center !important;
 width: 100%;
 text-align: center;
 font-size: 0.6875rem !important;
 line-height: 1.3 !important;
 }

 .ant-statistic-content {
 flex: 1 1 auto;
 display: flex !important;
 align-items: center !important;
 justify-content: center !important;
 flex-wrap: wrap;
 margin: 0 !important;
 gap: 4px;
 min-height: 0;
 }

 .ant-statistic-content-prefix {
 display: inline-flex;
 align-items: center;
 line-height: 1;
 }

 .ant-statistic .ant-statistic-content-value {
 font-size: 1rem !important;
 line-height: 1.3 !important;
 text-align: center;
 }
 }
 `
      : ""}

  ${(p) =>
    p.$bidRangeMobileStack
      ? `
 @media ${device.mobile} {
 .ant-card-body {
 min-height: 108px;
 padding-top: 12px;
 padding-bottom: 12px;
 }

 .ant-statistic-content {
 flex-direction: row !important;
 flex-wrap: wrap !important;
 align-items: center !important;
 justify-content: center !important;
 gap: 6px 8px !important;
 }

 .ant-statistic-content-prefix {
 margin-bottom: 0 !important;
 margin-inline-end: 0 !important;
 font-size: 0.9375rem !important;
 opacity: 0.88;
 line-height: 1;
 }

 .ant-statistic .ant-statistic-content-value {
 font-size: inherit !important;
 font-weight: inherit !important;
 line-height: inherit !important;
 letter-spacing: inherit;
 text-align: center !important;
 }
 }
 `
      : ""}
`;

// ScrollIndicator component with visibility detection
const ScrollIndicator = ({ messageId, messages }) => {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const messageRef = useRef(null);

  useEffect(() => {
    if (!messageRef.current) return;

    const checkScrollPosition = () => {
      const messageElement = messageRef.current.closest(
        '[data-message-role="assistant"]'
      );
      if (!messageElement) return;

      const rect = messageElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Consider "at bottom" if the bottom of the message is visible or close to visible
      const isBottomVisible = rect.bottom <= windowHeight + 50; // 50px buffer
      setIsAtBottom(isBottomVisible);
    };

    // Check initial position
    checkScrollPosition();

    // Listen for scroll events
    const container = document.querySelector(".ant-layout-content");
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("scroll", checkScrollPosition);

      return () => {
        container.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("scroll", checkScrollPosition);
      };
    }
  }, [messageId]);

  const scrollToBottom = () => {
    const msgRows = document.querySelectorAll(
      '[data-message-role="assistant"]'
    );
    const currentMsgIndex = messages.findIndex((m) => m.id === messageId);
    if (msgRows[currentMsgIndex]) {
      msgRows[currentMsgIndex].scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  };

  return (
    <div ref={messageRef}>
      {!isAtBottom && (
        <ScrollToBottomButton onClick={scrollToBottom}>
          <DownOutlined />
        </ScrollToBottomButton>
      )}
    </div>
  );
};

export default ClumseChat;
